import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppContext, Point, Layer } from '../context/context';
import { useRides } from '../context/RidesContext';
import { importUATRides } from '../services/fixedRouteUatImporter';
import type { RideSchema } from '../types/simulation';
import { DEFAULT_SIMULATION_DATE } from '../config/constants';
import {
  combineDateWithTime,
  extractTimeFromDateTime,
  isValidTimeFormat,
} from '../utils/dateTimeUtils';
import {
  SHIFT_BAND_WINDOWS,
  getPassengerArrivalTime,
  getShiftBandWindow,
  inferShiftBandFromMinuteOfDay,
  timeToMinutes,
} from '../services/postcodeAssignment/shiftBands';
import { trackAnalyticsEvent } from '../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../services/eventTaxonomy';
import type {
  PostcodeAssignmentFailedStage,
  ToolLaunchSource,
} from '../services/eventTypes';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';

import * as XLSX from 'xlsx';
import { drawerPrimaryActionSx } from '../theme/componentStyles';
import {
  PostcodeAssignmentMapPanel,
  type PostcodeAssignmentMapLive,
} from '../components/postcodeAssignment/PostcodeAssignmentMapPanel';
import {
  applyConsolidationConstraints,
  buildConsolidationMatrixDiagnostics,
  buildConsolidationMatrix,
  summarizeConsolidation,
  type ConsolidationInputRow,
  type ConsolidationMatrix,
  type ConsolidationOutputRow,
} from '../utils/postcodeAssignment/consolidateAssignedStops';
import {
  clampOsmAssignmentSearchRadius,
  fetchNearbyOsmTransitStopsByBoundingBox,
  getOsmStopCacheKey,
} from '../utils/postcodeAssignment/fetchNearbyOsmStops';
import {
  OpsVehicleMappingPanel,
} from '../components/postcodeAssignment/OpsVehicleMappingPanel';
import type { VehicleSlot } from '../services/postcodeAssignment/opsVehicleTypes';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../utils/iconFallbacks';
import { opsColors } from '../theme/opsTokens';
import { ENVIRONMENT_TOKENS_FLAG } from '../services/featureFlags/runtimeFeatureSets';
import { useFeatureFlags } from '../services/featureFlags/useFeatureFlags';

type StopRow = { name: string; lat: number; lon: number; osmId?: string; kind?: string };
type HubRow = { name: string; lat: number; lon: number };
type RowWithNearest = Record<string, string | number | null | undefined>;
type ResultSheet = { name: string; rows: RowWithNearest[] };

function toRowWithNearest(row: Record<string, unknown>): RowWithNearest {
  const normalized: RowWithNearest = {};
  Object.entries(row).forEach(([key, value]) => {
    if (typeof value === 'string' || typeof value === 'number' || value == null) {
      normalized[key] = value;
      return;
    }
    normalized[key] = String(value);
  });
  return normalized;
}

function normalizeText(value: unknown): string {
  return String(value ?? '').trim();
}

/**
 * Excel and some exports use Unicode minus (U+2212) before negative longitudes; {@link Number} only accepts ASCII
 * hyphen-minus, so plain `Number(str)` returns NaN for values like "-1.09".
 */
function normalizeNumericStringForParse(value: string): string {
  return value
    .replace(/\u2212/g, '-') // MINUS SIGN
    .replace(/\u2013/g, '-') // EN DASH (occasionally pasted as a negative sign)
    .replace(/\u2014/g, '-') // EM DASH
    .trim();
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const normalized = normalizeNumericStringForParse(String(value ?? '').trim());
  if (normalized === '') return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function haversineDistanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
}

function findNearestStop(lat: number, lon: number, stops: StopRow[]): StopRow | null {
  if (!stops.length) return null;
  let nearest = stops[0];
  let nearestDistance = haversineDistanceKm(lat, lon, nearest.lat, nearest.lon);
  for (let index = 1; index < stops.length; index += 1) {
    const stop = stops[index];
    const distance = haversineDistanceKm(lat, lon, stop.lat, stop.lon);
    if (distance < nearestDistance) {
      nearest = stop;
      nearestDistance = distance;
    }
  }
  return nearest;
}

function findNearestHub(lat: number, lon: number, hubs: HubRow[]): HubRow | null {
  if (!hubs.length) return null;
  let nearest = hubs[0];
  let nearestDistance = haversineDistanceKm(lat, lon, nearest.lat, nearest.lon);
  for (let index = 1; index < hubs.length; index += 1) {
    const hub = hubs[index];
    const distance = haversineDistanceKm(lat, lon, hub.lat, hub.lon);
    if (distance < nearestDistance) {
      nearest = hub;
      nearestDistance = distance;
    }
  }
  return nearest;
}

/** Sentinel for hub column dropdown state (mirrors postcode auto pattern). */
const AUTO_HUB_LAT_COLUMN = '__hub_lat_auto__';
const AUTO_HUB_LON_COLUMN = '__hub_lon_auto__';
const AUTO_HUB_NAME_COLUMN = '__hub_name_auto__';
/** Hub row label fallback only; omit a name/source column explicitly. */
const HUB_NAME_COLUMN_NONE = '__hub_name_none__';
const AUTO_HUB_COMBINED_COLUMN = '__hub_combined_auto__';

type HubCoordLayoutMode = 'separate' | 'combined';

/** Postcode workbook row-origin column sentinels (declared early for resolvers below). */
const NONE_POSTCODE_ORIGIN_COORD_COLUMN = '__pc_origin_coord_none__';
const AUTO_POSTCODE_ORIGIN_COORD_COLUMN = '__pc_origin_coord_auto__';
const AUTO_POSTCODE_COMBINED_COLUMN = '__pc_origin_combined_auto__';

function hubHeaderKeysFromFirstDataSheet(hubWorkbook: XLSX.WorkBook): { sheetName: string; keys: string[] } | null {
  for (const sheetName of hubWorkbook.SheetNames) {
    const sheet = hubWorkbook.Sheets[sheetName];
    if (!sheet) continue;
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet);
    if (!rows.length) continue;
    const keys = rows[0] ? Object.keys(rows[0]) : [];
    return { sheetName, keys };
  }
  return null;
}

function detectHubLatColumn(keys: string[]): string | null {
  const lat = keys.find((key) => key.toLowerCase().includes('lat'));
  return lat ?? null;
}

function detectHubLonColumn(keys: string[]): string | null {
  return (
    keys.find((key) => {
      const l = key.toLowerCase();
      return l.includes('lon') || l.includes('lng') || l.includes('long');
    }) ?? null
  );
}

function detectHubNameColumn(keys: string[]): string | null {
  return (
    keys.find((key) => key.toLowerCase().includes('name')) ??
    keys.find((key) => key.toLowerCase().includes('hub')) ??
    null
  );
}

function detectHubCombinedColumnFromHeaders(keys: string[]): string | null {
  return (
    keys.find((k) => {
      const l = k.toLowerCase();
      if (l.includes('coord') || l.includes('location') || l.includes('point')) return true;
      if (l.includes('lat') && (l.includes('lon') || l.includes('lng'))) return true;
      return false;
    }) ?? null
  );
}

/**
 * Parse a single cell that encodes latitude and longitude (comma, semicolon, space, pipe, or JSON array).
 * Returns null for a lone number or text that does not yield two finite coordinates.
 */
function splitLatLonPairFromCell(value: unknown): { lat: number; lon: number } | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return null;
  }
  if (Array.isArray(value) && value.length >= 2) {
    const lat = toNumber(value[0]);
    const lon = toNumber(value[1]);
    if (lat !== null && lon !== null) return { lat, lon };
    return null;
  }

  const tryPair = (a: string, b: string): { lat: number; lon: number } | null => {
    const lat = toNumber(a);
    const lon = toNumber(b);
    if (lat !== null && lon !== null) return { lat, lon };
    return null;
  };

  const s = normalizeText(value);
  if (!s) return null;

  let t = s.replace(/^\s*[\[(]\s*/, '').replace(/\s*[\])]\s*$/, '');

  if (t.startsWith('[')) {
    try {
      const parsed = JSON.parse(t) as unknown;
      if (Array.isArray(parsed) && parsed.length >= 2) {
        const lat = toNumber(parsed[0]);
        const lon = toNumber(parsed[1]);
        if (lat !== null && lon !== null) return { lat, lon };
      }
    } catch {
      /* fall through */
    }
  }

  if (t.includes(',')) {
    const parts = t.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const got = tryPair(parts[0]!, parts[1]!);
      if (got) return got;
    }
  }
  if (t.includes(';')) {
    const parts = t.split(';').map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const got = tryPair(parts[0]!, parts[1]!);
      if (got) return got;
    }
  }
  if (t.includes('|')) {
    const parts = t.split('|').map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const got = tryPair(parts[0]!, parts[1]!);
      if (got) return got;
    }
  }
  const byWs = t.split(/\s+/).map((p) => p.trim()).filter(Boolean);
  if (byWs.length >= 2) {
    const got = tryPair(byWs[0]!, byWs[1]!);
    if (got) return got;
  }
  return null;
}

function inferHubCombinedLatLonColumn(rows: Array<Record<string, unknown>>): string | null {
  if (!rows.length) return null;
  const keys = Object.keys(rows[0]);
  if (!keys.length) return null;
  const sample = rows.slice(0, Math.min(40, rows.length));
  let bestKey: string | null = null;
  let bestRatio = 0;
  for (const key of keys) {
    let nonEmpty = 0;
    let hits = 0;
    for (const row of sample) {
      const cell = row[key];
      if (cell == null || normalizeText(cell) === '') continue;
      nonEmpty += 1;
      if (splitLatLonPairFromCell(cell) != null) hits += 1;
    }
    if (nonEmpty === 0) continue;
    const ratio = hits / nonEmpty;
    if (ratio > bestRatio) {
      bestRatio = ratio;
      bestKey = key;
    }
  }
  if (bestKey && bestRatio >= 0.45) {
    return bestKey;
  }
  return null;
}

function effectiveHubDataColumnKey(
  columnKeys: string[],
  userSelection: string | undefined,
  autoSentinel: string,
  detected: string | null
): string | null {
  if (!userSelection || userSelection === autoSentinel) {
    return detected && columnKeys.includes(detected) ? detected : null;
  }
  return columnKeys.includes(userSelection) ? userSelection : null;
}

function effectiveHubNameColumnKey(
  columnKeys: string[],
  userSelection: string | undefined,
  detected: string | null
): string | null {
  if (userSelection === HUB_NAME_COLUMN_NONE) {
    return null;
  }
  if (!userSelection || userSelection === AUTO_HUB_NAME_COLUMN) {
    return detected && columnKeys.includes(detected) ? detected : null;
  }
  return columnKeys.includes(userSelection) ? userSelection : null;
}

/** Parse every hub sheet row using caller-chosen latitude/longitude (and optional name) columns. */
function collectHubRowsFromWorkbookMapped(
  hubWorkbook: XLSX.WorkBook,
  mapping: { latKey: string; lonKey: string; nameKey: string | null }
): HubRow[] {
  const uploadedHubs: HubRow[] = [];
  for (const sheetName of hubWorkbook.SheetNames) {
    const sheet = hubWorkbook.Sheets[sheetName];
    if (!sheet) continue;
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet);
    for (const row of rows) {
      const lat = toNumber(row[mapping.latKey]);
      const lon = toNumber(row[mapping.lonKey]);
      if (lat === null || lon === null) continue;
      const rawName =
        mapping.nameKey !== null ? normalizeText(row[mapping.nameKey]) : '';
      uploadedHubs.push({
        name: rawName || `Hub ${uploadedHubs.length + 1}`,
        lat,
        lon,
      });
    }
  }
  return uploadedHubs;
}

/** Hub rows where one column holds both coordinates (see {@link splitLatLonPairFromCell}). */
function collectHubRowsFromWorkbookCombined(
  hubWorkbook: XLSX.WorkBook,
  mapping: { combinedKey: string; nameKey: string | null }
): HubRow[] {
  const uploadedHubs: HubRow[] = [];
  for (const sheetName of hubWorkbook.SheetNames) {
    const sheet = hubWorkbook.Sheets[sheetName];
    if (!sheet) continue;
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet);
    for (const row of rows) {
      const pair = splitLatLonPairFromCell(row[mapping.combinedKey]);
      if (!pair) continue;
      const rawName =
        mapping.nameKey !== null ? normalizeText(row[mapping.nameKey]) : '';
      uploadedHubs.push({
        name: rawName || `Hub ${uploadedHubs.length + 1}`,
        lat: pair.lat,
        lon: pair.lon,
      });
    }
  }
  return uploadedHubs;
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) {
    throw new DOMException('Assignment stopped', 'AbortError');
  }
}

async function sleepCancellable(ms: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) {
    throw new DOMException('Assignment stopped', 'AbortError');
  }
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Assignment stopped', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

function formatDurationShort(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}h ${m}m ${s}s`;
  }
  if (m > 0) {
    return `${m}m ${s}s`;
  }
  return `${s}s`;
}

/** postcodes.io allows up to 100 codes per bulk POST. */
const POSTCODES_IO_BULK_MAX = 100;
const POSTCODES_IO_BATCH_SPACING_MS = 120;

type PostcodesIoBulkItem = {
  query: string;
  result: { postcode?: string; latitude?: number; longitude?: number } | null;
};

type PostcodesIoBulkResponse = {
  status?: number;
  result?: PostcodesIoBulkItem[];
};

/**
 * Single-postcode GET (fallback if bulk request fails).
 */
async function geocodeUkPostcode(
  postcode: string,
  signal: AbortSignal
): Promise<{ lat: number; lon: number } | null> {
  throwIfAborted(signal);
  const query = encodeURIComponent(postcode);
  const response = await fetch(`https://api.postcodes.io/postcodes/${query}`, { signal });
  if (!response.ok) return null;
  const data = (await response.json()) as {
    status?: number;
    result?: { latitude?: number; longitude?: number };
  };
  const lat = toNumber(data?.result?.latitude);
  const lon = toNumber(data?.result?.longitude);
  if (lat === null || lon === null) return null;
  return { lat, lon };
}

/**
 * Bulk geocode via POST /postcodes (up to {@link POSTCODES_IO_BULK_MAX} per call). Keys in the returned map are
 * {@link normalizeText} of each requested postcode.
 */
async function geocodeUkPostcodesBulk(
  postcodes: string[],
  signal: AbortSignal
): Promise<Map<string, { lat: number; lon: number } | null>> {
  const out = new Map<string, { lat: number; lon: number } | null>();
  throwIfAborted(signal);
  if (postcodes.length === 0) return out;
  const response = await fetch(
    'https://api.postcodes.io/postcodes?filter=postcode,longitude,latitude',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ postcodes }),
      signal,
    }
  );
  if (!response.ok) {
    throw new Error(`postcodes.io bulk lookup failed (${response.status})`);
  }
  const data = (await response.json()) as PostcodesIoBulkResponse;
  const rows = data.result ?? [];
  for (const entry of rows) {
    const key = normalizeText(entry.query);
    if (!key) continue;
    if (!entry.result) {
      out.set(key, null);
      continue;
    }
    const lat = toNumber(entry.result.latitude);
    const lon = toNumber(entry.result.longitude);
    out.set(key, lat !== null && lon !== null ? { lat, lon } : null);
  }
  for (const pc of postcodes) {
    const k = normalizeText(pc);
    if (k && !out.has(k)) {
      out.set(k, null);
    }
  }
  return out;
}

function effectivePostcodeOriginCoordColumnKey(
  columnKeys: string[],
  userSelection: string | undefined,
  detected: string | null
): string | null {
  if (!userSelection || userSelection === NONE_POSTCODE_ORIGIN_COORD_COLUMN) {
    return null;
  }
  if (userSelection === AUTO_POSTCODE_ORIGIN_COORD_COLUMN) {
    return detected && columnKeys.includes(detected) ? detected : null;
  }
  return columnKeys.includes(userSelection) ? userSelection : null;
}

function effectivePostcodeCombinedColumnKey(
  columnKeys: string[],
  userSelection: string | undefined,
  detected: string | null
): string | null {
  if (!userSelection || userSelection === NONE_POSTCODE_ORIGIN_COORD_COLUMN) {
    return null;
  }
  if (userSelection === AUTO_POSTCODE_COMBINED_COLUMN) {
    return detected && columnKeys.includes(detected) ? detected : null;
  }
  return columnKeys.includes(userSelection) ? userSelection : null;
}

/**
 * Resolved origin columns per sheet from UI state + header/sample guesses; combined vs separate mutually exclusive paths.
 * When workbook row coordinates are off for a sheet, behaviour matches "no workbook coordinates" for that sheet only.
 */
function resolvePostcodeSheetRunRows(
  sheets: Array<{ name: string; rows: Array<Record<string, unknown>>; postcodeColumn: string | null }>,
  rowCoordsEnabledBySheet: Record<string, boolean | undefined>,
  layoutBySheet: Record<string, PostcodeOriginLayoutMode | undefined>,
  latSelectionBySheet: Record<string, string | undefined>,
  lonSelectionBySheet: Record<string, string | undefined>,
  combinedSelectionBySheet: Record<string, string | undefined>
): PostcodeSheetRunRow[] {
  return sheets.map((sheet) => {
    if (!rowCoordsEnabledBySheet[sheet.name]) {
      return {
        name: sheet.name,
        rows: sheet.rows,
        postcodeColumn: sheet.postcodeColumn,
        postcodeOriginLayout: 'separate',
        postcodeCombinedKey: null,
        postcodeLatKey: null,
        postcodeLonKey: null,
      };
    }

    const keys = sheet.rows[0] ? Object.keys(sheet.rows[0]) : [];
    const layout: PostcodeOriginLayoutMode = layoutBySheet[sheet.name] ?? 'separate';

    if (layout === 'combined') {
      const inferred = inferHubCombinedLatLonColumn(sheet.rows);
      const headerGuess = detectHubCombinedColumnFromHeaders(keys);
      const detectedCombined = inferred ?? headerGuess;
      const rawSel = combinedSelectionBySheet[sheet.name];
      const combinedSel = rawSel ?? AUTO_POSTCODE_COMBINED_COLUMN;
      const combinedKey = effectivePostcodeCombinedColumnKey(keys, combinedSel, detectedCombined);
      const wantsWorkbookCoords = combinedSel !== NONE_POSTCODE_ORIGIN_COORD_COLUMN;
      if (wantsWorkbookCoords && !combinedKey) {
        const reason =
          combinedSel === AUTO_POSTCODE_COMBINED_COLUMN
            ? 'no latitude/longitude-in-one-cell column was detected from headers or sample cells'
            : 'the chosen column is missing from this sheet';
        throw new Error(
          `Sheet "${sheet.name}": choose a combined latitude/longitude column for postcode rows (${keys.length} column${
            keys.length === 1 ? '' : 's'
          }). ${reason.charAt(0).toUpperCase() + reason.slice(1)}. ` +
            'Each cell should hold two numbers (e.g. 51.507, -0.128), or set the dropdown to None to use postcodes.io only.'
        );
      }
      return {
        name: sheet.name,
        rows: sheet.rows,
        postcodeColumn: sheet.postcodeColumn,
        postcodeOriginLayout: 'combined',
        postcodeCombinedKey: combinedKey,
        postcodeLatKey: null,
        postcodeLonKey: null,
      };
    }

    const detectedLat = detectHubLatColumn(keys);
    const detectedLon = detectHubLonColumn(keys);
    const latKey = effectivePostcodeOriginCoordColumnKey(
      keys,
      latSelectionBySheet[sheet.name],
      detectedLat
    );
    const lonKey = effectivePostcodeOriginCoordColumnKey(
      keys,
      lonSelectionBySheet[sheet.name],
      detectedLon
    );
    const hasLat = latKey !== null;
    const hasLon = lonKey !== null;
    if (hasLat !== hasLon) {
      throw new Error(
        `Sheet "${sheet.name}": choose both Postcode latitude and Postcode longitude columns, or set both to None (postcodes.io only).`
      );
    }
    if (latKey && lonKey && latKey === lonKey) {
      throw new Error(`Sheet "${sheet.name}": postcode latitude and longitude must be different columns.`);
    }
    return {
      name: sheet.name,
      rows: sheet.rows,
      postcodeColumn: sheet.postcodeColumn,
      postcodeOriginLayout: 'separate',
      postcodeCombinedKey: null,
      postcodeLatKey: hasLat ? latKey : null,
      postcodeLonKey: hasLon ? lonKey : null,
    };
  });
}

function rowHasWorkbookPostcodeOrigin(row: Record<string, unknown>, sheet: PostcodeSheetRunRow): boolean {
  if (sheet.postcodeOriginLayout === 'combined') {
    const k = sheet.postcodeCombinedKey;
    if (!k) return false;
    return splitLatLonPairFromCell(row[k]) != null;
  }
  const latKey = sheet.postcodeLatKey;
  const lonKey = sheet.postcodeLonKey;
  if (!latKey || !lonKey) return false;
  const la = toNumber(row[latKey]);
  const lo = toNumber(row[lonKey]);
  return la !== null && lo !== null;
}

/**
 * Distinct postcodes that still need postcodes.io because at least one row with that code lacks workbook lat/lon.
 */
function collectOrderedPostcodesNeedingGeocodeApi(sheets: PostcodeSheetRunRow[]): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const sheet of sheets) {
    const col = sheet.postcodeColumn;
    if (!col) continue;
    for (const row of sheet.rows) {
      const pc = normalizeText(row[col]);
      if (!pc) continue;
      if (rowHasWorkbookPostcodeOrigin(row, sheet)) {
        continue;
      }
      if (!seen.has(pc)) {
        seen.add(pc);
        ordered.push(pc);
      }
    }
  }
  return ordered;
}

function pickPostcodeRowOriginCoordinates(
  row: Record<string, unknown>,
  normalizedPostcode: string,
  sheet: PostcodeSheetRunRow,
  geocodeCache: Map<string, { lat: number; lon: number } | null>
): { lat: number; lon: number } | null {
  if (sheet.postcodeOriginLayout === 'combined') {
    const k = sheet.postcodeCombinedKey;
    if (k) {
      const pair = splitLatLonPairFromCell(row[k]);
      if (pair) return pair;
    }
  } else {
    const latKey = sheet.postcodeLatKey;
    const lonKey = sheet.postcodeLonKey;
    if (latKey && lonKey) {
      const la = toNumber(row[latKey]);
      const lo = toNumber(row[lonKey]);
      if (la !== null && lo !== null) return { lat: la, lon: lo };
    }
  }
  if (!normalizedPostcode) return null;
  return geocodeCache.get(normalizedPostcode) ?? null;
}

function detectPostcodeColumn(row: Record<string, unknown>): string | null {
  const keys = Object.keys(row);
  const column = keys.find((key) => key.toLowerCase().includes('postcode'));
  return column ?? null;
}

/**
 * Heuristic for UK postcode-like cell values when the sheet has no "postcode" header
 * (e.g. a single column of codes under a time-band label).
 */
function isLikelyUkPostcodeCell(value: unknown): boolean {
  const s = normalizeText(value).replace(/\s+/g, ' ').trim();
  if (!s || s.length < 5 || s.length > 12) return false;
  const compact = s.replace(/\s/g, '').toUpperCase();
  if (compact.length < 5 || compact.length > 8) return false;
  if (!/^[A-Z0-9]+$/i.test(compact)) return false;
  if (!/\d/.test(compact)) return false;
  const letters = (compact.match(/[A-Z]/g) || []).length;
  const digits = (compact.match(/\d/g) || []).length;
  return letters >= 2 && digits >= 1 && letters + digits === compact.length;
}

function inferPostcodeColumnFromSampleRows(rows: Array<Record<string, unknown>>): string | null {
  if (!rows.length) return null;
  const keys = Object.keys(rows[0]);
  if (!keys.length) return null;
  const sample = rows.slice(0, Math.min(40, rows.length));
  let bestKey: string | null = null;
  let bestRatio = 0;
  for (const key of keys) {
    let nonEmpty = 0;
    let hits = 0;
    for (const row of sample) {
      const cell = row[key];
      if (cell == null || normalizeText(cell) === '') continue;
      nonEmpty += 1;
      if (isLikelyUkPostcodeCell(cell)) hits += 1;
    }
    if (nonEmpty === 0) continue;
    const ratio = hits / nonEmpty;
    if (ratio > bestRatio) {
      bestRatio = ratio;
      bestKey = key;
    }
  }
  if (bestKey && bestRatio >= 0.45) {
    return bestKey;
  }
  return null;
}

function resolvePostcodeColumnForSheet(rows: Array<Record<string, unknown>>): string | null {
  if (!rows.length) return null;
  const byHeader = detectPostcodeColumn(rows[0]);
  if (byHeader) return byHeader;
  return inferPostcodeColumnFromSampleRows(rows);
}

/** Sentinel for "use auto-detect" in postcode column dropdown state. */
const AUTO_POSTCODE_COLUMN_SELECT = '__auto__';

type PostcodeOriginLayoutMode = 'separate' | 'combined';

type PostcodeSheetRunRow = {
  name: string;
  rows: Array<Record<string, unknown>>;
  postcodeColumn: string | null;
  postcodeOriginLayout: PostcodeOriginLayoutMode;
  /** When layout is combined and key is set, each row may skip postcodes.io when the cell parses as lat+lon. */
  postcodeCombinedKey: string | null;
  /** When layout is separate: both keys set on a row with numeric cells skips postcodes.io for that row's postcode. */
  postcodeLatKey: string | null;
  postcodeLonKey: string | null;
};

/** User's dropdown choice when present and valid; otherwise same as {@link resolvePostcodeColumnForSheet}. */
function effectivePostcodeColumnForSheet(
  rows: Array<Record<string, unknown>>,
  userSelection: string | undefined
): string | null {
  const detected = resolvePostcodeColumnForSheet(rows);
  if (!userSelection || userSelection === AUTO_POSTCODE_COLUMN_SELECT) {
    return detected;
  }
  const first = rows[0];
  if (!first) return detected;
  const keys = Object.keys(first);
  return keys.includes(userSelection) ? userSelection : detected;
}

type WorkbookSheetPreview = {
  name: string;
  columnKeys: string[];
  rowCount: number;
  detectedPostcodeColumn: string | null;
  detectedPostcodeLatColumn: string | null;
  detectedPostcodeLonColumn: string | null;
  /** Single column where sample cells parse as lat+lon pairs (postcode origin combined mode). */
  detectedPostcodeCombinedColumn: string | null;
};

/** First non-empty hub sheet drives column pickers (same hub keys are applied across all sheets at run time). */
type HubWorkbookPreview = {
  previewSheetName: string;
  columnKeys: string[];
  rowCount: number;
  detectedLatColumn: string | null;
  detectedLonColumn: string | null;
  detectedNameColumn: string | null;
  /** Single column where sample cells parse as lat+lon pairs. */
  detectedCombinedColumn: string | null;
};

// -------------------------------------------------------------------------------------------------
// Simulation-ready output configuration
// -------------------------------------------------------------------------------------------------

/** Whether the postcode-resolved endpoint is the from_* or to_* side of the trip. */
type SimulationPostcodeDirection = 'postcode_origin' | 'postcode_destination';
/** Source of the unsnapped opposite endpoint: a single value typed by the user, or per-row columns. */
type SimulationOtherEndpointMode = 'manual' | 'workbook';
type SimulationOtherEndpointLayout = 'separate' | 'combined';
type SimulationRequestTimeMode = 'manual' | 'workbook';
type SimulationPassengersMode = 'manual' | 'workbook';
type ShiftBand = 'DAY' | 'LATE' | 'NIGHT';

const AUTO_OTHER_LAT_COLUMN = '__other_lat_auto__';
const AUTO_OTHER_LON_COLUMN = '__other_lon_auto__';
const AUTO_OTHER_COMBINED_COLUMN = '__other_combined_auto__';
const AUTO_OTHER_ADDRESS_COLUMN = '__other_address_auto__';
const NONE_OTHER_ADDRESS_COLUMN = '__other_address_none__';
const AUTO_REQUEST_TIME_COLUMN = '__request_time_auto__';
const AUTO_PASSENGERS_COUNT_COLUMN = '__pax_count_auto__';
const AUTO_PASSENGERS_TYPE_COLUMN = '__pax_type_auto__';
const PASSENGERS_TYPE_COLUMN_NONE = '__pax_type_none__';
const AUTO_RIDE_ID_COLUMN = '__ride_id_auto__';
const NONE_RIDE_ID_COLUMN = '__ride_id_none__';
const AUTO_USER_ID_COLUMN = '__user_id_auto__';
const NONE_USER_ID_COLUMN = '__user_id_none__';

/** Matches `passengers[].type` default in RideSchema; downstream importers tolerate any string. */
const DEFAULT_PASSENGER_TYPE = 'standard';
const DEFAULT_PASSENGER_COUNT = 1;

function detectOtherEndpointLatColumn(keys: string[]): string | null {
  return (
    keys.find((key) => /dest.*lat|drop.*lat|to[_\s-]?lat|end[_\s-]?lat/i.test(key)) ?? null
  );
}

function detectOtherEndpointLonColumn(keys: string[]): string | null {
  return (
    keys.find((key) => /dest.*(lon|lng|long)|drop.*(lon|lng|long)|to[_\s-]?(lon|lng|long)|end[_\s-]?(lon|lng|long)/i.test(key)) ??
    null
  );
}

function detectOtherEndpointAddressColumn(keys: string[]): string | null {
  return (
    keys.find((key) => /(dest|drop|to|end).*(name|address|location|site|place)/i.test(key)) ??
    keys.find((key) => /destination/i.test(key)) ??
    keys.find((key) => /^name$|^address$|^location$/i.test(key)) ??
    null
  );
}

function detectRequestTimeColumn(keys: string[]): string | null {
  return (
    keys.find((key) => /request[_\s-]?time/i.test(key)) ??
    keys.find((key) => /pickup[_\s-]?time|departure[_\s-]?time|arrival[_\s-]?time/i.test(key)) ??
    keys.find((key) => /^time$/i.test(key)) ??
    keys.find((key) => /datetime|timestamp/i.test(key)) ??
    null
  );
}

function detectPassengersCountColumn(keys: string[]): string | null {
  return (
    keys.find((key) => /passenger.*count|num.*passenger|pax.*count|riders|seats/i.test(key)) ??
    keys.find((key) => /^passengers$|^pax$/i.test(key)) ??
    null
  );
}

function detectPassengersTypeColumn(keys: string[]): string | null {
  return keys.find((key) => /passenger.*type|pax.*type|rider.*type/i.test(key)) ?? null;
}

function detectRideIdColumn(keys: string[]): string | null {
  return (
    keys.find((key) => /^ride[_\s-]?id$/i.test(key)) ??
    keys.find((key) => /(trip|booking).*id/i.test(key)) ??
    keys.find((key) => /external[_\s-]?reference[_\s-]?id/i.test(key)) ??
    null
  );
}

function detectUserIdColumn(keys: string[]): string | null {
  return (
    keys.find((key) => /^user[_\s-]?id$/i.test(key)) ??
    keys.find((key) => /(rider|passenger|customer).*id/i.test(key)) ??
    keys.find((key) => /external[_\s-]?user[_\s-]?reference[_\s-]?id/i.test(key)) ??
    null
  );
}

/**
 * Auto / explicit / none-sentinel resolver shared by the simulation-output column pickers.
 * Returns null when the resolved column does not exist on the given sheet.
 */
/** Sentinels that resolve to "no column" (use manual / fallback default) in the simulation pickers. */
const SIMULATION_NONE_SENTINELS: ReadonlySet<string> = new Set([
  PASSENGERS_TYPE_COLUMN_NONE,
  NONE_OTHER_ADDRESS_COLUMN,
  NONE_RIDE_ID_COLUMN,
  NONE_USER_ID_COLUMN,
]);

function effectiveSimulationColumnKey(
  columnKeys: string[],
  userSelection: string | undefined,
  autoSentinel: string,
  detected: string | null
): string | null {
  if (!userSelection || userSelection === autoSentinel) {
    return detected && columnKeys.includes(detected) ? detected : null;
  }
  if (SIMULATION_NONE_SENTINELS.has(userSelection)) return null;
  return columnKeys.includes(userSelection) ? userSelection : null;
}

function padInt(n: number, width: number): string {
  return String(n).padStart(width, '0');
}

/** Emits a naive ISO-like wall clock (no Z, no offset) using UTC field accessors. */
function naiveIsoFromDate(d: Date): string {
  return (
    `${d.getUTCFullYear()}-${padInt(d.getUTCMonth() + 1, 2)}-${padInt(d.getUTCDate(), 2)}` +
    `T${padInt(d.getUTCHours(), 2)}:${padInt(d.getUTCMinutes(), 2)}:${padInt(d.getUTCSeconds(), 2)}` +
    `.${padInt(d.getUTCMilliseconds(), 3)}`
  );
}

/**
 * Returns a timezone-agnostic wall-clock string (e.g. "2026-05-11T07:30:00.000"). The simulator
 * RideSchema example uses this format — no Z suffix, no offset — so 7:30 typed locally arrives in
 * the simulator as 7:30 regardless of where the run happens. Accepts:
 *   - datetime-local input ("YYYY-MM-DDTHH:mm") - preserved verbatim, padded to ms;
 *   - already-naive strings ("YYYY-MM-DDTHH:mm:ss(.sss)?") - preserved, padded;
 *   - bare dates ("YYYY-MM-DD") - midnight ".000" padding;
 *   - Excel serial numbers - treated as wall-clock days since 1899-12-30 (no TZ shift);
 *   - Date instances and ISO strings with Z/offset - parsed, then their UTC fields are read so
 *     callers can choose how their upstream interprets the result.
 * Returns '' for empty input and the trimmed source text when nothing else parses.
 */
function coerceRequestTimeCellToIso(value: unknown): string {
  if (value == null) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return naiveIsoFromDate(value);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const epochMs = Math.round((value - 25569) * 86400 * 1000);
    const d = new Date(epochMs);
    return Number.isNaN(d.getTime()) ? '' : naiveIsoFromDate(d);
  }
  const text = String(value).trim();
  if (!text) return '';
  // Naive datetime-local / ISO without TZ: "YYYY-MM-DDTHH:mm[:ss[.sss]]" - keep verbatim.
  const naive = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,3}))?$/.exec(text);
  if (naive) {
    const [, date, hh, mm, ss = '00', ms = '000'] = naive;
    return `${date}T${hh}:${mm}:${ss.padStart(2, '0')}.${ms.padEnd(3, '0').slice(0, 3)}`;
  }
  // Bare date: pad to midnight.
  const dateOnly = /^(\d{4}-\d{2}-\d{2})$/.exec(text);
  if (dateOnly) return `${dateOnly[1]}T00:00:00.000`;
  // Anything else (with Z, offset, or locale formats) - parse, then emit UTC components naively.
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) return naiveIsoFromDate(parsed);
  return text;
}

function coercePassengersCountCell(value: unknown): number | null {
  const n = toNumber(value);
  if (n === null) return null;
  if (n < 0) return null;
  return Math.round(n);
}

function normalizePassengerTypeCell(value: unknown): string {
  const text = String(value ?? '').trim();
  return text || DEFAULT_PASSENGER_TYPE;
}

type SimulationOutputConfig = {
  enabled: boolean;
  direction: SimulationPostcodeDirection;
  otherEndpointMode: SimulationOtherEndpointMode;
  /** Parsed once when the user commits manual lat/lon; null until both fields hold finite numbers. */
  manualOther: { lat: number; lon: number } | null;
  /** Free-text label written to the other endpoint's from_address/to_address column. Empty allowed. */
  manualOtherAddress: string;
  otherLayout: SimulationOtherEndpointLayout;
  otherLatSelection: string;
  otherLonSelection: string;
  otherCombinedSelection: string;
  /** Workbook-mode column for the other endpoint's address/label. Available in both layouts. */
  otherAddressSelection: string;
  requestTimeMode: SimulationRequestTimeMode;
  /** ISO 8601 from the datetime-local input; '' means leave the request_time cell blank. */
  manualRequestTimeIso: string;
  requestTimeSelection: string;
  passengersMode: SimulationPassengersMode;
  manualPassengersCount: number;
  manualPassengersType: string;
  passengersCountSelection: string;
  passengersTypeSelection: string;
  rideIdSelection: string;
  userIdSelection: string;
};

type SimulationOutputSheetPlan = {
  otherLatKey: string | null;
  otherLonKey: string | null;
  otherCombinedKey: string | null;
  otherAddressKey: string | null;
  otherLayout: SimulationOtherEndpointLayout;
  requestTimeKey: string | null;
  passengersCountKey: string | null;
  passengersTypeKey: string | null;
  rideIdKey: string | null;
  userIdKey: string | null;
};

type SimulationOutputRunPlan = {
  enabled: boolean;
  direction: SimulationPostcodeDirection;
  manualOther: { lat: number; lon: number } | null;
  manualOtherAddress: string;
  manualRequestTimeIso: string;
  manualPassengersCount: number;
  manualPassengersType: string;
  perSheet: Record<string, SimulationOutputSheetPlan>;
};

function resolveSimulationOutputPlan(
  config: SimulationOutputConfig,
  sheets: Array<{ name: string; rows: Array<Record<string, unknown>> }>
): SimulationOutputRunPlan {
  const perSheet: Record<string, SimulationOutputSheetPlan> = {};
  for (const sheet of sheets) {
    const keys = sheet.rows[0] ? Object.keys(sheet.rows[0]) : [];
    const otherLatKey =
      config.otherEndpointMode === 'workbook' && config.otherLayout === 'separate'
        ? effectiveSimulationColumnKey(
            keys,
            config.otherLatSelection,
            AUTO_OTHER_LAT_COLUMN,
            detectOtherEndpointLatColumn(keys) ?? detectHubLatColumn(keys)
          )
        : null;
    const otherLonKey =
      config.otherEndpointMode === 'workbook' && config.otherLayout === 'separate'
        ? effectiveSimulationColumnKey(
            keys,
            config.otherLonSelection,
            AUTO_OTHER_LON_COLUMN,
            detectOtherEndpointLonColumn(keys) ?? detectHubLonColumn(keys)
          )
        : null;
    const otherCombinedKey =
      config.otherEndpointMode === 'workbook' && config.otherLayout === 'combined'
        ? effectiveSimulationColumnKey(
            keys,
            config.otherCombinedSelection,
            AUTO_OTHER_COMBINED_COLUMN,
            inferHubCombinedLatLonColumn(sheet.rows) ?? detectHubCombinedColumnFromHeaders(keys)
          )
        : null;
    const requestTimeKey =
      config.requestTimeMode === 'workbook'
        ? effectiveSimulationColumnKey(
            keys,
            config.requestTimeSelection,
            AUTO_REQUEST_TIME_COLUMN,
            detectRequestTimeColumn(keys)
          )
        : null;
    const passengersCountKey =
      config.passengersMode === 'workbook'
        ? effectiveSimulationColumnKey(
            keys,
            config.passengersCountSelection,
            AUTO_PASSENGERS_COUNT_COLUMN,
            detectPassengersCountColumn(keys)
          )
        : null;
    const passengersTypeKey =
      config.passengersMode === 'workbook'
        ? effectiveSimulationColumnKey(
            keys,
            config.passengersTypeSelection,
            AUTO_PASSENGERS_TYPE_COLUMN,
            detectPassengersTypeColumn(keys)
          )
        : null;
    // The address column is always available (whether the lat/lon side is manual or workbook) so the
    // user can label trips even when typing manual coordinates.
    const rawAddressSelection = config.otherAddressSelection;
    const otherAddressKey =
      rawAddressSelection === NONE_OTHER_ADDRESS_COLUMN
        ? null
        : effectiveSimulationColumnKey(
            keys,
            rawAddressSelection,
            AUTO_OTHER_ADDRESS_COLUMN,
            detectOtherEndpointAddressColumn(keys)
          );
    const rideIdKey = effectiveSimulationColumnKey(
      keys,
      config.rideIdSelection,
      AUTO_RIDE_ID_COLUMN,
      detectRideIdColumn(keys)
    );
    const userIdKey = effectiveSimulationColumnKey(
      keys,
      config.userIdSelection,
      AUTO_USER_ID_COLUMN,
      detectUserIdColumn(keys)
    );
    perSheet[sheet.name] = {
      otherLatKey,
      otherLonKey,
      otherCombinedKey,
      otherAddressKey,
      otherLayout: config.otherLayout,
      requestTimeKey,
      passengersCountKey,
      passengersTypeKey,
      rideIdKey,
      userIdKey,
    };
  }
  return {
    enabled: config.enabled,
    direction: config.direction,
    manualOther: config.manualOther,
    manualOtherAddress: config.manualOtherAddress,
    manualRequestTimeIso: config.manualRequestTimeIso,
    manualPassengersCount: config.manualPassengersCount,
    manualPassengersType: config.manualPassengersType,
    perSheet,
  };
}

function pickRowOtherEndpoint(
  row: Record<string, unknown>,
  sheetPlan: SimulationOutputSheetPlan,
  manualOther: { lat: number; lon: number } | null
): { lat: number; lon: number } | null {
  if (sheetPlan.otherLayout === 'combined' && sheetPlan.otherCombinedKey) {
    const parsed = splitLatLonPairFromCell(row[sheetPlan.otherCombinedKey]);
    if (parsed) return parsed;
  } else if (sheetPlan.otherLatKey && sheetPlan.otherLonKey) {
    const la = toNumber(row[sheetPlan.otherLatKey]);
    const lo = toNumber(row[sheetPlan.otherLonKey]);
    if (la !== null && lo !== null) return { lat: la, lon: lo };
  }
  return manualOther;
}

function pickRowOtherEndpointAddress(
  row: Record<string, unknown>,
  sheetPlan: SimulationOutputSheetPlan,
  manualAddress: string
): string {
  if (sheetPlan.otherAddressKey) {
    const fromRow = String(row[sheetPlan.otherAddressKey] ?? '').trim();
    if (fromRow) return fromRow;
  }
  return manualAddress;
}

function pickRowRequestTimeIso(
  row: Record<string, unknown>,
  sheetPlan: SimulationOutputSheetPlan,
  manualIso: string
): string {
  if (sheetPlan.requestTimeKey) {
    const iso = coerceRequestTimeCellToIso(row[sheetPlan.requestTimeKey]);
    if (iso) return iso;
  }
  return manualIso;
}

function pickRowPassengers(
  row: Record<string, unknown>,
  sheetPlan: SimulationOutputSheetPlan,
  manualCount: number,
  manualType: string
): { count: number; type: string } {
  let count = manualCount;
  let type = manualType;
  if (sheetPlan.passengersCountKey) {
    const fromRow = coercePassengersCountCell(row[sheetPlan.passengersCountKey]);
    if (fromRow !== null) count = fromRow;
  }
  if (sheetPlan.passengersTypeKey) {
    const fromRow = normalizePassengerTypeCell(row[sheetPlan.passengersTypeKey]);
    if (fromRow) type = fromRow;
  }
  return { count, type };
}

/**
 * Group key for consolidated-stop rollups. Rows without a consolidated lat/lon (e.g. unassigned
 * postcodes) return null and are skipped during aggregation.
 */
function consolidatedGroupKey(row: RowWithNearest): string | null {
  const lat = toNumber(row.consolidated_stop_lat);
  const lon = toNumber(row.consolidated_stop_lon);
  if (lat === null || lon === null) return null;
  const name = typeof row.consolidated_stop_name === 'string' ? row.consolidated_stop_name : '';
  return `${name}|${lat.toFixed(7)}|${lon.toFixed(7)}`;
}

/** Sheet name reserved for the per-consolidated-stop simulation output rollup. */
const SIMULATION_TRIPS_SHEET_NAME = 'Simulation trips';

function readString(row: RowWithNearest, key: string): string {
  const v = row[key];
  return typeof v === 'string' ? v : '';
}

/**
 * Reduces per-postcode result rows into one row per consolidated stop. Rows lacking a consolidated
 * stop or valid from/to coordinates are skipped. The first contributing row's other-endpoint /
 * request_time / passenger_type values are used as the representative; passenger counts are summed.
 * Returns null when the rollup would be empty so callers can skip the sheet entirely.
 *
 * Per-source attribution: `source_ride_ids`, `source_user_ids`, `source_passenger_counts`, and
 * `source_passenger_types` are pipe-separated and index-aligned — entry N of each pertains to the
 * same contributing source row. Downstream consumers (e.g. `tripsSheetRowToRideSchema`) split these
 * to emit one ride per passenger while preserving stop-level consolidation.
 *
 * `external_stop_reference_id` is sourced from the first contributing row's resolved bus stop or
 * hub name (`consolidated_stop_name`), falling back to the raw `postcode` cell (e.g. "SO31 4RA")
 * only when no stop name is available. Threaded into the ride so the Fixed Route export can use
 * it as a human-readable stop label.
 */
function buildSimulationTripsSheet(sheets: ResultSheet[]): ResultSheet | null {
  type Aggregate = {
    ride_id: string;
    user_id: string;
    from_latitude: number;
    from_longitude: number;
    from_address: string;
    to_latitude: number;
    to_longitude: number;
    to_address: string;
    request_time: string;
    request_type: string;
    passengers_count: number;
    passengers_type: string;
    consolidated_stop_name: string;
    /**
     * Postcode of the first contributing row, surfaced separately from `consolidated_stop_name`
     * after Issue 2 (2026-05-28). Lets the result sheet keep the postcode visible alongside the
     * clean stop name (which used to be a `${postcode} / ${stopName}` blob).
     */
    consolidated_stop_postcode: string;
    /** Resolved stop/hub name of the consolidated pickup (first contributing row's `consolidated_stop_name`, falling back to its raw `postcode`); used by the Fixed Route export as a human-readable stop label. */
    external_stop_reference_id: string;
    /** Pipe-separated source per-row ride_ids that fed this consolidated trip; eases traceback. */
    source_ride_ids: string;
    /** Pipe-separated source per-row user_ids that fed this consolidated trip; index-aligned with source_ride_ids. */
    source_user_ids: string;
    /** Pipe-separated passenger counts contributed by each source row; index-aligned. */
    source_passenger_counts: string;
    /** Pipe-separated passenger types contributed by each source row; index-aligned. */
    source_passenger_types: string;
  };
  const groups = new Map<string, Aggregate>();
  let tripCounter = 0;
  for (const sheet of sheets) {
    if (sheet.name === SIMULATION_TRIPS_SHEET_NAME) continue;
    for (const row of sheet.rows) {
      const key = consolidatedGroupKey(row);
      if (!key) continue;
      const fromLat = toNumber(row.from_latitude);
      const fromLon = toNumber(row.from_longitude);
      const toLat = toNumber(row.to_latitude);
      const toLon = toNumber(row.to_longitude);
      if (fromLat === null || fromLon === null || toLat === null || toLon === null) continue;
      const count = toNumber(row.passengers_count) ?? 0;
      const sourceRideId = readString(row, 'ride_id');
      const sourceUserId = readString(row, 'user_id');
      const sourcePassengerType = readString(row, 'passengers_type') || DEFAULT_PASSENGER_TYPE;
      const sourceStopName = readString(row, 'consolidated_stop_name');
      const sourcePostcode = readString(row, 'postcode');
      // Trim 4: prefer the resolved bus stop / hub name; fall back to the raw postcode (Ben 2026-05-28).
      const stopReference = sourceStopName || sourcePostcode;
      const existing = groups.get(key);
      const pushPipe = (current: string, next: string) =>
        current ? `${current}|${next}` : next;
      if (existing) {
        existing.passengers_count += count;
        existing.source_ride_ids = pushPipe(existing.source_ride_ids, sourceRideId);
        existing.source_user_ids = pushPipe(existing.source_user_ids, sourceUserId);
        existing.source_passenger_counts = pushPipe(existing.source_passenger_counts, String(count));
        existing.source_passenger_types = pushPipe(existing.source_passenger_types, sourcePassengerType);
        if (!existing.external_stop_reference_id && stopReference) {
          existing.external_stop_reference_id = stopReference;
        }
      } else {
        tripCounter += 1;
        groups.set(key, {
          ride_id: `trip${tripCounter}`,
          user_id: `user${tripCounter}`,
          from_latitude: fromLat,
          from_longitude: fromLon,
          from_address: readString(row, 'from_address'),
          to_latitude: toLat,
          to_longitude: toLon,
          to_address: readString(row, 'to_address'),
          request_time: readString(row, 'request_time'),
          request_type: readString(row, 'request_type'),
          passengers_count: count,
          passengers_type: sourcePassengerType,
          consolidated_stop_name: sourceStopName,
          consolidated_stop_postcode: sourcePostcode,
          external_stop_reference_id: stopReference,
          source_ride_ids: sourceRideId,
          source_user_ids: sourceUserId,
          source_passenger_counts: String(count),
          source_passenger_types: sourcePassengerType,
        });
      }
    }
  }
  if (groups.size === 0) return null;
  const rows: RowWithNearest[] = Array.from(groups.values()).map((entry) => ({ ...entry }));
  return { name: SIMULATION_TRIPS_SHEET_NAME, rows };
}

/**
 * Returns sheetResults with the rollup sheet appended (or removed) per the runPlan state. Idempotent:
 * any prior simulation-trips sheet is stripped first, so callers can pass the most recent state
 * without worrying about duplication on slider re-applies.
 */
function withSimulationTripsSheet(sheets: ResultSheet[], runPlanEnabled: boolean): ResultSheet[] {
  const base = sheets.filter((sheet) => sheet.name !== SIMULATION_TRIPS_SHEET_NAME);
  if (!runPlanEnabled) return base;
  const rollup = buildSimulationTripsSheet(base);
  return rollup ? [...base, rollup] : base;
}

/**
 * Sums passengers_count across every row sharing the same consolidated stop and writes the rollup
 * into each contributing row's consolidated_stop_passengers_total column. Idempotent - re-run on
 * live consolidation re-applies (slider changes) to keep totals in sync.
 */
function aggregateConsolidatedPassengers(sheets: ResultSheet[]): void {
  const totals = new Map<string, number>();
  for (const sheet of sheets) {
    for (const row of sheet.rows) {
      const key = consolidatedGroupKey(row);
      if (!key) continue;
      const count = toNumber(row.passengers_count);
      if (count === null) continue;
      totals.set(key, (totals.get(key) ?? 0) + count);
    }
  }
  for (const sheet of sheets) {
    for (const row of sheet.rows) {
      const key = consolidatedGroupKey(row);
      row.consolidated_stop_passengers_total = key ? totals.get(key) ?? 0 : '';
    }
  }
}

function buildWorkbook(sheets: ResultSheet[]): XLSX.WorkBook {
  const workbook = XLSX.utils.book_new();
  sheets.forEach((sheet) => {
    const worksheet = XLSX.utils.json_to_sheet(sheet.rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name.slice(0, 31));
  });
  return workbook;
}

type AssignmentProgress = {
  current: number;
  total: number;
  sheetName: string;
  rowInSheet: number;
  sheetRows: number;
  /** Human-readable context (postcode, missing column, etc.) */
  detail: string;
  /** Optional 0-100 bar value for multi-phase runs (geocode -> OSM batches -> assign). */
  barPercent?: number;
};

function buildRowProgressLabel(
  row: Record<string, unknown>,
  postcodeColumn: string | null,
  postcode: string
): string {
  if (!postcodeColumn) {
    return 'No postcode column on this row';
  }
  if (!postcode) {
    return 'Empty postcode';
  }
  return `Postcode ${postcode}`;
}

function isAcceptedWorkbookFile(file: File): boolean {
  const name = normalizeText(file.name);
  if (/\.xlsx$/i.test(name) || /\.xls$/i.test(name)) {
    return true;
  }
  const mime = String(file.type ?? '').toLowerCase();
  return (
    mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mime === 'application/vnd.ms-excel'
  );
}

/** First spreadsheet in a dropped/transferred {@link FileList}, or null if none match. */
function pickFirstAcceptedWorkbookFile(files: FileList | null | undefined): File | null {
  if (!files?.length) return null;
  for (let i = 0; i < files.length; i += 1) {
    const f = files.item(i);
    if (f && isAcceptedWorkbookFile(f)) return f;
  }
  return null;
}

type WorkbookUploadCardProps = {
  title: string;
  /** Shown beside the mailbox / location motif. */
  icon: React.ReactElement;
  chooseFileAriaLabel: string;
  /** Optional lead-in before "Drag XLS or XLSX...". Omit when the enclosing section exposes details in its info tooltip. */
  footerHelper?: string;
  /** Combined label for the drop target (covers drag-and-drop plus choose-file). */
  dropRegionAriaLabel: string;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  /** Invoked after a workbook is chosen via file dialog or dropped (extension/MIME gated). */
  onChosenFileChange: (file: File | null, method?: 'drag_drop' | 'file_input') => void;
  chosenFile: File | null;
  onClearChosen: () => void;
};

/**
 * Matching upload surfaces for postcode and hub workbooks: shared orange accent, icon header, hidden file input, primary Choose action.
 */
function WorkbookUploadCard({
  title,
  icon,
  chooseFileAriaLabel,
  footerHelper,
  dropRegionAriaLabel,
  fileInputRef,
  onChosenFileChange,
  chosenFile,
  onClearChosen,
}: WorkbookUploadCardProps) {
  const { t } = useTranslation('simulation');
  const theme = useTheme();
  const [dragOver, setDragOver] = React.useState(false);
  const dragDepthRef = React.useRef(0);

  const primaryBtnSx = (th: Parameters<typeof drawerPrimaryActionSx>[0]) => ({
    ...drawerPrimaryActionSx(th),
    '&:focus-visible': {
      outline: `2px solid ${th.palette.actionBrand.primary}`,
      outlineOffset: 2,
    },
  });

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDragOver(false);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setDragOver(false);
    const next = pickFirstAcceptedWorkbookFile(event.dataTransfer.files);
    if (next) {
      onChosenFileChange(next, 'drag_drop');
    }
  };

  return (
    <Paper
      component="section"
      variant="outlined"
      aria-label={dropRegionAriaLabel}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        flex: 1,
        minWidth: 0,
        p: 2,
        borderColor: alpha(
          opsColors.brand.primary,
          dragOver ? 0.75 : 0.42
        ),
        bgcolor: alpha(
          opsColors.brand.primary,
          dragOver ? 0.12 : 0.048
        ),
        outline: dragOver ? `2px dashed ${alpha(opsColors.brand.primary, 0.85)}` : 'none',
        outlineOffset: dragOver ? 2 : 0,
        transition: theme.transitions.create(['border-color', 'background-color', 'outline'], {
          duration: theme.transitions.duration.shortest,
        }),
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              flexShrink: 0,
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
              color: opsColors.brand.primary,
              bgcolor: alpha(opsColors.brand.primary, 0.14),
              '& .MuiSvgIcon-root': { fontSize: 26 },
            }}
            aria-hidden
          >
            {icon}
          </Box>
          <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" color="text.primary" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => onChosenFileChange(e.currentTarget.files?.[0] ?? null, 'file_input')}
              style={{ display: 'none' }}
              tabIndex={-1}
            />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 1, columnGap: 1 }}>
              <Button
                type="button"
                aria-label={chooseFileAriaLabel}
                sx={(th) => ({ ...primaryBtnSx(th) })}
                onClick={() => fileInputRef.current?.click()}
              >
                {t('postcodeAssignment.chooseFile')}
              </Button>
              {chosenFile ? (
                <>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    noWrap
                    sx={{ flex: '1 1 120px', minWidth: 0, maxWidth: '100%' }}
                    title={chosenFile.name}
                  >
                    {chosenFile.name}
                  </Typography>
                  <Button
                    type="button"
                    variant="text"
                    sx={(th) => ({
                      flexShrink: 0,
                      fontWeight: 500,
                      color: th.palette.text.secondary,
                      '&:hover': { bgcolor: alpha(th.palette.actionBrand.primary, 0.06) },
                      '&:focus-visible': {
                        outline: `2px solid ${th.palette.actionBrand.primary}`,
                        outlineOffset: 2,
                      },
                    })}
                    onClick={onClearChosen}
                  >
                    {t('postcodeAssignment.clear')}
                  </Button>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('postcodeAssignment.noFileChosen')}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.43 }}>
          {footerHelper ? `${footerHelper.trim()} ` : ''}{t('postcodeAssignment.dragHint')}
        </Typography>
      </Stack>
    </Paper>
  );
}

/** Hover the title row info icon - long-form UX copy for postcode/hub/OSM/OSRM/map behaviour. */
const POSTCODE_ASSIGNMENT_HELP_TOOLTIP = [
  'Upload a postcode workbook. Postcodes default to any column whose header includes the word Postcode (case-insensitive), or the column where most sample cells look like UK postcodes; you can override per sheet with the dropdown below.',
  'If your sheet already has latitude and longitude for each postcode row, map those columns (per sheet): matching pairs of numeric cells are used as row origins so postcodes.io is skipped for those rows. Distinct remaining postcodes are geocoded in the UK via postcodes.io using bulk POST requests (up to 100 codes per round trip).',
  'Use "Assign to predetermined hubs" when you want hub-based matching. When enabled, upload a hub workbook and each row uses the nearest hub.',
  'If no hub workbook is provided, OpenStreetMap transit stops within your search radius are loaded in batched Overpass queries and the nearest stop per row is written to the export with crow-fly distance.',
  'Walking radius consolidation remaps those assigned targets into a smaller optimized pickup set. Tighter walking radius means more consolidated stops; wider radius means fewer stops.',
  'Walking calculations use the Atlas OSRM proxy for pedestrian routing when the Walking Calculations toggle is on. Turn it off to use crow-fly distances only for consolidation (faster, less accurate).',
  'After a run, use the Assignment results accordion for the sheet, filter, download, and enriched table; expand the assignment map accordion beneath it to preview origins and targets.',
].join('\n\n');

/** Hover the walking-calculations switch - OSRM walking routes for consolidation vs crow-fly fallback. */
const WALKING_CALCULATIONS_OSRM_TOOLTIP = [
  'Uses walking routes (OSRM) to estimate walk time and distance between each row origin and candidate pickup points for consolidation.',
  'When off, consolidation uses crow-fly distance and a simple minutes-per-km estimate only (no OSRM calls).',
  'Applies whether you assigned hubs or OpenStreetMap transit stops.',
].join('\n\n');

/** Tooltip copy blocks: theme `shape.borderRadius` is pill-like for shells; tooltips stay a modest box for readability. */
const TOOLTIP_TIGHT_BOX_RADIUS = '4px';

/** Hover OSM radius field - hubs vs Overpass batches and clamp range. */
const OSM_SEARCH_RADIUS_HELP_TOOLTIP =
  'Used when no hub workbook is provided. Stops use the public Overpass API in small batches with ~1s spacing and automatic backoff on rate limits (429). Radius is clamped between 80 m and 5 km; larger radii mean heavier queries.';

/** Radius field is disabled when matching to uploaded hubs instead of querying OSM stops. */
const OSM_SEARCH_RADIUS_DISABLED_WITH_HUB_TOOLTIP =
  'Not used while a hub workbook is uploaded - each row uses the nearest hub. Clear the hub file to enable Overpass stop search and this radius.';

const WALKING_RADIUS_HELP_TOOLTIP =
  'Consolidates assigned targets into fewer pickup points. Tighter radius = more stops and less walking. Wider radius = fewer stops and more walking.';

/** Section headings: same readable box width as page info tooltip */
const SECTION_HELP_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      maxWidth: 440,
      whiteSpace: 'pre-line' as const,
      typography: 'body2',
      lineHeight: 1.55,
      textAlign: 'left' as const,
      borderRadius: TOOLTIP_TIGHT_BOX_RADIUS,
    },
  },
} as const;

const ROW_COORDINATES_FROM_WORKBOOK_TOOLTIP =
  'When on, configure separate lat/lon columns or one combined column per sheet. When off, row origins come from postcode geocoding (postcodes.io) only - the coordinate pickers stay hidden.';

const POSTCODE_WORKBOOK_SECTION_TOOLTIP = [
  'Required postcode workbook should include UK postcodes for each relevant row.',
  'After load, per sheet: Postcode column - Auto follows headers or postcode-like cells; choose a column if the guess is wrong or there is no clear postcode header.',
  'Use the Lat / lon from workbook toggle to show coordinate columns; hover it for behaviour.',
  "Without a hub file, OSM search radius sits beside the first sheet's postcode control; hover that field for Overpass API and clamp range.",
  'Postcode row coordinates - Separate lat/lon: when both columns are set, each row uses those numbers as its origin (postcodes.io is skipped for that row). Set both to None to geocode only.',
  'One column (combined): two numbers per cell like hub mode - comma, semicolon, pipe, spaces, or a pair such as [51.507, -0.128].',
].join('\n\n');

const HUB_WORKBOOK_SECTION_TOOLTIP = [
  'Hub Workbook (Optional)',
  "Not sure which hub to use? Upload a hub list and we'll automatically match each person to their nearest one. If you skip this, we'll find the nearest public transport stop using OpenStreetMaps instead.",
  "Your hub file will need to include coordinates for each hub - either as separate latitude and longitude columns, or combined into one column. You can also include a hub name column; if you don't, hubs will simply be labelled Hub 1, Hub 2, and so on.",
].join('\n\n');

const ASSIGNMENT_RESULTS_SECTION_TOOLTIP = [
  'Choose a sheet that came from the postcode workbook.',
  'Filter matches any cell in the visible sheet.',
  'Download writes the enriched XLSX (all sheets, with suggested targets, consolidated targets, walking distance to consolidated pickup, and crow-fly distances).',
].join('\n\n');

const SIMULATION_OUTPUT_SECTION_TOOLTIP = [
  'Adds simulation-ready columns (from_latitude, from_longitude, to_latitude, to_longitude, request_time, request_type, passengers_count, passengers_type, from_address, to_address) to every result row.',
  'The postcode side fills from the consolidated stop/hub (or the raw geocoded point when no target was found). The other side is left unsnapped - taken from a manual lat/lon you type, or from columns on each postcode sheet.',
  'Direction decides which side is the origin. "Postcode = origin" emits departure_fixed; "Postcode = destination" emits arrival_fixed.',
  'Request time accepts ISO 8601 strings, Excel date cells, and Excel serial numbers; manual mode applies one datetime to every row.',
  'Column choices apply to every postcode sheet by name; sheets that lack a chosen column fall back to the manual value (or blank if none).',
].join('\n\n');

const ASSIGNMENT_MAP_SECTION_TOOLTIP = [
  'Live trail as the run finishes rows: green = postcode origin, purple = assigned hub, blue = OSM transit stop when used.',
  'Teal dashed lines and markers indicate consolidated pickup points after walking-radius optimisation.',
  'Coloured lines run origin -> target; a midpoint arrow shows direction. Very long runs keep only the most recent part of the trail.',
  'The map legend matches hub-only vs OSM-only runs.',
].join('\n\n');

type SectionTitleWithTooltipProps = {
  title: string;
  tooltip: string;
  variant?: 'subtitle1' | 'subtitle2';
  component?: React.ElementType;
  /** Extra sx on the title Typography (e.g. accordion summary). */
  titleSx?: SxProps<Theme>;
};

function SectionTitleWithTooltip({
  title,
  tooltip,
  variant = 'subtitle1',
  component,
  titleSx,
}: SectionTitleWithTooltipProps) {
  const { t } = useTranslation('simulation');
  const defaultComponent: React.ElementType = variant === 'subtitle1' ? 'h2' : 'h3';
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'nowrap', width: '100%', minWidth: 0 }}>
      <Typography
        variant={variant}
        component={component ?? defaultComponent}
        sx={[{ fontWeight: 600, flex: '1 1 auto', minWidth: 0 }, ...(Array.isArray(titleSx) ? titleSx : titleSx ? [titleSx] : [])]}
      >
        {title}
      </Typography>
      <Tooltip
        title={tooltip}
        placement="top"
        enterTouchDelay={0}
        describeChild
        slotProps={SECTION_HELP_TOOLTIP_SLOT_PROPS}
      >
        <IconButton
          aria-label={t('postcodeAssignment.tooltips.aboutSectionAria', { title })}
          size="small"
          sx={{
            flexShrink: 0,
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
              bgcolor: (theme) => alpha(opsColors.brand.primary, 0.08),
            },
            '&:focus-visible': {
              outline: (theme) => `2px solid ${opsColors.brand.primary}`,
              outlineOffset: 2,
            },
          }}
        >
          <Icon name="circle-info" size={20} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

const osmSearchRadiusTooltipSlotProps = {
  tooltip: {
    sx: {
      maxWidth: 420,
      whiteSpace: 'pre-line' as const,
      typography: 'body2',
      lineHeight: 1.55,
      textAlign: 'left',
      borderRadius: TOOLTIP_TIGHT_BOX_RADIUS,
    },
  },
};

/** Max postcode-target pairs retained on the assignment map trail (Leaflet responsiveness on huge sheets). */
const ASSIGNMENT_MAP_TRAIL_MAX = 400;
/** Progress bar segments (0-100) so ETA sees geocode, OSM, assign, and road batches as separate work. */
const BAR_GEO_MAX = 20;
const BAR_ROWSCAN_END = 22;
const BAR_OSM_START = 22;
const BAR_OSM_MAX = 50;
const BAR_ASSIGN_START = 50;
const BAR_ASSIGN_MAX = 58;
const BAR_ROAD_START = 58;
const DEFAULT_WALKING_RADIUS_METERS = 400;
const MIN_WALKING_RADIUS_METERS = 100;
const MAX_WALKING_RADIUS_METERS = 2000;
const DEFAULT_WALKING_TIME_MINUTES = 20;
const MIN_WALKING_TIME_MINUTES = 5;
const MAX_WALKING_TIME_MINUTES = 60;
const LIVE_RECOMPUTE_THROTTLE_MS = 120;
const DISTANCE_PRESETS_METERS = [250, 500, 750, 1000] as const;
const TIME_PRESETS_MINUTES = [10, 20, 30] as const;
const APPROX_WALK_METERS_PER_MIN = 85;

function getConsolidationMatrixMaxMeters(): number {
  return Math.max(
    MAX_WALKING_RADIUS_METERS,
    Math.round(MAX_WALKING_TIME_MINUTES * APPROX_WALK_METERS_PER_MIN)
  );
}

function clampWalkingRadiusMeters(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_WALKING_RADIUS_METERS;
  return Math.min(MAX_WALKING_RADIUS_METERS, Math.max(MIN_WALKING_RADIUS_METERS, Math.round(value)));
}

function clampWalkingTimeMinutes(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_WALKING_TIME_MINUTES;
  return Math.min(MAX_WALKING_TIME_MINUTES, Math.max(MIN_WALKING_TIME_MINUTES, Math.round(value)));
}

function getConsolidationSummaryMessage(
  originalUniqueTargets: number,
  consolidatedUniqueTargets: number,
  averageWalkingMinutes?: number
): string {
  if (originalUniqueTargets <= 0) {
    return 'Consolidation summary: no eligible assigned targets were available for consolidation.';
  }
  const reduction = Math.max(0, originalUniqueTargets - consolidatedUniqueTargets);
  const rate = (reduction / originalUniqueTargets) * 100;
  const avgTimeText =
    typeof averageWalkingMinutes === 'number' && Number.isFinite(averageWalkingMinutes)
      ? ` Avg walking time: ${averageWalkingMinutes.toFixed(1)} min.`
      : '';
  return `Consolidation summary: ${originalUniqueTargets} original target${
    originalUniqueTargets === 1 ? '' : 's'
  } -> ${consolidatedUniqueTargets} consolidated pickup point${
    consolidatedUniqueTargets === 1 ? '' : 's'
  } (${rate.toFixed(1)}% consolidation).${avgTimeText}`;
}

type ConsolidationSummaryView = {
  originalTargets: number;
  consolidatedTargets: number;
  reductionCount: number;
  consolidationRatePct: number;
  avgWalkingMinutes: number;
};

type RunTiming = {
  elapsedMs: number;
  etaRemainingMs: number | null;
};

function deriveLaunchSource(referrer: string | null, search: string): ToolLaunchSource {
  const params = new URLSearchParams(search);
  if (params.has('shared')) {
    return 'shared_link';
  }
  if (referrer && referrer.includes('/tools')) {
    return 'directory';
  }
  return 'direct_url';
}

function categorizeAssignmentError(error: unknown): {
  errorCategory: string;
  errorCode?: string;
  retryable: boolean;
  failedStage: PostcodeAssignmentFailedStage;
} {
  const message = error instanceof Error ? error.message : String(error);
  const lower = message.toLowerCase();
  let failedStage: PostcodeAssignmentFailedStage = 'render';
  if (lower.includes('postcode') || lower.includes('geocode')) {
    failedStage = 'geocode';
  } else if (lower.includes('osrm') || lower.includes('road')) {
    failedStage = 'osrm';
  } else if (lower.includes('osm') || lower.includes('overpass')) {
    failedStage = 'osm_overpass';
  }
  let errorCategory = 'unknown';
  if (lower.includes('network') || lower.includes('fetch')) {
    errorCategory = 'network';
  } else if (lower.includes('timeout')) {
    errorCategory = 'timeout';
  } else if (lower.includes('column') || lower.includes('workbook') || lower.includes('row')) {
    errorCategory = 'validation';
  }
  return {
    errorCategory,
    errorCode: message.slice(0, 120),
    retryable: errorCategory === 'network' || errorCategory === 'timeout',
    failedStage,
  };
}

const PostcodeStopAssignmentPage: React.FC = () => {
  type ConsolidationRuntimeInput = ConsolidationInputRow & { sheetIndex: number; rowIndex: number };
  const { t } = useTranslation('simulation');
  const location = useLocation();
  const navigate = useNavigate();
  const appContext = React.useContext(AppContext);
  const { bulkAddRides } = useRides();
  const [postcodeFile, setPostcodeFileState] = React.useState<File | null>(null);
  const [hubFile, setHubFileState] = React.useState<File | null>(null);

  React.useEffect(() => {
    const referrer = typeof document !== 'undefined' ? document.referrer : null;
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.tool_launched, {
      tool_id: 'postcode_stop_assignment',
      source: deriveLaunchSource(referrer, location.search),
      timestamp: new Date().toISOString(),
    });
    // Mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPostcodeFile = React.useCallback(
    (file: File | null, method: 'drag_drop' | 'file_input' = 'file_input') => {
      if (file) {
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.postcode_assignment_file_added, {
          file_type: 'postcode_workbook',
          upload_method: method,
          timestamp: new Date().toISOString(),
        });
      }
      setPostcodeFileState(file);
    },
    []
  );

  const setHubFile = React.useCallback(
    (file: File | null, method: 'drag_drop' | 'file_input' = 'file_input') => {
      if (file) {
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.postcode_assignment_file_added, {
          file_type: 'hub_workbook',
          upload_method: method,
          timestamp: new Date().toISOString(),
        });
      }
      setHubFileState(file);
    },
    []
  );
  const postcodeWorkbookInputRef = React.useRef<HTMLInputElement>(null);
  const hubWorkbookInputRef = React.useRef<HTMLInputElement>(null);
  const [sheetResults, setSheetResults] = React.useState<ResultSheet[]>([]);
  const [activeSheet, setActiveSheet] = React.useState<string>('');
  const [isRunning, setIsRunning] = React.useState(false);
  const [isConstraintRecomputing, setIsConstraintRecomputing] = React.useState(false);
  const [assignmentProgress, setAssignmentProgress] = React.useState<AssignmentProgress | null>(null);
  const [runTiming, setRunTiming] = React.useState<RunTiming | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [infoMessage, setInfoMessage] = React.useState<string | null>(null);
  const [consolidationSummary, setConsolidationSummary] = React.useState<ConsolidationSummaryView | null>(null);
  const [search, setSearch] = React.useState('');
  const [searchRadiusMeters, setSearchRadiusMeters] = React.useState(1000);
  const [walkingRadiusMeters, setWalkingRadiusMeters] = React.useState(DEFAULT_WALKING_RADIUS_METERS);
  const [walkingRadiusEnabled, setWalkingRadiusEnabled] = React.useState(true);
  const [walkingTimeMinutes, setWalkingTimeMinutes] = React.useState(DEFAULT_WALKING_TIME_MINUTES);
  const [walkingTimeEnabled, setWalkingTimeEnabled] = React.useState(false);
  const [distancePresetCards, setDistancePresetCards] = React.useState<
    Array<{ label: string; meters: number; stopCount: number; avgWalkingMinutes: number }>
  >([]);
  const [timePresetCards, setTimePresetCards] = React.useState<
    Array<{ label: string; minutes: number; stopCount: number; avgWalkingMinutes: number }>
  >([]);
  /** When false, consolidation uses crow-fly only (no OSRM walking batch calls). */
  const [useOsrmWalkingForConsolidation, setUseOsrmWalkingForConsolidation] = React.useState(true);
  const [workbookPreview, setWorkbookPreview] = React.useState<WorkbookSheetPreview[] | null>(null);
  const [workbookPreviewLoading, setWorkbookPreviewLoading] = React.useState(false);
  const [workbookPreviewError, setWorkbookPreviewError] = React.useState<string | null>(null);
  /** Per sheet: column header key, or AUTO_POSTCODE_COLUMN_SELECT. */
  const [postcodeColumnSelectionBySheet, setPostcodeColumnSelectionBySheet] = React.useState<Record<string, string>>(
    {}
  );
  /** Per sheet: workbook columns for row origin lat/lon - optional; None uses postcodes.io only. */
  const [postcodeOriginLatColumnSelectionBySheet, setPostcodeOriginLatColumnSelectionBySheet] = React.useState<
    Record<string, string>
  >({});
  const [postcodeOriginLonColumnSelectionBySheet, setPostcodeOriginLonColumnSelectionBySheet] = React.useState<
    Record<string, string>
  >({});
  /** Per sheet: separate lat/lon columns vs one cell with both (same parsing as hub combined mode). */
  const [postcodeOriginLayoutBySheet, setPostcodeOriginLayoutBySheet] = React.useState<
    Record<string, PostcodeOriginLayoutMode>
  >({});
  const [postcodeOriginCombinedColumnSelectionBySheet, setPostcodeOriginCombinedColumnSelectionBySheet] =
    React.useState<Record<string, string>>({});
  /** When false (default): hide lat/lon/combined postcode controls and behave as postcodes.io-only origins per sheet. */
  const [postcodeRowCoordinatesEnabledBySheet, setPostcodeRowCoordinatesEnabledBySheet] = React.useState<
    Record<string, boolean>
  >({});

  const [hubWorkbookPreview, setHubWorkbookPreview] = React.useState<HubWorkbookPreview | null>(null);
  const [hubPreviewLoading, setHubPreviewLoading] = React.useState(false);
  const [hubPreviewError, setHubPreviewError] = React.useState<string | null>(null);
  /** User hub column picks (same keys reused on every sheet when running). */
  const [hubCoordLayout, setHubCoordLayout] = React.useState<HubCoordLayoutMode>('separate');
  const [hubLatColumnSelection, setHubLatColumnSelection] = React.useState<string>(AUTO_HUB_LAT_COLUMN);
  const [hubLonColumnSelection, setHubLonColumnSelection] = React.useState<string>(AUTO_HUB_LON_COLUMN);
  const [hubCombinedColumnSelection, setHubCombinedColumnSelection] = React.useState<string>(AUTO_HUB_COMBINED_COLUMN);
  const [hubNameColumnSelection, setHubNameColumnSelection] = React.useState<string>(AUTO_HUB_NAME_COLUMN);
  const [assignToPredeterminedHubs, setAssignToPredeterminedHubs] = React.useState(false);

  /** Simulation-ready output controls. Off by default - tool keeps current diagnostic-only behaviour. */
  const [simulationOutputEnabled, setSimulationOutputEnabled] = React.useState(false);
  const [postcodeDirection, setPostcodeDirection] = React.useState<SimulationPostcodeDirection>(
    'postcode_origin'
  );
  const [otherEndpointMode, setOtherEndpointMode] = React.useState<SimulationOtherEndpointMode>('manual');
  const [otherEndpointLayout, setOtherEndpointLayout] = React.useState<SimulationOtherEndpointLayout>(
    'separate'
  );
  const [otherEndpointManualLatText, setOtherEndpointManualLatText] = React.useState('51.0066834');
  const [otherEndpointManualLonText, setOtherEndpointManualLonText] = React.useState('-0.9518752');
  /** Free-text label for the other endpoint - flows into from_address/to_address (per direction). */
  const [otherEndpointManualAddress, setOtherEndpointManualAddress] = React.useState('Whitman Labs');
  const [otherEndpointAddressSelection, setOtherEndpointAddressSelection] = React.useState<string>(
    AUTO_OTHER_ADDRESS_COLUMN
  );
  const [otherEndpointLatSelection, setOtherEndpointLatSelection] = React.useState<string>(
    AUTO_OTHER_LAT_COLUMN
  );
  const [otherEndpointLonSelection, setOtherEndpointLonSelection] = React.useState<string>(
    AUTO_OTHER_LON_COLUMN
  );
  const [otherEndpointCombinedSelection, setOtherEndpointCombinedSelection] = React.useState<string>(
    AUTO_OTHER_COMBINED_COLUMN
  );
  const [requestTimeMode, setRequestTimeMode] = React.useState<SimulationRequestTimeMode>('manual');
  /** Stored as the raw <input type="datetime-local"> value (e.g. "2026-05-11T08:00"); converted to ISO at run time. */
  const [requestTimeManualLocal, setRequestTimeManualLocal] = React.useState(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    // Initial default = DAY band's arrival pickup time (06:40). This is the
    // value written to the simulator as `request_time`; the dropdown label
    // shows the corresponding work shift (DAY 07:00–15:00).
    return `${yyyy}-${mm}-${dd}T${getPassengerArrivalTime('DAY')}`;
  });
  const [requestTimeSelection, setRequestTimeSelection] = React.useState<string>(AUTO_REQUEST_TIME_COLUMN);
  const [selectedShiftBand, setSelectedShiftBand] = React.useState<ShiftBand>('DAY');
  // Default `request_time` for the band is the arrival pickup time —
  // passenger work start minus the 20-min buffer (e.g. DAY → 06:40). The
  // row-builder no longer subtracts again; this default is already the final
  // pickup target written into rides.
  const shiftBandStartTime = React.useCallback((band: ShiftBand): string => {
    return getPassengerArrivalTime(band);
  }, []);
  const currentLocalDate = React.useCallback((): string => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);
  /**
   * Passengers are always column-driven with hard-coded "1 standard" fallbacks - no manual count
   * field. The type-fallback constant stays at {@link DEFAULT_PASSENGER_TYPE}; the count constant
   * stays at {@link DEFAULT_PASSENGER_COUNT}.
   */
  const passengersManualType = DEFAULT_PASSENGER_TYPE;
  const [passengersCountSelection, setPassengersCountSelection] = React.useState<string>(
    AUTO_PASSENGERS_COUNT_COLUMN
  );
  const [passengersTypeSelection, setPassengersTypeSelection] = React.useState<string>(
    AUTO_PASSENGERS_TYPE_COLUMN
  );
  const [rideIdSelection, setRideIdSelection] = React.useState<string>(AUTO_RIDE_ID_COLUMN);
  const [userIdSelection, setUserIdSelection] = React.useState<string>(AUTO_USER_ID_COLUMN);

  /** Parsed once per render so downstream consumers (run loop, validators) share the same parse logic. */
  const manualOtherEndpoint = React.useMemo<{ lat: number; lon: number } | null>(() => {
    const lat = toNumber(otherEndpointManualLatText);
    const lon = toNumber(otherEndpointManualLonText);
    if (lat === null || lon === null) return null;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
    return { lat, lon };
  }, [otherEndpointManualLatText, otherEndpointManualLonText]);

  const manualRequestTimeIso = React.useMemo<string>(() => {
    if (!requestTimeManualLocal.trim()) return '';
    return coerceRequestTimeCellToIso(requestTimeManualLocal);
  }, [requestTimeManualLocal]);

  /** Union of column header keys across every postcode sheet; deduped, original order preserved. */
  const allPostcodeColumnKeys = React.useMemo<string[]>(() => {
    if (!workbookPreview) return [];
    const seen = new Set<string>();
    const ordered: string[] = [];
    workbookPreview.forEach((sheet) => {
      sheet.columnKeys.forEach((key) => {
        if (!seen.has(key)) {
          seen.add(key);
          ordered.push(key);
        }
      });
    });
    return ordered;
  }, [workbookPreview]);

  /** Auto-detection on column union — same logic resolveSimulationOutputPlan runs per sheet, surfaced for UI labels. */
  const detectedOtherLat = React.useMemo(
    () => detectOtherEndpointLatColumn(allPostcodeColumnKeys) ?? detectHubLatColumn(allPostcodeColumnKeys),
    [allPostcodeColumnKeys]
  );
  const detectedOtherLon = React.useMemo(
    () => detectOtherEndpointLonColumn(allPostcodeColumnKeys) ?? detectHubLonColumn(allPostcodeColumnKeys),
    [allPostcodeColumnKeys]
  );
  const detectedOtherCombined = React.useMemo(
    () => detectHubCombinedColumnFromHeaders(allPostcodeColumnKeys),
    [allPostcodeColumnKeys]
  );
  const detectedOtherAddress = React.useMemo(
    () => detectOtherEndpointAddressColumn(allPostcodeColumnKeys),
    [allPostcodeColumnKeys]
  );
  const detectedRequestTimeKey = React.useMemo(
    () => detectRequestTimeColumn(allPostcodeColumnKeys),
    [allPostcodeColumnKeys]
  );
  const detectedPassengersCountKey = React.useMemo(
    () => detectPassengersCountColumn(allPostcodeColumnKeys),
    [allPostcodeColumnKeys]
  );
  const detectedPassengersTypeKey = React.useMemo(
    () => detectPassengersTypeColumn(allPostcodeColumnKeys),
    [allPostcodeColumnKeys]
  );
  const detectedRideIdKey = React.useMemo(
    () => detectRideIdColumn(allPostcodeColumnKeys),
    [allPostcodeColumnKeys]
  );
  const detectedUserIdKey = React.useMemo(
    () => detectUserIdColumn(allPostcodeColumnKeys),
    [allPostcodeColumnKeys]
  );

  const trackConfigChanged = React.useCallback(
    (fieldGroup: 'walking_radius' | 'walking_time' | 'walking_radius_toggle' | 'walking_time_toggle' | 'walking_preset') => {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.postcode_assignment_config_changed, {
        field_group: fieldGroup,
        timestamp: new Date().toISOString(),
      });
    },
    []
  );

  const assignmentAbortRef = React.useRef<AbortController | null>(null);
  const runStartMsRef = React.useRef(0);
  const lastBarPercentRef = React.useRef(0);
  const assignmentMapTrailRef = React.useRef<PostcodeAssignmentMapLive[]>([]);
  const [mapRevision, setMapRevision] = React.useState(0);

  // ── Ops vehicle mapping (optional) ──────────────────────────────────────────
  /** When true the Ops vehicle mapping panel is visible. */
  const [opsVehicleMappingEnabled, setOpsVehicleMappingEnabled] = React.useState(false);
  const environmentTokensEnabled = useFeatureFlags().isFeatureRuntimeEnabled(ENVIRONMENT_TOKENS_FLAG);
  /**
   * Vehicle slots linking simulation routes to real Ops vehicle IDs.
   * W9 reads `opsVehicleId` from each slot when building route upload shift
   * assignment payloads.
   */
  const [vehicleSlots, setVehicleSlots] = React.useState<VehicleSlot[]>([]);
  const handleShiftBandChange = React.useCallback((band: ShiftBand) => {
    setSelectedShiftBand(band);
    setRequestTimeMode('manual');
    setRequestTimeManualLocal(`${currentLocalDate()}T${shiftBandStartTime(band)}`);
    const bandWindow = getShiftBandWindow(band);
    const target = { startWindow: bandWindow.startTime, endWindow: bandWindow.endTime };
    setVehicleSlots((prev) =>
      prev.map((slot) => ({ ...slot, startWindow: target.startWindow, endWindow: target.endWindow }))
    );
  }, [currentLocalDate, shiftBandStartTime]);

  /** Persist OSM stop lookups across runs so repeated attempts can reuse prior Overpass responses. */
  const osmStopCacheRef = React.useRef<Map<string, StopRow[]>>(new Map());
  const consolidationMatrixRef = React.useRef<ConsolidationMatrix | null>(null);
  const consolidationInputsRef = React.useRef<ConsolidationRuntimeInput[]>([]);
  const baseResultsRef = React.useRef<ResultSheet[] | null>(null);
  const consolidationMapEntriesRef = React.useRef<
    Array<{ originLat: number; originLon: number; targetLat: number; targetLon: number }>
  >([]);
  const liveRecomputeTimerRef = React.useRef<number | null>(null);
  const pendingPresetRecomputeRef = React.useRef(false);
  /**
   * Snapshot of the user's simulation-output config at the moment Run is pressed. Live re-applies
   * (slider changes) read from this so they don't pick up half-typed manual values after the run.
   */
  const simulationRunPlanRef = React.useRef<SimulationOutputRunPlan | null>(null);
  /**
   * Sequential ID counters for the per-postcode-row simulation output. Reset at the start of every
   * run so a fresh assignment always begins at ride1/user1. IDs are write-once on each result row
   * (see {@link writeSimulationColumnsForRow}), so slider re-applies don't re-increment.
   */
  const rideIdCounterRef = React.useRef(0);
  const userIdCounterRef = React.useRef(0);

  /**
   * Overwrites the 10 simulation-output columns on a single result row using the run-time plan.
   * Reads postcode-side coordinates from the row's consolidated_* fields when present, else the raw
   * postcode_* fields. Idempotent: safe to call multiple times during live re-applies.
   */
  const writeSimulationColumnsForRow = React.useCallback(
    (
      resultRow: RowWithNearest,
      originalRow: Record<string, unknown>,
      sheetPlan: SimulationOutputSheetPlan,
      runPlan: SimulationOutputRunPlan
    ): void => {
      const consolidatedLat = toNumber(resultRow.consolidated_stop_lat);
      const consolidatedLon = toNumber(resultRow.consolidated_stop_lon);
      const postcodeLat = toNumber(resultRow.postcode_lat);
      const postcodeLon = toNumber(resultRow.postcode_lon);
      const snappedLat = consolidatedLat ?? postcodeLat;
      const snappedLon = consolidatedLon ?? postcodeLon;
      const consolidatedName =
        typeof resultRow.consolidated_stop_name === 'string' ? resultRow.consolidated_stop_name : '';
      const suggestedName =
        typeof resultRow.suggested_stop_name === 'string' ? resultRow.suggested_stop_name : '';
      const postcodeColumn =
        typeof resultRow.postcode === 'string' && resultRow.postcode
          ? resultRow.postcode
          : null;
      const stopLabel = consolidatedName || suggestedName;
      // 2026-05-28 (Issue 2 / Ben): the snapped address used to be `${postcode} / ${stopName}`
      // (e.g. "PO1 5JS / Lake Road Health Centre"). Ben asked for just the stop / hub name;
      // the postcode is still preserved in the standalone `postcode` cell and in the new
      // `consolidated_stop_postcode` column on the rollup sheet so nothing is lost.
      const snappedAddress = stopLabel || (postcodeColumn ?? '');

      const other = pickRowOtherEndpoint(originalRow, sheetPlan, runPlan.manualOther);
      const otherAddress = pickRowOtherEndpointAddress(originalRow, sheetPlan, runPlan.manualOtherAddress);
      const requestTimeIso = pickRowRequestTimeIso(originalRow, sheetPlan, runPlan.manualRequestTimeIso);
      const { count: paxCount, type: paxType } = pickRowPassengers(
        originalRow,
        sheetPlan,
        runPlan.manualPassengersCount,
        runPlan.manualPassengersType
      );

      const postcodeIsOrigin = runPlan.direction === 'postcode_origin';
      const fromLat = postcodeIsOrigin ? snappedLat : other?.lat ?? null;
      const fromLon = postcodeIsOrigin ? snappedLon : other?.lon ?? null;
      const toLat = postcodeIsOrigin ? other?.lat ?? null : snappedLat;
      const toLon = postcodeIsOrigin ? other?.lon ?? null : snappedLon;
      const fromAddress = postcodeIsOrigin ? snappedAddress : otherAddress;
      const toAddress = postcodeIsOrigin ? otherAddress : snappedAddress;
      const requestType = postcodeIsOrigin ? 'departure_fixed' : 'arrival_fixed';

      resultRow.from_latitude = fromLat ?? '';
      resultRow.from_longitude = fromLon ?? '';
      resultRow.from_address = fromAddress;
      resultRow.to_latitude = toLat ?? '';
      resultRow.to_longitude = toLon ?? '';
      resultRow.to_address = toAddress;
      resultRow.request_time = requestTimeIso;
      resultRow.request_type = requestType;
      resultRow.passengers_count = paxCount;
      resultRow.passengers_type = paxType;
      // IDs are write-once: slider re-applies keep the same ride/user identity for a given input row.
      if (typeof resultRow.ride_id !== 'string' || !resultRow.ride_id) {
        const fromColumn = sheetPlan.rideIdKey ? String(originalRow[sheetPlan.rideIdKey] ?? '').trim() : '';
        if (fromColumn) {
          resultRow.ride_id = fromColumn;
        } else {
          rideIdCounterRef.current += 1;
          resultRow.ride_id = `ride${rideIdCounterRef.current}`;
        }
      }
      if (typeof resultRow.user_id !== 'string' || !resultRow.user_id) {
        const fromColumn = sheetPlan.userIdKey ? String(originalRow[sheetPlan.userIdKey] ?? '').trim() : '';
        if (fromColumn) {
          resultRow.user_id = fromColumn;
        } else {
          userIdCounterRef.current += 1;
          resultRow.user_id = `user${userIdCounterRef.current}`;
        }
      }
    },
    []
  );

  /**
   * Stashed original rows + per-sheet plans keyed by [sheetIndex][rowIndex] so live re-applies can
   * read the unsnapped other-endpoint and time/passenger cells without touching the workbook again.
   */
  const simulationOriginalRowsRef = React.useRef<Array<{ sheetName: string; rows: Array<Record<string, unknown>> }>>(
    []
  );

  const handleClearHubFile = () => {
    if (hubFile) {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.postcode_assignment_file_cleared, {
        file_type: 'hub_workbook',
        timestamp: new Date().toISOString(),
      });
    }
    setHubFileState(null);
    setHubWorkbookPreview(null);
    setHubPreviewError(null);
    setHubPreviewLoading(false);
    setHubCoordLayout('separate');
    setHubLatColumnSelection(AUTO_HUB_LAT_COLUMN);
    setHubLonColumnSelection(AUTO_HUB_LON_COLUMN);
    setHubCombinedColumnSelection(AUTO_HUB_COMBINED_COLUMN);
    setHubNameColumnSelection(AUTO_HUB_NAME_COLUMN);
    if (hubWorkbookInputRef.current) {
      hubWorkbookInputRef.current.value = '';
    }
  };

  const handleClearPostcodeFile = () => {
    if (postcodeFile) {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.postcode_assignment_file_cleared, {
        file_type: 'postcode_workbook',
        timestamp: new Date().toISOString(),
      });
    }
    setPostcodeFileState(null);
    consolidationMatrixRef.current = null;
    consolidationInputsRef.current = [];
    consolidationMapEntriesRef.current = [];
    baseResultsRef.current = null;
    setDistancePresetCards([]);
    setTimePresetCards([]);
    setConsolidationSummary(null);
    if (postcodeWorkbookInputRef.current) {
      postcodeWorkbookInputRef.current.value = '';
    }
  };

  React.useEffect(() => {
    if (!postcodeFile) {
      setWorkbookPreview(null);
      setWorkbookPreviewError(null);
      setWorkbookPreviewLoading(false);
      setPostcodeColumnSelectionBySheet({});
      setPostcodeOriginLatColumnSelectionBySheet({});
      setPostcodeOriginLonColumnSelectionBySheet({});
      setPostcodeOriginLayoutBySheet({});
      setPostcodeOriginCombinedColumnSelectionBySheet({});
      setPostcodeRowCoordinatesEnabledBySheet({});
      return undefined;
    }
    let cancelled = false;
    setWorkbookPreviewLoading(true);
    setWorkbookPreviewError(null);
    void (async () => {
      try {
        const buffer = await postcodeFile.arrayBuffer();
        if (cancelled) return;
        const wb = XLSX.read(buffer, { type: 'array' });
        const preview: WorkbookSheetPreview[] = wb.SheetNames.map((name) => {
          const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(wb.Sheets[name]);
          const columnKeys = rows[0] ? Object.keys(rows[0]) : [];
          const inferredCombined = inferHubCombinedLatLonColumn(rows);
          const headerCombinedGuess = detectHubCombinedColumnFromHeaders(columnKeys);
          return {
            name,
            columnKeys,
            rowCount: rows.length,
            detectedPostcodeColumn: resolvePostcodeColumnForSheet(rows),
            detectedPostcodeLatColumn: detectHubLatColumn(columnKeys),
            detectedPostcodeLonColumn: detectHubLonColumn(columnKeys),
            detectedPostcodeCombinedColumn: inferredCombined ?? headerCombinedGuess,
          };
        });
        if (!cancelled) {
          setWorkbookPreview(preview);
          setPostcodeColumnSelectionBySheet({});
          setPostcodeOriginLatColumnSelectionBySheet({});
          setPostcodeOriginLonColumnSelectionBySheet({});
          setPostcodeOriginLayoutBySheet({});
          setPostcodeOriginCombinedColumnSelectionBySheet({});
          setPostcodeRowCoordinatesEnabledBySheet({});
        }
      } catch (parseErr) {
        if (!cancelled) {
          setWorkbookPreview(null);
          setWorkbookPreviewError(
            parseErr instanceof Error ? parseErr.message : 'Could not read that workbook.'
          );
        }
      } finally {
        if (!cancelled) {
          setWorkbookPreviewLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [postcodeFile]);

  React.useEffect(() => {
    if (!hubFile) {
      setHubWorkbookPreview(null);
      setHubPreviewError(null);
      setHubPreviewLoading(false);
      setHubCoordLayout('separate');
      setHubLatColumnSelection(AUTO_HUB_LAT_COLUMN);
      setHubLonColumnSelection(AUTO_HUB_LON_COLUMN);
      setHubCombinedColumnSelection(AUTO_HUB_COMBINED_COLUMN);
      setHubNameColumnSelection(AUTO_HUB_NAME_COLUMN);
      return undefined;
    }
    let cancelled = false;
    setHubPreviewLoading(true);
    setHubPreviewError(null);
    void (async () => {
      try {
        const buffer = await hubFile.arrayBuffer();
        if (cancelled) return;
        const wb = XLSX.read(buffer, { type: 'array' });
        const head = hubHeaderKeysFromFirstDataSheet(wb);
        if (!head) {
          setHubWorkbookPreview(null);
          setHubPreviewError('Hub workbook has no rows with readable headers.');
          return;
        }
        const sheet = wb.Sheets[head.sheetName];
        const rows = sheet ? XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet) : [];
        const columnKeys = head.keys;
        const inferredCombined = inferHubCombinedLatLonColumn(rows);
        const headerCombined = detectHubCombinedColumnFromHeaders(columnKeys);
        if (!cancelled) {
          setHubWorkbookPreview({
            previewSheetName: head.sheetName,
            columnKeys,
            rowCount: rows.length,
            detectedLatColumn: detectHubLatColumn(columnKeys),
            detectedLonColumn: detectHubLonColumn(columnKeys),
            detectedNameColumn: detectHubNameColumn(columnKeys),
            detectedCombinedColumn: inferredCombined ?? headerCombined,
          });
          setHubLatColumnSelection(AUTO_HUB_LAT_COLUMN);
          setHubLonColumnSelection(AUTO_HUB_LON_COLUMN);
          setHubCombinedColumnSelection(AUTO_HUB_COMBINED_COLUMN);
          setHubNameColumnSelection(AUTO_HUB_NAME_COLUMN);
        }
      } catch (parseErr) {
        if (!cancelled) {
          setHubWorkbookPreview(null);
          setHubPreviewError(
            parseErr instanceof Error ? parseErr.message : 'Could not read that hub workbook.'
          );
        }
      } finally {
        if (!cancelled) {
          setHubPreviewLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hubFile]);
  React.useEffect(
    () => () => {
      if (liveRecomputeTimerRef.current != null) {
        window.clearTimeout(liveRecomputeTimerRef.current);
      }
    },
    []
  );

  const applyCachedConsolidation = React.useCallback(() => {
    const matrix = consolidationMatrixRef.current;
    const inputs = consolidationInputsRef.current;
    const base = baseResultsRef.current;
    if (!matrix || !inputs.length || !base) return;

    const outputs = applyConsolidationConstraints(matrix, {
      distanceEnabled: walkingRadiusEnabled,
      maxWalkingMeters: clampWalkingRadiusMeters(walkingRadiusMeters),
      timeEnabled: walkingTimeEnabled,
      maxWalkingMinutes: clampWalkingTimeMinutes(walkingTimeMinutes),
    });

    const nextResults = base.map((sheet) => ({
      name: sheet.name,
      rows: sheet.rows.map((row) => ({ ...row })),
    }));

    const runPlan = simulationRunPlanRef.current;
    const originalSheets = simulationOriginalRowsRef.current;
    for (let i = 0; i < inputs.length; i += 1) {
      const input = inputs[i]!;
      const row = outputs[i] as ConsolidationOutputRow;
      const resultRow = nextResults[input.sheetIndex]?.rows[input.rowIndex];
      if (!resultRow) continue;
      resultRow.consolidated_stop_name = row.consolidatedStopName;
      resultRow.consolidated_stop_lat = row.consolidatedStopLat;
      resultRow.consolidated_stop_lon = row.consolidatedStopLon;
      resultRow.consolidated_target_type = row.consolidatedTargetType;
      resultRow.consolidated_stop_distance_crow_flies_km = row.consolidatedStopDistanceCrowFliesKm;
      resultRow.walking_distance_to_consolidated_km = row.walkingDistanceToConsolidatedKm;
      resultRow.walking_time_to_consolidated_min = row.walkingTimeToConsolidatedMin;
      if (runPlan?.enabled) {
        const sheetName = nextResults[input.sheetIndex]?.name;
        const sheetPlan = sheetName ? runPlan.perSheet[sheetName] : undefined;
        const originalRow = originalSheets[input.sheetIndex]?.rows[input.rowIndex];
        if (sheetPlan && originalRow) {
          writeSimulationColumnsForRow(resultRow, originalRow, sheetPlan, runPlan);
        }
      }
    }

    const trailEntries = consolidationMapEntriesRef.current;
    for (let i = 0; i < outputs.length && i < trailEntries.length; i += 1) {
      const out = outputs[i]!;
      const m = trailEntries[i]!;
      const trail = assignmentMapTrailRef.current.find(
        (entry) =>
          entry.originLat === m.originLat &&
          entry.originLon === m.originLon &&
          entry.stopLat === m.targetLat &&
          entry.stopLon === m.targetLon
      );
      if (trail) {
        trail.consolidatedStopLat = out.consolidatedStopLat;
        trail.consolidatedStopLon = out.consolidatedStopLon;
        trail.consolidatedStopName = out.consolidatedStopName;
        trail.walkingDistanceToConsolidatedKm = out.walkingDistanceToConsolidatedKm;
        trail.walkingTimeToConsolidatedMin = out.walkingTimeToConsolidatedMin;
      }
    }
    setMapRevision((r) => r + 1);

    if (runPlan?.enabled) {
      aggregateConsolidatedPassengers(nextResults);
    }

    setSheetResults(withSimulationTripsSheet(nextResults, runPlan?.enabled ?? false));
    const summary = summarizeConsolidation(outputs, inputs);
    setConsolidationSummary({
      originalTargets: summary.originalUniqueTargets,
      consolidatedTargets: summary.consolidatedUniqueTargets,
      reductionCount: Math.max(0, summary.originalUniqueTargets - summary.consolidatedUniqueTargets),
      consolidationRatePct:
        summary.originalUniqueTargets > 0
          ? (Math.max(0, summary.originalUniqueTargets - summary.consolidatedUniqueTargets) /
              summary.originalUniqueTargets) *
            100
          : 0,
      avgWalkingMinutes: summary.averageWalkingMinutes,
    });
  }, [
    walkingRadiusEnabled,
    walkingRadiusMeters,
    walkingTimeEnabled,
    walkingTimeMinutes,
    writeSimulationColumnsForRow,
  ]);

  const recomputePresetCards = React.useCallback(() => {
    const matrix = consolidationMatrixRef.current;
    const inputs = consolidationInputsRef.current;
    if (!matrix || !inputs.length) {
      setDistancePresetCards([]);
      setTimePresetCards([]);
      return;
    }
    const distCards = DISTANCE_PRESETS_METERS.map((meters) => {
      const outputs = applyConsolidationConstraints(matrix, {
        distanceEnabled: true,
        maxWalkingMeters: meters,
        timeEnabled: false,
        maxWalkingMinutes: clampWalkingTimeMinutes(walkingTimeMinutes),
      });
      const s = summarizeConsolidation(outputs, inputs);
      return {
        label: meters >= 1000 ? `${(meters / 1000).toFixed(0)}km` : `${meters}m`,
        meters,
        stopCount: s.consolidatedUniqueTargets,
        avgWalkingMinutes: s.averageWalkingMinutes,
      };
    });
    const tCards = TIME_PRESETS_MINUTES.map((minutes) => {
      const outputs = applyConsolidationConstraints(matrix, {
        distanceEnabled: false,
        maxWalkingMeters: clampWalkingRadiusMeters(walkingRadiusMeters),
        timeEnabled: true,
        maxWalkingMinutes: minutes,
      });
      const s = summarizeConsolidation(outputs, inputs);
      return {
        label: `${minutes} min`,
        minutes,
        stopCount: s.consolidatedUniqueTargets,
        avgWalkingMinutes: s.averageWalkingMinutes,
      };
    });
    setDistancePresetCards(distCards);
    setTimePresetCards(tCards);
  }, [walkingRadiusEnabled, walkingRadiusMeters, walkingTimeEnabled, walkingTimeMinutes]);

  /** Rebuild consolidation matrix when OSRM walking toggle changes (same row inputs, different cost model). */
  React.useEffect(() => {
    const inputs = consolidationInputsRef.current;
    if (!inputs.length) return undefined;
    const ac = new AbortController();
    let cancelled = false;
    void (async () => {
      setIsConstraintRecomputing(true);
      try {
        const maxCandidatesPerRow = Math.min(10, Math.max(6, Math.ceil(1200 / Math.max(1, inputs.length))));
        const maxPairs = 3000;
        const matrix = await buildConsolidationMatrix(inputs, getConsolidationMatrixMaxMeters(), ac.signal, {
          maxCandidatesPerRow,
          maxPairs,
          skipOsrmWalking: !useOsrmWalkingForConsolidation,
        });
        if (cancelled || ac.signal.aborted) return;
        consolidationMatrixRef.current = matrix;
        applyCachedConsolidation();
        recomputePresetCards();
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        console.warn('[PostcodeAssignment] consolidation matrix rebuild failed', e);
      } finally {
        if (!cancelled) {
          setIsConstraintRecomputing(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only when toggle changes, not when sliders change
  }, [useOsrmWalkingForConsolidation]);

  const runCachedUiRecompute = React.useCallback(() => {
    setIsConstraintRecomputing(true);
    window.setTimeout(() => {
      try {
        applyCachedConsolidation();
        recomputePresetCards();
      } finally {
        setIsConstraintRecomputing(false);
      }
    }, 0);
  }, [applyCachedConsolidation, recomputePresetCards]);

  /**
   * Preset card clicks update several pieces of state; because state updates are async, recomputing immediately in the click
   * handler can use stale values. This effect runs the recompute after React has committed the new preset state.
   */
  React.useEffect(() => {
    if (!pendingPresetRecomputeRef.current) return;
    pendingPresetRecomputeRef.current = false;
    runCachedUiRecompute();
  }, [walkingRadiusEnabled, walkingRadiusMeters, walkingTimeEnabled, walkingTimeMinutes, runCachedUiRecompute]);

  const scheduleLiveRecompute = React.useCallback(() => {
    if (liveRecomputeTimerRef.current != null) {
      window.clearTimeout(liveRecomputeTimerRef.current);
    }
    setIsConstraintRecomputing(true);
    liveRecomputeTimerRef.current = window.setTimeout(() => {
      try {
        applyCachedConsolidation();
        recomputePresetCards();
      } finally {
        liveRecomputeTimerRef.current = null;
        setIsConstraintRecomputing(false);
      }
    }, LIVE_RECOMPUTE_THROTTLE_MS);
  }, [applyCachedConsolidation, recomputePresetCards]);

  const handleStopAssignment = () => {
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.postcode_assignment_aborted, {
      timestamp: new Date().toISOString(),
    });
    assignmentAbortRef.current?.abort();
  };

  React.useEffect(() => {
    if (!isRunning) {
      return undefined;
    }
    const tick = () => {
      const elapsedMs = Date.now() - runStartMsRef.current;
      const bar = Math.min(100, Math.max(0, lastBarPercentRef.current));
      const pct = bar / 100;
      const MIN_PCT = 0.03;
      const MIN_ELAPSED_MS = 1200;
      let etaRemainingMs: number | null = null;
      if (pct >= MIN_PCT && elapsedMs >= MIN_ELAPSED_MS && pct < 0.995) {
        etaRemainingMs = Math.max(0, Math.round(elapsedMs / pct - elapsedMs));
      }
      setRunTiming({ elapsedMs, etaRemainingMs });
    };
    tick();
    const id = window.setInterval(tick, 500);
    return () => window.clearInterval(id);
  }, [isRunning]);

  const runAssignment = async () => {
    if (!postcodeFile) {
      setError(t('errors.assignment.uploadFirst'));
      return;
    }
    setError(null);
    setInfoMessage(null);
    setIsRunning(true);
    setAssignmentProgress(null);
    setRunTiming({ elapsedMs: 0, etaRemainingMs: null });
    assignmentAbortRef.current?.abort();
    const abortController = new AbortController();
    assignmentAbortRef.current = abortController;
    const { signal } = abortController;
    runStartMsRef.current = Date.now();
    lastBarPercentRef.current = 0;
    assignmentMapTrailRef.current = [];
    rideIdCounterRef.current = 0;
    userIdCounterRef.current = 0;
    try {
      const radiusUsed = clampOsmAssignmentSearchRadius(searchRadiusMeters);
      const osmStopCache = osmStopCacheRef.current;
      const uploadedHubs: HubRow[] = [];

      const postcodeBuffer = await postcodeFile.arrayBuffer();
      const postcodeWorkbook = XLSX.read(postcodeBuffer, { type: 'array' });
      const postcodeSheetsBase = postcodeWorkbook.SheetNames.map((name) => {
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(postcodeWorkbook.Sheets[name]);
        const userPick = postcodeColumnSelectionBySheet[name];
        const postcodeColumn = effectivePostcodeColumnForSheet(rows, userPick);
        return { name, rows, postcodeColumn };
      });
      const postcodeSheets = resolvePostcodeSheetRunRows(
        postcodeSheetsBase,
        postcodeRowCoordinatesEnabledBySheet,
        postcodeOriginLayoutBySheet,
        postcodeOriginLatColumnSelectionBySheet,
        postcodeOriginLonColumnSelectionBySheet,
        postcodeOriginCombinedColumnSelectionBySheet
      );

      const simulationConfig: SimulationOutputConfig = {
        enabled: simulationOutputEnabled,
        direction: postcodeDirection,
        otherEndpointMode,
        manualOther: manualOtherEndpoint,
        manualOtherAddress: otherEndpointManualAddress.trim(),
        otherLayout: otherEndpointLayout,
        otherLatSelection: otherEndpointLatSelection,
        otherLonSelection: otherEndpointLonSelection,
        otherCombinedSelection: otherEndpointCombinedSelection,
        otherAddressSelection: otherEndpointAddressSelection,
        requestTimeMode,
        manualRequestTimeIso,
        requestTimeSelection,
        passengersMode: 'workbook' as SimulationPassengersMode,
        manualPassengersCount: DEFAULT_PASSENGER_COUNT,
        manualPassengersType: passengersManualType,
        passengersCountSelection,
        passengersTypeSelection,
        rideIdSelection,
        userIdSelection,
      };
      if (simulationConfig.enabled) {
        if (simulationConfig.otherEndpointMode === 'manual' && !simulationConfig.manualOther) {
          throw new Error(t('errors.assignment.simulationManualMissing'));
        }
      }
      const simulationRunPlan = resolveSimulationOutputPlan(simulationConfig, postcodeSheets);
      simulationRunPlanRef.current = simulationRunPlan;
      simulationOriginalRowsRef.current = postcodeSheets.map((sheet) => ({
        sheetName: sheet.name,
        rows: sheet.rows,
      }));

      const totalRows = postcodeSheets.reduce((sum, sheet) => sum + sheet.rows.length, 0);
      if (totalRows === 0) {
        throw new Error(t('errors.assignment.noRowsToProcess'));
      }
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.postcode_assignment_started, {
        postcode_rows: totalRows,
        sheet_count: postcodeSheets.length,
        has_hub_file: Boolean(hubFile),
        osm_search_radius_m: radiusUsed,
        use_osrm_walking_for_consolidation: useOsrmWalkingForConsolidation,
        simulation_output_enabled: simulationRunPlan.enabled,
        ...(simulationRunPlan.enabled
          ? {
              simulation_direction: simulationRunPlan.direction,
              simulation_other_endpoint_mode: otherEndpointMode,
            }
          : {}),
        timestamp: new Date().toISOString(),
      });
      for (const sheet of postcodeSheets) {
        if (sheet.rows.length > 0 && !sheet.postcodeColumn) {
          throw new Error(t('errors.assignment.couldNotDetermineColumn', { sheet: sheet.name }));
        }
      }

      if (hubFile) {
        const hubBuffer = await hubFile.arrayBuffer();
        const hubWorkbook = XLSX.read(hubBuffer, { type: 'array' });
        const headMeta = hubHeaderKeysFromFirstDataSheet(hubWorkbook);
        if (!headMeta || headMeta.keys.length === 0) {
          throw new Error(t('errors.assignment.hubHeadersFailed'));
        }
        const columnKeys = headMeta.keys;
        const detectedNameCol = detectHubNameColumn(columnKeys);
        const nameKey = effectiveHubNameColumnKey(columnKeys, hubNameColumnSelection, detectedNameCol);

        if (hubCoordLayout === 'combined') {
          const sheet = hubWorkbook.Sheets[headMeta.sheetName];
          const previewRows = sheet ? XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet) : [];
          const inferredCombined = inferHubCombinedLatLonColumn(previewRows);
          const headerCombinedGuess = detectHubCombinedColumnFromHeaders(columnKeys);
          const detectedCombinedCol = inferredCombined ?? headerCombinedGuess;
          const combinedKey = effectiveHubDataColumnKey(
            columnKeys,
            hubCombinedColumnSelection,
            AUTO_HUB_COMBINED_COLUMN,
            detectedCombinedCol
          );
          if (!combinedKey) {
            throw new Error(t('errors.assignment.hubCombinedRequired', { sheet: headMeta.sheetName, columns: t('errors.assignment.columnPlural', { count: columnKeys.length }) }));
          }
          uploadedHubs.push(
            ...collectHubRowsFromWorkbookCombined(hubWorkbook, { combinedKey, nameKey })
          );
        } else {
          const detectedLatCol = detectHubLatColumn(columnKeys);
          const detectedLonCol = detectHubLonColumn(columnKeys);
          const latKey = effectiveHubDataColumnKey(
            columnKeys,
            hubLatColumnSelection,
            AUTO_HUB_LAT_COLUMN,
            detectedLatCol
          );
          const lonKey = effectiveHubDataColumnKey(
            columnKeys,
            hubLonColumnSelection,
            AUTO_HUB_LON_COLUMN,
            detectedLonCol
          );
          if (!latKey || !lonKey) {
            throw new Error(t('errors.assignment.hubLatLonRequired', { sheet: headMeta.sheetName, columns: t('errors.assignment.columnPlural', { count: columnKeys.length }) }));
          }
          if (latKey === lonKey) {
            throw new Error(t('errors.assignment.hubLatLonSameColumn'));
          }
          uploadedHubs.push(
            ...collectHubRowsFromWorkbookMapped(hubWorkbook, { latKey, lonKey, nameKey })
          );
        }
        if (uploadedHubs.length === 0) {
          throw new Error(t('errors.assignment.hubNoValidRows'));
        }
      }

      const updateBarPercentRef = (next: AssignmentProgress) => {
        lastBarPercentRef.current = Math.min(
          100,
          (next.barPercent ?? 0) -
            (next.total > 0 ? (next.current / next.total) * 100 : 0)
        );
      };

      const commitProgress = (next: AssignmentProgress) => {
        updateBarPercentRef(next);
        setAssignmentProgress(next);
      };

      const progressStride = totalRows > 800 ? 12 : totalRows > 200 ? 5 : 1;

      const geocodeCache = new Map<string, { lat: number; lon: number } | null>();
      const uniqueOsmCenters = new Map<string, { lat: number; lon: number }>();

      const postcodesToGeocode = collectOrderedPostcodesNeedingGeocodeApi(postcodeSheets);
      const uniqueTotal = postcodesToGeocode.length;
      const geocodeBatchCount = uniqueTotal === 0 ? 0 : Math.ceil(uniqueTotal / POSTCODES_IO_BULK_MAX);

      if (uniqueTotal === 0) {
        commitProgress({
          current: 0,
          total: totalRows,
          sheetName: postcodeSheets[0]?.name ?? '',
          rowInSheet: 0,
          sheetRows: totalRows,
          detail:
            'Geocoding: skipped - no postcodes sent to postcodes.io (workbook latitude/longitude covers every row that needs a point, or there are no codes to look up)',
          barPercent: BAR_GEO_MAX,
        });
      }

      for (let offset = 0; offset < postcodesToGeocode.length; offset += POSTCODES_IO_BULK_MAX) {
        throwIfAborted(signal);
        const chunk = postcodesToGeocode.slice(offset, offset + POSTCODES_IO_BULK_MAX);
        const batchIndex = Math.floor(offset / POSTCODES_IO_BULK_MAX) + 1;
        const processedUniques = Math.min(offset + chunk.length, uniqueTotal);
        const rowMappedCurrent =
          uniqueTotal === 0 ? 0 : Math.min(totalRows, Math.round((processedUniques / uniqueTotal) * totalRows));
        commitProgress({
          current: rowMappedCurrent,
          total: totalRows,
          sheetName: postcodeSheets[0]?.name ?? '',
          rowInSheet: processedUniques,
          sheetRows: uniqueTotal || totalRows,
          detail: `Geocoding: postcodes.io batch ${batchIndex}/${Math.max(1, geocodeBatchCount)} (${chunk.length} distinct codes)...`,
          barPercent: uniqueTotal === 0 ? 0 : (processedUniques / uniqueTotal) * BAR_GEO_MAX,
        });
        try {
          const batchMap = await geocodeUkPostcodesBulk(chunk, signal);
          batchMap.forEach((value, key) => {
            geocodeCache.set(key, value);
          });
        } catch {
          for (const pc of chunk) {
            throwIfAborted(signal);
            if (!geocodeCache.has(pc)) {
              geocodeCache.set(pc, await geocodeUkPostcode(pc, signal));
            }
          }
        }
        if (offset + POSTCODES_IO_BULK_MAX < postcodesToGeocode.length) {
          await sleepCancellable(POSTCODES_IO_BATCH_SPACING_MS, signal);
        }
      }

      let passGeocode = 0;
      for (const sheet of postcodeSheets) {
        const { name: sheetName, rows, postcodeColumn: sheetPostcodeColumn } = sheet;
        for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
          throwIfAborted(signal);
          const row = rows[rowIndex];
          passGeocode += 1;
          const postcodeColumn = sheetPostcodeColumn;
          const postcode = postcodeColumn ? normalizeText(row[postcodeColumn]) : '';
          const detail = buildRowProgressLabel(row, postcodeColumn, postcode);
          const snapshot: AssignmentProgress = {
            current: passGeocode,
            total: totalRows,
            sheetName,
            rowInSheet: rowIndex + 1,
            sheetRows: rows.length,
            detail,
            barPercent: BAR_ROWSCAN_END,
          };
          if (passGeocode === 1 || passGeocode === totalRows || passGeocode % progressStride === 0) {
            commitProgress(snapshot);
          } else {
            updateBarPercentRef(snapshot);
          }

          if (!postcodeColumn || (!postcode && !rowHasWorkbookPostcodeOrigin(row, sheet))) {
            continue;
          }
          const point = pickPostcodeRowOriginCoordinates(row, postcode, sheet, geocodeCache);
          if (point) {
            const cacheKey = getOsmStopCacheKey(point.lat, point.lon, radiusUsed);
            uniqueOsmCenters.set(cacheKey, { lat: point.lat, lon: point.lon });
          }
        }
      }

      const centerList = Array.from(uniqueOsmCenters.entries()).map(([cacheKey, p]) => ({
        cacheKey,
        lat: p.lat,
        lon: p.lon,
      }));
            const osmSpan = BAR_OSM_MAX - BAR_OSM_START;

      if (uploadedHubs.length > 0) {
        commitProgress({
          current: totalRows,
          total: totalRows,
          sheetName: postcodeSheets[0]?.name ?? '',
          rowInSheet: totalRows,
          sheetRows: totalRows,
          detail: `Hub mode: using ${uploadedHubs.length} uploaded hubs`,
          barPercent: BAR_OSM_MAX,
        });
      } else if (centerList.length === 0) {
        commitProgress({
          current: totalRows,
          total: totalRows,
          sheetName: postcodeSheets[0]?.name ?? '',
          rowInSheet: totalRows,
          sheetRows: totalRows,
          detail:
            'OSM: no search areas (no row coordinates - check postcodes, postcodes.io, or postcode lat/lon / combined columns)',
          barPercent: BAR_OSM_MAX,
        });
      }

      if (centerList.length > 0 && uploadedHubs.length === 0) {
        throwIfAborted(signal);
        commitProgress({
          current: totalRows,
          total: totalRows,
          sheetName: postcodeSheets[0]?.name ?? '',
          rowInSheet: totalRows,
          sheetRows: totalRows,
          detail: `OSM: bbox lookup (${centerList.length} unique locations)…`,
          barPercent: BAR_OSM_START + osmSpan * 0.5,
        });
        const bboxMap = await fetchNearbyOsmTransitStopsByBoundingBox(
          centerList.map((c) => ({ lat: c.lat, lon: c.lon, cacheKey: c.cacheKey })),
          radiusUsed,
          { signal }
        );
        bboxMap.forEach((candidates, key) => {
          osmStopCache.set(
            key,
            candidates.map((candidate) => ({
              name: candidate.name,
              lat: candidate.lat,
              lon: candidate.lon,
              osmId: candidate.osmId,
              kind: candidate.kind,
            }))
          );
        });
        commitProgress({
          current: totalRows,
          total: totalRows,
          sheetName: postcodeSheets[0]?.name ?? '',
          rowInSheet: totalRows,
          sheetRows: totalRows,
          detail: `OSM: bbox lookup complete (${bboxMap.size}/${centerList.length} locations cached)`,
          barPercent: BAR_OSM_MAX,
        });
      }

      const results: ResultSheet[] = [];
      const consolidationInputs: Array<{
        sheetIndex: number;
        rowIndex: number;
        originLat: number;
        originLon: number;
        targetName: string;
        targetLat: number;
        targetLon: number;
        targetType: string;
      }> = [];
      const mapEntries: Array<{ originLat: number; originLon: number; targetLat: number; targetLon: number }> = [];
      let passAssign = 0;
      const assignSpan = BAR_ASSIGN_MAX - BAR_ASSIGN_START;

      for (let sheetIndex = 0; sheetIndex < postcodeSheets.length; sheetIndex += 1) {
        const sheet = postcodeSheets[sheetIndex]!;
        const { name: sheetName, rows, postcodeColumn: sheetPostcodeColumn } = sheet;
        const enrichedRows: RowWithNearest[] = [];
        for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
          throwIfAborted(signal);
          const row = rows[rowIndex];
          passAssign += 1;
          const postcodeColumn = sheetPostcodeColumn;
          const postcode = postcodeColumn ? normalizeText(row[postcodeColumn]) : '';
          const detail = buildRowProgressLabel(row, postcodeColumn, postcode);
          const snapshot: AssignmentProgress = {
            current: passAssign,
            total: totalRows,
            sheetName,
            rowInSheet: rowIndex + 1,
            sheetRows: rows.length,
            detail,
            barPercent: BAR_ASSIGN_START + (passAssign / totalRows) * assignSpan,
          };
          if (passAssign === 1 || passAssign === totalRows || passAssign % progressStride === 0) {
            commitProgress(snapshot);
          } else {
            updateBarPercentRef(snapshot);
          }

          if (!postcodeColumn) {
            enrichedRows.push({ ...toRowWithNearest(row), postcode: '', postcode_lat: '', postcode_lon: '' });
            continue;
          }
          const hasWorkbookOrigin = rowHasWorkbookPostcodeOrigin(row, sheet);
          if (!postcode && !hasWorkbookOrigin) {
            enrichedRows.push({ ...toRowWithNearest(row), postcode: '', postcode_lat: '', postcode_lon: '' });
            continue;
          }
          const point = pickPostcodeRowOriginCoordinates(row, postcode, sheet, geocodeCache);
          if (!point) {
            enrichedRows.push({
              ...toRowWithNearest(row),
              postcode: postcode || '',
              postcode_lat: '',
              postcode_lon: '',
              suggested_stop_name: '',
              suggested_stop_lat: '',
              suggested_stop_lon: '',
              suggested_stop_osm_id: '',
              suggested_target_type: '',
              distance_crow_flies_km: '',
              consolidated_stop_name: '',
              consolidated_stop_lat: '',
              consolidated_stop_lon: '',
              consolidated_target_type: '',
              consolidated_stop_distance_crow_flies_km: '',
              walking_distance_to_consolidated_km: '',
              walking_time_to_consolidated_min: '',
            });
            continue;
          }

          const cacheKey = getOsmStopCacheKey(point.lat, point.lon, radiusUsed);
          const stops = osmStopCache.get(cacheKey) ?? [];
          const nearestStop = uploadedHubs.length > 0 ? null : findNearestStop(point.lat, point.lon, stops);
          const nearestHub = uploadedHubs.length > 0 ? findNearestHub(point.lat, point.lon, uploadedHubs) : null;
          const targetName = nearestHub?.name ?? nearestStop?.name ?? null;
          const targetLat = nearestHub?.lat ?? nearestStop?.lat ?? null;
          const targetLon = nearestHub?.lon ?? nearestStop?.lon ?? null;
          assignmentMapTrailRef.current.push({
            postcode: postcode || '(coordinates only)',
            originLat: point.lat,
            originLon: point.lon,
            stopLat: targetLat,
            stopLon: targetLon,
            stopName: targetName,
            targetKind:
              targetLat === null || targetLon === null
                ? null
                : uploadedHubs.length > 0
                  ? 'hub'
                  : 'osm',
            walkingDistanceToConsolidatedKm: null,
            walkingTimeToConsolidatedMin: null,
          });
          if (assignmentMapTrailRef.current.length > ASSIGNMENT_MAP_TRAIL_MAX) {
            assignmentMapTrailRef.current = assignmentMapTrailRef.current.slice(-ASSIGNMENT_MAP_TRAIL_MAX);
          }
          const distance =
            targetLat !== null && targetLon !== null
              ? haversineDistanceKm(point.lat, point.lon, targetLat, targetLon)
              : null;
          enrichedRows.push({
            ...toRowWithNearest(row),
            postcode: postcode || '',
            postcode_lat: Number(point.lat.toFixed(7)),
            postcode_lon: Number(point.lon.toFixed(7)),
            suggested_stop_name: targetName ?? '',
            suggested_stop_lat: targetLat ?? '',
            suggested_stop_lon: targetLon ?? '',
            suggested_stop_osm_id: nearestStop?.osmId ?? '',
            suggested_target_type: uploadedHubs.length > 0 ? 'hub' : nearestStop?.kind ?? '',
            distance_crow_flies_km: distance === null ? '' : Number(distance.toFixed(3)),
            consolidated_stop_name: '',
            consolidated_stop_lat: '',
            consolidated_stop_lon: '',
            consolidated_target_type: '',
            consolidated_stop_distance_crow_flies_km: '',
            walking_distance_to_consolidated_km: '',
            walking_time_to_consolidated_min: '',
          });
          if (targetLat !== null && targetLon !== null && targetName) {
            consolidationInputs.push({
              sheetIndex,
              rowIndex: enrichedRows.length - 1,
              originLat: point.lat,
              originLon: point.lon,
              targetName,
              targetLat,
              targetLon,
              targetType: uploadedHubs.length > 0 ? 'hub' : nearestStop?.kind ?? 'osm_stop',
            });
            mapEntries.push({
              originLat: point.lat,
              originLon: point.lon,
              targetLat,
              targetLon,
            });
          }
        }
        results.push({ name: sheetName, rows: enrichedRows });
      }

      // First simulation-output pass: uses postcode_* coords (consolidation has not run yet). This
      // state seeds baseResultsRef so live re-applies preserve simulation columns for rows that
      // weren't consolidatable; per-input rows get a refined write inside the consolidation loop.
      if (simulationRunPlan.enabled) {
        for (let sIdx = 0; sIdx < results.length; sIdx += 1) {
          const sheet = results[sIdx]!;
          const sheetPlan = simulationRunPlan.perSheet[sheet.name];
          if (!sheetPlan) continue;
          const originalRows = postcodeSheets[sIdx]?.rows ?? [];
          for (let rIdx = 0; rIdx < sheet.rows.length; rIdx += 1) {
            writeSimulationColumnsForRow(sheet.rows[rIdx]!, originalRows[rIdx] ?? {}, sheetPlan, simulationRunPlan);
          }
        }
      }

      if (consolidationInputs.length > 0) {
        commitProgress({
          current: totalRows,
          total: totalRows,
          sheetName: postcodeSheets[0]?.name ?? '',
          rowInSheet: totalRows,
          sheetRows: totalRows,
          detail: `Consolidating pickup points (walking radius ${clampWalkingRadiusMeters(walkingRadiusMeters)}m)...`,
          barPercent: BAR_ROAD_START,
        });
        const matrixInputs: ConsolidationRuntimeInput[] = consolidationInputs.map((input) => ({
          originLat: input.originLat,
          originLon: input.originLon,
          targetName: input.targetName,
          targetLat: input.targetLat,
          targetLon: input.targetLon,
          targetType: input.targetType,
          sheetIndex: input.sheetIndex,
          rowIndex: input.rowIndex,
        }));
        const maxCandidatesPerRow = Math.min(10, Math.max(6, Math.ceil(1200 / Math.max(1, matrixInputs.length))));
        const maxPairs = 3000;
        commitProgress({
          current: totalRows,
          total: totalRows,
          sheetName: postcodeSheets[0]?.name ?? '',
          rowInSheet: totalRows,
          sheetRows: totalRows,
          detail: `Consolidation matrix: building candidate pairs (max ${maxCandidatesPerRow}/row, cap ${maxPairs} total)...`,
          barPercent: BAR_ROAD_START,
        });
        const matrixMaxMeters = getConsolidationMatrixMaxMeters();
        const matrix = await buildConsolidationMatrix(matrixInputs, matrixMaxMeters, signal, {
          maxCandidatesPerRow,
          maxPairs,
          skipOsrmWalking: !useOsrmWalkingForConsolidation,
          onProgress: (done, total) => {
            const frac = total > 0 ? done / total : 1;
            commitProgress({
              current: totalRows,
              total: totalRows,
              sheetName: postcodeSheets[0]?.name ?? '',
              rowInSheet: totalRows,
              sheetRows: totalRows,
              detail: `Consolidation matrix: walking routes ${done}/${total} pair checks...`,
              barPercent: BAR_ROAD_START + frac * 8,
            });
          },
        });
        commitProgress({
          current: totalRows,
          total: totalRows,
          sheetName: postcodeSheets[0]?.name ?? '',
          rowInSheet: totalRows,
          sheetRows: totalRows,
          detail:
            `Consolidation matrix ready: ${matrixInputs.length} rows, ${matrix.candidates.length} unique targets, ` +
            `${matrix.candidatePairsBeforeCap} candidate pairs, routing ${matrix.routedPairs} after cap.`,
          barPercent: BAR_ROAD_START + 8,
        });
        const diagnostics = buildConsolidationMatrixDiagnostics(matrix, [5, 10, 15, 20, 30]);
        // Diagnostic telemetry for time-sensitivity debugging; intentionally console-only for now.
        console.info('[PostcodeAssignment] consolidation matrix diagnostics', diagnostics);
        consolidationMatrixRef.current = matrix;
        consolidationInputsRef.current = matrixInputs;
        consolidationMapEntriesRef.current = mapEntries;
        baseResultsRef.current = results.map((sheet) => ({
          name: sheet.name,
          rows: sheet.rows.map((row) => ({ ...row })),
        }));
        const consolidated = applyConsolidationConstraints(matrix, {
          distanceEnabled: walkingRadiusEnabled,
          maxWalkingMeters: clampWalkingRadiusMeters(walkingRadiusMeters),
          timeEnabled: walkingTimeEnabled,
          maxWalkingMinutes: clampWalkingTimeMinutes(walkingTimeMinutes),
        });
        for (let i = 0; i < consolidationInputs.length; i += 1) {
          const input = consolidationInputs[i]!;
          const row = consolidated[i]!;
          const resultRow = results[input.sheetIndex]?.rows[input.rowIndex];
          if (resultRow) {
            resultRow.consolidated_stop_name = row.consolidatedStopName;
            resultRow.consolidated_stop_lat = row.consolidatedStopLat;
            resultRow.consolidated_stop_lon = row.consolidatedStopLon;
            resultRow.consolidated_target_type = row.consolidatedTargetType;
            resultRow.consolidated_stop_distance_crow_flies_km = row.consolidatedStopDistanceCrowFliesKm;
            resultRow.walking_distance_to_consolidated_km = row.walkingDistanceToConsolidatedKm;
            resultRow.walking_time_to_consolidated_min = row.walkingTimeToConsolidatedMin;
            if (simulationRunPlan.enabled) {
              const sheetName = results[input.sheetIndex]?.name;
              const sheetPlan = sheetName ? simulationRunPlan.perSheet[sheetName] : undefined;
              const originalRow = postcodeSheets[input.sheetIndex]?.rows[input.rowIndex];
              if (sheetPlan && originalRow) {
                writeSimulationColumnsForRow(resultRow, originalRow, sheetPlan, simulationRunPlan);
              }
            }
          }
          const trail = assignmentMapTrailRef.current.find(
            (entry) =>
              entry.originLat === input.originLat &&
              entry.originLon === input.originLon &&
              entry.stopLat === input.targetLat &&
              entry.stopLon === input.targetLon
          );
          if (trail) {
            trail.consolidatedStopLat = row.consolidatedStopLat;
            trail.consolidatedStopLon = row.consolidatedStopLon;
            trail.consolidatedStopName = row.consolidatedStopName;
        trail.walkingDistanceToConsolidatedKm = row.walkingDistanceToConsolidatedKm;
        trail.walkingTimeToConsolidatedMin = row.walkingTimeToConsolidatedMin;
          }
        }
        setMapRevision((r) => r + 1);
        const summary = summarizeConsolidation(consolidated, matrixInputs);
        setConsolidationSummary({
          originalTargets: summary.originalUniqueTargets,
          consolidatedTargets: summary.consolidatedUniqueTargets,
          reductionCount: Math.max(0, summary.originalUniqueTargets - summary.consolidatedUniqueTargets),
          consolidationRatePct:
            summary.originalUniqueTargets > 0
              ? (Math.max(0, summary.originalUniqueTargets - summary.consolidatedUniqueTargets) /
                  summary.originalUniqueTargets) *
                100
              : 0,
          avgWalkingMinutes: summary.averageWalkingMinutes,
        });
        recomputePresetCards();
      }

      if (simulationRunPlan.enabled) {
        aggregateConsolidatedPassengers(results);
      }

      lastBarPercentRef.current = 100;

      const clonedResults = results.map((sheet) => ({
        name: sheet.name,
        rows: sheet.rows.map((row) => ({ ...row })),
      }));
      const finalResults = withSimulationTripsSheet(clonedResults, simulationRunPlan.enabled);
      setSheetResults(finalResults);
      setActiveSheet(finalResults[0]?.name ?? '');

      const finalDurationMs = Date.now() - runStartMsRef.current;
      let geocodedRows = 0;
      let stopsAssigned = 0;
      let hubsAssigned = 0;
      let walkingDistanceRows = 0;
      let simulationOutputRows = 0;
      for (const sheet of results) {
        for (const row of sheet.rows) {
          const r = row as Record<string, unknown>;
          if (typeof r.lat === 'number' && typeof r.lon === 'number') {
            geocodedRows += 1;
          }
          if (typeof r.stop_id === 'string' && r.stop_id) {
            stopsAssigned += 1;
          }
          if (typeof r.hub_name === 'string' && r.hub_name) {
            hubsAssigned += 1;
          }
          if (
            typeof r.walking_distance_to_consolidated_km === 'number' &&
            Number.isFinite(r.walking_distance_to_consolidated_km)
          ) {
            walkingDistanceRows += 1;
          }
          if (
            simulationRunPlan.enabled &&
            typeof r.from_latitude === 'number' &&
            typeof r.from_longitude === 'number' &&
            typeof r.to_latitude === 'number' &&
            typeof r.to_longitude === 'number'
          ) {
            simulationOutputRows += 1;
          }
        }
      }
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.postcode_assignment_completed, {
        postcode_rows: totalRows,
        geocoded_rows: geocodedRows,
        stops_assigned: stopsAssigned,
        hubs_assigned: hubsAssigned,
        walking_distance_rows: walkingDistanceRows,
        duration_ms: finalDurationMs,
        ...(simulationRunPlan.enabled ? { simulation_output_rows: simulationOutputRows } : {}),
        timestamp: new Date().toISOString(),
      });
    } catch (runError) {
      if (
        signal.aborted ||
        (runError instanceof DOMException && runError.name === 'AbortError')
      ) {
        setInfoMessage(t('errors.assignment.stoppedBeforeCompletion'));
      } else {
        const message = runError instanceof Error ? runError.message : t('errors.assignment.unknownProcessing');
        setError(message);
        const failureDurationMs = Date.now() - runStartMsRef.current;
        const { errorCategory, errorCode, retryable, failedStage } = categorizeAssignmentError(runError);
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.postcode_assignment_failed, {
          feature_area: 'postcode_assignment',
          failed_stage: failedStage,
          error_category: errorCategory,
          error_code: errorCode,
          retryable,
          duration_ms: failureDurationMs,
          timestamp: new Date().toISOString(),
        });
      }
    } finally {
      assignmentAbortRef.current = null;
      setIsRunning(false);
      setAssignmentProgress(null);
      setRunTiming(null);
    }
  };

  const downloadResults = () => {
    if (!sheetResults.length) return;
    const workbook = buildWorkbook(sheetResults);
    const totalRows = sheetResults.reduce((sum, sheet) => sum + sheet.rows.length, 0);
    const includesWalkingDistances = sheetResults.some((sheet) =>
      sheet.rows.some((row) => {
        const value = (row as Record<string, unknown>).walking_distance_to_consolidated_km;
        return typeof value === 'number' && Number.isFinite(value);
      })
    );
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.postcode_assignment_workbook_downloaded, {
      sheet_count: sheetResults.length,
      total_rows: totalRows,
      includes_walking_distances: includesWalkingDistances,
      output_kind: 'full',
      timestamp: new Date().toISOString(),
    });
    XLSX.writeFile(workbook, 'postcode_stop_assignments.xlsx');
  };

  /** Trips-only download: one sheet, one row per consolidated stop, passenger counts summed. */
  const simulationTripsSheet = React.useMemo(
    () => sheetResults.find((sheet) => sheet.name === SIMULATION_TRIPS_SHEET_NAME) ?? null,
    [sheetResults]
  );

  const downloadSimulationTripsResults = () => {
    if (!simulationTripsSheet) return;
    const workbook = buildWorkbook([simulationTripsSheet]);
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.postcode_assignment_workbook_downloaded, {
      sheet_count: 1,
      total_rows: simulationTripsSheet.rows.length,
      includes_walking_distances: false,
      output_kind: 'simulation_trips',
      timestamp: new Date().toISOString(),
    });
    XLSX.writeFile(workbook, 'postcode_simulation_trips.xlsx');
  };

  /**
   * Converts one consolidated-stop row of the simulation-trips sheet into one ride per passenger.
   *
   * Trip-shape rules (2026-05-28 refresh; see `tasks/postcode-improvements-2026-05-28.md`):
   *   1. Shift the source `request_time` back 1 hour (wall-time).
   *   2. Override (1): if the resulting wall-time hour falls inside a known shift band, switch the
   *      ride to `request_type = "arrival_fixed"` and set `request_time = shift_start - 20 minutes`.
   *      Bands are sourced from `services/postcodeAssignment/shiftBands.ts` so they stay aligned
   *      with `OpsVehicleMappingPanel`'s `ELC_PRESET_DEFAULTS`.
   *   3. Explode `passengers_count > 1` consolidations into one ride per passenger, each keeping a
   *      distinct `external_user_reference_id` (preferring the upstream per-source `user_id` /
   *      `ride_id` columns when available, else synthesising deterministic ids from the row's
   *      consolidated trip id). All exploded rides share identical `from_*` / `to_*` / `request_*`
   *      so the simulator still treats them as a multi-pickup at one stop.
   *   4. Propagate the consolidated stop's `external_stop_reference_id` (postcode) onto every
   *      exploded ride.
   *
   * Returns `[]` when coordinates are missing or no passengers contribute.
   */
  const tripsSheetRowToRideSchema = React.useCallback((row: RowWithNearest, rowIndex: number): RideSchema[] => {
    const fromLat = toNumber(row.from_latitude);
    const fromLon = toNumber(row.from_longitude);
    const toLat = toNumber(row.to_latitude);
    const toLon = toNumber(row.to_longitude);
    if (fromLat === null || fromLon === null || toLat === null || toLon === null) return [];
    const passengerCount = Math.max(1, coercePassengersCountCell(row.passengers_count) ?? DEFAULT_PASSENGER_COUNT);
    const passengerType =
      (typeof row.passengers_type === 'string' && row.passengers_type.trim()) || DEFAULT_PASSENGER_TYPE;
    const rawRequestTime = typeof row.request_time === 'string' ? row.request_time : '';
    // Map import overrides the date portion with the app's DEFAULT_SIMULATION_DATE so simulator runs
    // are date-anchored to the standard demo day - wall clock (HH:mm) is preserved.
    let baseRequestTime = rawRequestTime;
    if (rawRequestTime) {
      try {
        const timeOnly = extractTimeFromDateTime(rawRequestTime);
        if (isValidTimeFormat(timeOnly)) {
          baseRequestTime = combineDateWithTime(DEFAULT_SIMULATION_DATE, timeOnly);
        }
      } catch {
        // Leave rawRequestTime in place if the source string isn't parseable.
      }
    }
    // Default request_type is `arrival_fixed` when the source row didn't
    // specify one — the request_time the user picked (or the band default,
    // e.g. DAY 06:40) is treated as the desired arrival pickup target. The
    // 20-min buffer before work-shift start is already baked into the band's
    // passengerArrivalTime, so we do not subtract again here.
    const hasExplicitRequestType =
      typeof row.request_type === 'string' && row.request_type.trim() !== '';
    const finalRequestType = hasExplicitRequestType ? (row.request_type as string) : 'arrival_fixed';
    const finalRequestTime = baseRequestTime;
    const fromAddress = typeof row.from_address === 'string' ? row.from_address : '';
    const toAddress = typeof row.to_address === 'string' ? row.to_address : '';
    const consolidatedTripId =
      typeof row.ride_id === 'string' && row.ride_id ? row.ride_id : `trip${rowIndex + 1}`;
    const consolidatedUserId =
      typeof row.user_id === 'string' && row.user_id ? row.user_id : `user${rowIndex + 1}`;
    const sourceRideIds =
      typeof row.source_ride_ids === 'string' && row.source_ride_ids
        ? row.source_ride_ids.split('|').filter((id) => id.length > 0)
        : [];
    const sourceUserIds =
      typeof row.source_user_ids === 'string' && row.source_user_ids
        ? row.source_user_ids.split('|').filter((id) => id.length > 0)
        : [];
    const sourceCounts =
      typeof row.source_passenger_counts === 'string' && row.source_passenger_counts
        ? row.source_passenger_counts
            .split('|')
            .map((s) => Number(s))
            .map((n) => (Number.isFinite(n) && n > 0 ? Math.round(n) : 1))
        : [];
    const sourceTypes =
      typeof row.source_passenger_types === 'string' && row.source_passenger_types
        ? row.source_passenger_types.split('|')
        : [];
    const externalStopReferenceId =
      typeof row.external_stop_reference_id === 'string' && row.external_stop_reference_id
        ? row.external_stop_reference_id
        : undefined;
    const mappedSlots = vehicleSlots.filter((slot) => slot.opsVehicleId);
    const mappedVehicleId =
      mappedSlots.length > 0 ? mappedSlots[rowIndex % mappedSlots.length]?.opsVehicleId : undefined;

    // Build the per-passenger expansion: walk each contributing source row and emit one ride per
    // passenger it represents. Falls back to the aggregate (passenger_count, passenger_type,
    // ride_id, user_id) when source-level metadata is missing — keeps behaviour for rides that
    // came through without consolidation tracing.
    type PassengerSlot = { rideId: string; userId: string; type: string; passengerIndex: number };
    const passengerSlots: PassengerSlot[] = [];
    if (sourceCounts.length > 0) {
      for (let sourceIndex = 0; sourceIndex < sourceCounts.length; sourceIndex += 1) {
        const sourceCount = Math.max(1, sourceCounts[sourceIndex] ?? 1);
        const baseRideId = sourceRideIds[sourceIndex] || `${consolidatedTripId}-s${sourceIndex + 1}`;
        const baseUserId = sourceUserIds[sourceIndex] || `${consolidatedUserId}-s${sourceIndex + 1}`;
        const sourceType = sourceTypes[sourceIndex] || passengerType;
        for (let paxIndex = 0; paxIndex < sourceCount; paxIndex += 1) {
          passengerSlots.push({
            rideId: sourceCount > 1 ? `${baseRideId}-p${paxIndex + 1}` : baseRideId,
            userId: sourceCount > 1 ? `${baseUserId}-p${paxIndex + 1}` : baseUserId,
            type: sourceType,
            passengerIndex: passengerSlots.length + 1,
          });
        }
      }
    }
    if (passengerSlots.length === 0) {
      for (let paxIndex = 0; paxIndex < passengerCount; paxIndex += 1) {
        passengerSlots.push({
          rideId: passengerCount > 1 ? `${consolidatedTripId}-p${paxIndex + 1}` : consolidatedTripId,
          userId: passengerCount > 1 ? `${consolidatedUserId}-p${paxIndex + 1}` : consolidatedUserId,
          type: passengerType,
          passengerIndex: paxIndex + 1,
        });
      }
    }

    return passengerSlots.map((slot) => ({
      from_latitude: fromLat,
      from_longitude: fromLon,
      to_latitude: toLat,
      to_longitude: toLon,
      from_address: fromAddress || undefined,
      to_address: toAddress || undefined,
      request_time: finalRequestTime || undefined,
      request_type: finalRequestType,
      // Per-passenger row: one passenger of `type` per ride. Multi-pickup
      // consolidation is preserved by identical from_*/to_*/request_*.
      passengers: [{ type: slot.type, number_of_passengers: 1 }],
      ride_id: slot.rideId,
      user_id: slot.userId,
      external_reference_id: slot.rideId,
      external_user_reference_id: slot.userId,
      external_stop_reference_id: externalStopReferenceId,
      vehicle_id: mappedVehicleId,
      ...(sourceRideIds.length > 0 ? { squish_source_ride_ids: sourceRideIds } : {}),
    } satisfies RideSchema));
  }, [vehicleSlots]);

  /**
   * Pushes the simulation-trips rollup into RidesContext and onto the map as a new layer. Returns
   * true on success so the caller can chain post-push behaviour (e.g. navigating to the map page).
   */
  const pushSimulationTripsToMap = React.useCallback((): boolean => {
    if (!simulationTripsSheet) {
      setError(t('errors.assignment.noTripsToPush'));
      return false;
    }
    if (!appContext) {
      setError(t('errors.assignment.mapContextUnavailable'));
      return false;
    }
    const rides: RideSchema[] = [];
    let skipped = 0;
    let consolidatedStops = 0;
    for (let index = 0; index < simulationTripsSheet.rows.length; index += 1) {
      const row = simulationTripsSheet.rows[index]!;
      const expanded = tripsSheetRowToRideSchema(row, index);
      if (expanded.length === 0) {
        skipped += 1;
        continue;
      }
      consolidatedStops += 1;
      rides.push(...expanded);
    }
    if (rides.length === 0) {
      setError(t('errors.assignment.couldNotBuildRides'));
      return false;
    }
    // Note: the consolidated stop name is carried to the Fixed Route import via
    // each ride's `from_address` / `to_address` (set in `buildSimulationTripsSheet`),
    // which HTTM persists and echoes back on `/activities`. No client-side
    // registry is needed — see `resolveStopName` in simulationResultsToFixedRoutes.
    const layerName = `Postcode trips - ${new Date().toLocaleString()}`;
    try {
      bulkAddRides(rides, 'merge');
      importUATRides(rides, appContext, layerName);
      const mappedSlots = vehicleSlots.filter((slot) => slot.opsVehicleId);
      if (mappedSlots.length > 0) {
        // Register the depot as a Point on the active map layer (de-duped by
        // ~11 m tolerance) so it shows in the Fleet drawer's edit-vehicle
        // dropdown and on the map. Returns the point id, which we then use
        // as `startLocation`/`endLocation` for every vehicle so the dropdown
        // can find it by id. Falls back to the depot label string when no
        // map layer is available (rare — keeps the simulator path working
        // via `*LocationCoordinates`).
        const ensureDepotPointId = (
          lat: number,
          lng: number,
          name: string,
        ): string => {
          const layers = appContext.global.mapLayers || [];
          const tolerance = 0.0001; // ~11 m
          for (const layer of layers) {
            const existing = (layer.shapes || []).find((shape) => {
              if (shape.type !== 'point') return false;
              const [pLat, pLng] = (shape as Point).coordinates ?? [0, 0];
              const dist = Math.sqrt((pLat - lat) ** 2 + (pLng - lng) ** 2);
              return dist < tolerance;
            }) as Point | undefined;
            if (existing) return existing.id;
          }
          const targetLayer = layers[0];
          if (!targetLayer) return name;
          const pointId = `postcode-depot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          const newPoint: Point = {
            id: pointId,
            type: 'point',
            name,
            description: 'Postcode Stop Assignment depot',
            colour: '#1976d2',
            icon: 'location',
            size: 24,
            isLocked: false,
            coordinates: [lat, lng],
            layerId: targetLayer.layerId,
            hidden: false,
          };
          appContext.global.setLayers((prev: Layer[]) =>
            prev.map((layer) =>
              layer.layerId === targetLayer.layerId
                ? { ...layer, shapes: [...layer.shapes, newPoint] }
                : layer,
            ),
          );
          return pointId;
        };

        const firstDepotSlot = mappedSlots.find(
          (s) => typeof s.depotLatitude === 'number' && typeof s.depotLongitude === 'number',
        );
        const depotPointId =
          firstDepotSlot &&
          firstDepotSlot.depotLatitude !== undefined &&
          firstDepotSlot.depotLongitude !== undefined
            ? ensureDepotPointId(
                firstDepotSlot.depotLatitude,
                firstDepotSlot.depotLongitude,
                firstDepotSlot.depotLabel || 'Depot',
              )
            : undefined;

        const fleetId = `postcode-ops-${Date.now()}`;
        // Per-band ordinal counter so each route is labelled "DAY 1", "DAY 2",
        // "LATE 1", "NIGHT 1" … in slot order. The band comes from the slot's
        // vehicle-shift start window (DAY 03:00 / LATE 11:00 / NIGHT 19:00).
        // This route label is carried separately from the vehicle name (the
        // rego), so the Fixed Route upload names the route by band while still
        // attributing the correct physical vehicle by id.
        const routeOrdinalByBand = new Map<string, number>();
        const fleetVehicles = mappedSlots.map((slot, index) => {
          // Depot is captured panel-wide and copied onto every slot. When set,
          // the simulation orchestrator reads `*LocationCoordinates` and emits
          // real shift start/end addresses; otherwise it falls back to 0,0.
          const depotCoords =
            typeof slot.depotLatitude === 'number' && typeof slot.depotLongitude === 'number'
              ? { lat: slot.depotLatitude, lng: slot.depotLongitude }
              : undefined;
          // Prefer the registered point id so the Fleet drawer's edit dialog
          // can resolve a Select option. Falls back to the human-readable
          // depot label or a generic "Depot" string.
          const startEndLocationId = depotPointId || slot.depotLabel || 'Depot';

          // Derive the band from the slot's shift start window, then assign the
          // next per-band ordinal → "DAY 1", "LATE 1", "NIGHT 1" …. Malformed
          // windows fall back to DAY (the most common case).
          const shiftBand = (() => {
            const startWindowStr = (slot.startWindow || '').trim();
            const hhmm = startWindowStr.match(/(\d{1,2}:\d{2})/)?.[1] ?? '03:00';
            try {
              return inferShiftBandFromMinuteOfDay(timeToMinutes(hhmm));
            } catch {
              return 'DAY' as const;
            }
          })();
          const routeOrdinal = (routeOrdinalByBand.get(shiftBand) ?? 0) + 1;
          routeOrdinalByBand.set(shiftBand, routeOrdinal);
          const routeName = `${shiftBand} ${routeOrdinal}`;

          return {
            id: slot.opsVehicleId || `postcode-ops-vehicle-${index + 1}`,
            name: slot.name || slot.opsVehicleLabel || `Ops Vehicle ${index + 1}`,
            // Route label for the Fixed Route export; vehicle `name` stays the
            // rego so the physical vehicle is still matched by id on upload.
            routeName,
            shiftBand,
            standardSeats: 25,
            wheelchairSeats: 0,
            startLocation: startEndLocationId,
            endLocation: startEndLocationId,
            startLocationCoordinates: depotCoords,
            endLocationCoordinates: depotCoords,
            operatingHours: [
              {
                // Fallback matches the DAY band default in shiftBands.ts —
                // fires only when slot.startWindow is blank (e.g. an empty
                // slot added via "Add slot"). Auto-load and ELC preset both
                // populate startWindow explicitly.
                start: slot.startWindow || '03:00',
                end: slot.endWindow || '11:00',
                breaks: [],
              },
            ],
            uatVehicleId: slot.opsVehicleId,
            uatMetadata: {
              originalId: slot.opsVehicleId,
              originalName: slot.opsVehicleLabel || slot.name,
              opsVehicleId: slot.opsVehicleId,
              opsVehicleLabel: slot.opsVehicleLabel || slot.name,
              importedAt: Date.now(),
              importedFrom: 'PostcodeAssignmentPushToMap',
            },
          };
        });
        appContext.global.setFleets((prev) => [
          ...prev,
          {
            id: fleetId,
            name: `Postcode Ops Fleet ${new Date().toLocaleTimeString()}`,
            vehicles: fleetVehicles,
            uatMetadata: {
              importedAt: Date.now(),
              importedFrom: 'PostcodeAssignmentPushToMap',
              sourceVehicleIds: mappedSlots.map((slot) => slot.opsVehicleId).filter(Boolean),
            },
          },
        ]);
      }
      const depotSource: 'hub' | 'manual' | 'none' = (() => {
        const firstDepotSlot = vehicleSlots.find(
          (s) => typeof s.depotLatitude === 'number' && typeof s.depotLongitude === 'number',
        );
        if (!firstDepotSlot) return 'none';
        const label = firstDepotSlot.depotLabel ?? '';
        return label.toLowerCase().startsWith('manual') ? 'manual' : 'hub';
      })();
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.postcode_assignment_trips_pushed_to_map, {
        // After the per-passenger explosion (2026-05-28), `trip_count` is one row per
        // passenger and matches the headcount headed into the simulator. The number of
        // consolidated pickup stops is captured separately as `consolidated_stop_count`.
        trip_count: rides.length,
        passenger_count: rides.length,
        consolidated_stop_count: consolidatedStops,
        vehicle_count: vehicleSlots.filter((s) => s.opsVehicleId).length,
        depot_source: depotSource,
        timestamp: new Date().toISOString(),
      });
      const note = skipped > 0 ? t('errors.assignment.tripsAddedSkipped', { count: skipped }) : '';
      setError(null);
      setInfoMessage(
        t('errors.assignment.tripsAdded', { count: rides.length, layer: layerName, note })
      );
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : t('errors.assignment.unknownPushError');
      setError(t('errors.assignment.addTripsToMapFailed', { message }));
      return false;
    }
  }, [appContext, bulkAddRides, simulationTripsSheet, tripsSheetRowToRideSchema, vehicleSlots]);

  const pushTripsAndOpenMap = React.useCallback(() => {
    if (pushSimulationTripsToMap()) {
      navigate('/');
    }
  }, [navigate, pushSimulationTripsToMap]);

  const selectedSheetRows = React.useMemo(() => {
    const rawRows = sheetResults.find((sheet) => sheet.name === activeSheet)?.rows;
    const rows = Array.isArray(rawRows) ? rawRows : [];
    if (!search.trim()) return rows;
    const term = search.toLowerCase();
    return rows.filter((row) => Object.values(row).some((value) => String(value ?? '').toLowerCase().includes(term)));
  }, [sheetResults, activeSheet, search]);

  const lastFilterTrackedRef = React.useRef<string>('');
  React.useEffect(() => {
    const trimmed = search.trim();
    if (!trimmed) {
      lastFilterTrackedRef.current = '';
      return;
    }
    const handle = window.setTimeout(() => {
      if (lastFilterTrackedRef.current === trimmed) {
        return;
      }
      lastFilterTrackedRef.current = trimmed;
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.postcode_assignment_result_filtered, {
        query_length: trimmed.length,
        matched_rows: selectedSheetRows.length,
        timestamp: new Date().toISOString(),
      });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [search, selectedSheetRows.length]);

  const columns = selectedSheetRows[0] ? Object.keys(selectedSheetRows[0]) : [];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom={false}>
          {t('postcodeAssignment.pageTitle')}
        </Typography>
        <Tooltip
          title={t('postcodeAssignment.tooltips.pageHelp')}
          placement="bottom-start"
          enterTouchDelay={0}
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 440,
                whiteSpace: 'pre-line',
                typography: 'body2',
                lineHeight: 1.55,
                textAlign: 'left',
                borderRadius: TOOLTIP_TIGHT_BOX_RADIUS,
              },
            },
          }}
        >
          <IconButton
            aria-label={t('postcodeAssignment.infoButtonAria')}
            size="small"
            edge="start"
            sx={{
              mt: -0.5,
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                bgcolor: (theme) => alpha(opsColors.brand.primary, 0.08),
              },
              '&:focus-visible': {
                outline: (theme) => `2px solid ${opsColors.brand.primary}`,
                outlineOffset: 2,
              },
            }}
          >
            <Icon name="circle-info" size={20} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          mb: 3,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          alignItems: 'start',
        }}
      >
        <Paper
          variant="outlined"
          sx={(theme) => ({
            p: 2,
            minWidth: 0,
            borderColor: opsColors.neutral.line,
            bgcolor: opsColors.neutral.panelAlt,
          })}
        >
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <SectionTitleWithTooltip title={t('postcodeAssignment.postcodeWorkbookSection')} tooltip={t('postcodeAssignment.tooltips.postcodeWorkbookSection')} />
            <WorkbookUploadCard
              title={t('postcodeAssignment.postcodeWorkbookCardTitle')}
              icon={<Icon size={20} d={ICON_FALLBACKS.MailOutlineOutlined} />}
              chooseFileAriaLabel={t('postcodeAssignment.postcodeWorkbookChooseFileAria')}
              dropRegionAriaLabel={t('postcodeAssignment.postcodeWorkbookDropAria')}
              fileInputRef={postcodeWorkbookInputRef}
              onChosenFileChange={setPostcodeFile}
              chosenFile={postcodeFile}
              onClearChosen={handleClearPostcodeFile}
            />
            <Box>
              {workbookPreviewLoading && (
                <Typography variant="body2" color="text.secondary">
                  {t('postcodeAssignment.readingWorkbook')}
                </Typography>
              )}
              {workbookPreviewError && (
                <Alert severity="warning" sx={{ mt: workbookPreviewLoading ? 1 : 0 }}>
                  {workbookPreviewError}
                </Alert>
              )}
              {workbookPreview && workbookPreview.length > 0 && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" component="h3">
                    {t('postcodeAssignment.postcodeColumn')}
                  </Typography>
                  {workbookPreview.map((sheet, sheetIndex) => {
                const selectId = `postcode-column-${sheet.name}`;
                const value = postcodeColumnSelectionBySheet[sheet.name] ?? AUTO_POSTCODE_COLUMN_SELECT;
                const autoLabel = sheet.detectedPostcodeColumn
                  ? t('postcodeAssignment.autoDetected', { column: sheet.detectedPostcodeColumn })
                  : t('postcodeAssignment.autoNoColumn');
                const latSelectId = `postcode-origin-lat-${sheet.name}`;
                const lonSelectId = `postcode-origin-lon-${sheet.name}`;
                const latVal =
                  postcodeOriginLatColumnSelectionBySheet[sheet.name] ?? NONE_POSTCODE_ORIGIN_COORD_COLUMN;
                const lonVal =
                  postcodeOriginLonColumnSelectionBySheet[sheet.name] ?? NONE_POSTCODE_ORIGIN_COORD_COLUMN;
                const latAutoLabel = sheet.detectedPostcodeLatColumn
                  ? t('postcodeAssignment.autoDetected', { column: sheet.detectedPostcodeLatColumn })
                  : t('postcodeAssignment.autoNoLatitude');
                const lonAutoLabel = sheet.detectedPostcodeLonColumn
                  ? t('postcodeAssignment.autoDetected', { column: sheet.detectedPostcodeLonColumn })
                  : t('postcodeAssignment.autoNoLongitude');
                const sheetHeading =
                  workbookPreview.length > 1 ? ` - ${sheet.name}` : '';
                const postcodeOriginLayout = postcodeOriginLayoutBySheet[sheet.name] ?? 'separate';
                const combinedSelectId = `postcode-origin-combined-${sheet.name}`;
                const combinedVal =
                  postcodeOriginCombinedColumnSelectionBySheet[sheet.name] ?? AUTO_POSTCODE_COMBINED_COLUMN;
                const combinedAutoLabel = sheet.detectedPostcodeCombinedColumn
                  ? t('postcodeAssignment.autoDetected', { column: sheet.detectedPostcodeCombinedColumn })
                  : t('postcodeAssignment.autoNoCombined');
                const originLayoutFieldId = `postcode-origin-layout-legend-${sheet.name}`;
                const sheetRowCoordsOn = postcodeRowCoordinatesEnabledBySheet[sheet.name] === true;

                const postcodeColumnControl = (
                  <FormControl fullWidth sx={sheetIndex === 0 ? { flex: '1 1 auto', minWidth: 0 } : undefined}>
                    <InputLabel id={`${selectId}-label`}>
                      {workbookPreview.length > 1 ? t('postcodeAssignment.postcodeColumnWithSheet', { sheet: sheet.name }) : t('postcodeAssignment.postcodeColumn')}
                    </InputLabel>
                    <Select
                      labelId={`${selectId}-label`}
                      id={selectId}
                      label={workbookPreview.length > 1 ? t('postcodeAssignment.postcodeColumnWithSheet', { sheet: sheet.name }) : t('postcodeAssignment.postcodeColumn')}
                      value={value}
                      disabled={sheet.columnKeys.length === 0}
                      onChange={(event) => {
                        const next = event.target.value;
                        setPostcodeColumnSelectionBySheet((prev) => ({ ...prev, [sheet.name]: next }));
                      }}
                    >
                      <MenuItem value={AUTO_POSTCODE_COLUMN_SELECT}>{autoLabel}</MenuItem>
                      {sheet.columnKeys.map((key) => (
                        <MenuItem key={key} value={key}>
                          {key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );

                return (
                  <Stack key={sheet.name} spacing={1.5} sx={{ width: '100%' }}>
                    {sheetIndex === 0 ? (
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        useFlexGap
                        sx={{
                          width: '100%',
                          alignItems: { xs: 'stretch', sm: 'flex-start' },
                          flexWrap: { sm: 'nowrap' },
                        }}
                      >
                        {postcodeColumnControl}
                        <Tooltip
                          title={
                            hubFile ? t('postcodeAssignment.tooltips.osmRadiusDisabledWithHub') : t('postcodeAssignment.tooltips.osmRadius')
                          }
                          placement="top"
                          enterTouchDelay={0}
                          describeChild
                          slotProps={osmSearchRadiusTooltipSlotProps}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              flex: '0 0 auto',
                              minWidth: { xs: '100%', sm: 200 },
                              maxWidth: { sm: 280 },
                              width: { xs: '100%', sm: 'auto' },
                            }}
                          >
                            <TextField
                              label={t('postcodeAssignment.osmSearchRadius')}
                              type="number"
                              value={searchRadiusMeters}
                              disabled={Boolean(hubFile)}
                              onChange={(event) => {
                                const raw = event.target.value;
                                const n = Number(raw);
                                setSearchRadiusMeters(raw === '' || Number.isNaN(n) ? 1000 : n);
                              }}
                              inputProps={{ min: 80, max: 5000, step: 50 }}
                              fullWidth
                            />
                          </Box>
                        </Tooltip>
                      </Stack>
                    ) : (
                      postcodeColumnControl
                    )}
                    <Tooltip
                      title={t('postcodeAssignment.tooltips.rowCoordinatesFromWorkbook')}
                      placement="top"
                      enterTouchDelay={0}
                      describeChild
                      slotProps={SECTION_HELP_TOOLTIP_SLOT_PROPS}
                    >
                      <FormControlLabel
                        sx={{
                          m: 0,
                          mr: 'auto',
                          alignItems: 'center',
                          width: '100%',
                          maxWidth: '100%',
                          '& .MuiFormControlLabel-label': { minWidth: 0 },
                        }}
                        control={
                          <Switch
                            checked={sheetRowCoordsOn}
                            color="primary"
                            onChange={(_event, checked) =>
                              setPostcodeRowCoordinatesEnabledBySheet((prev) => ({
                                ...prev,
                                [sheet.name]: checked,
                              }))
                            }
                            inputProps={{
                              'aria-label': t('postcodeAssignment.useWorkbookLatLonAria', { sheet: sheet.name }),
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" component="span" sx={{ cursor: 'help' }}>
                            {workbookPreview.length > 1
                              ? t('postcodeAssignment.useWorkbookLatLonWithSheet', { suffix: sheetHeading })
                              : t('postcodeAssignment.useWorkbookLatLon')}
                          </Typography>
                        }
                      />
                    </Tooltip>
                    {sheetRowCoordsOn && (
                      <>
                        <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
                          <FormLabel
                            id={originLayoutFieldId}
                            component="legend"
                            sx={{ typography: 'subtitle2', mb: 0.5 }}
                          >
                            {workbookPreview.length > 1 ? t('postcodeAssignment.postcodeOriginsWithSheet', { suffix: sheetHeading }) : t('postcodeAssignment.postcodeRowCoordinates')}
                          </FormLabel>
                          <RadioGroup
                            aria-labelledby={originLayoutFieldId}
                            row
                            name={`postcode-origin-layout-${sheet.name}`}
                            value={postcodeOriginLayout}
                            onChange={(_event, value: string) =>
                              setPostcodeOriginLayoutBySheet((prev) => ({
                                ...prev,
                                [sheet.name]: value as PostcodeOriginLayoutMode,
                              }))
                            }
                            sx={{ gap: 1, flexWrap: 'wrap' }}
                          >
                            <FormControlLabel
                              value="separate"
                              control={<Radio size="small" />}
                              label={t('postcodeAssignment.separateLatLon')}
                            />
                            <FormControlLabel
                              value="combined"
                              control={<Radio size="small" />}
                              label={t('postcodeAssignment.oneColumnLatLon')}
                            />
                          </RadioGroup>
                        </FormControl>
                        {postcodeOriginLayout === 'combined' ? (
                          <>
                            <FormControl fullWidth>
                              <InputLabel id={`${combinedSelectId}-label`}>
                                {workbookPreview.length > 1
                                  ? t('postcodeAssignment.latLonColumnWithSheet', { suffix: sheetHeading })
                                  : t('postcodeAssignment.postcodeLatLonColumn')}
                              </InputLabel>
                              <Select
                                labelId={`${combinedSelectId}-label`}
                                id={combinedSelectId}
                                label={
                                  workbookPreview.length > 1
                                    ? t('postcodeAssignment.latLonColumnWithSheet', { suffix: sheetHeading })
                                    : t('postcodeAssignment.postcodeLatLonColumn')
                                }
                                value={combinedVal}
                                disabled={sheet.columnKeys.length === 0}
                                onChange={(event) =>
                                  setPostcodeOriginCombinedColumnSelectionBySheet((prev) => ({
                                    ...prev,
                                    [sheet.name]: event.target.value,
                                  }))
                                }
                              >
                                <MenuItem value={NONE_POSTCODE_ORIGIN_COORD_COLUMN}>
                                  {t('postcodeAssignment.nonePostcodesIoOnly')}
                                </MenuItem>
                                <MenuItem value={AUTO_POSTCODE_COMBINED_COLUMN}>{combinedAutoLabel}</MenuItem>
                                {sheet.columnKeys.map((key) => (
                                  <MenuItem key={`${sheet.name}-pc-combined-${key}`} value={key}>
                                    {key}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </>
                        ) : (
                          <>
                            <Stack
                              direction="row"
                              spacing={2}
                              useFlexGap
                              sx={{
                                width: '100%',
                                flexWrap: 'nowrap',
                                alignItems: 'flex-start',
                              }}
                            >
                              <FormControl fullWidth sx={{ flex: '1 1 50%', minWidth: 0 }}>
                                <InputLabel id={`${latSelectId}-label`}>
                                  {workbookPreview.length > 1 ? t('postcodeAssignment.latitudeWithSheet', { suffix: sheetHeading }) : t('postcodeAssignment.postcodeLatitude')}
                                </InputLabel>
                                <Select
                                  labelId={`${latSelectId}-label`}
                                  id={latSelectId}
                                  label={
                                    workbookPreview.length > 1 ? t('postcodeAssignment.latitudeWithSheet', { suffix: sheetHeading }) : t('postcodeAssignment.postcodeLatitude')
                                  }
                                  value={latVal}
                                  disabled={sheet.columnKeys.length === 0}
                                  onChange={(event) => {
                                    const next = event.target.value;
                                    setPostcodeOriginLatColumnSelectionBySheet((prev) => ({
                                      ...prev,
                                      [sheet.name]: next,
                                    }));
                                  }}
                                >
                                  <MenuItem value={NONE_POSTCODE_ORIGIN_COORD_COLUMN}>
                                    None - use postcodes.io only
                                  </MenuItem>
                                  <MenuItem value={AUTO_POSTCODE_ORIGIN_COORD_COLUMN}>{latAutoLabel}</MenuItem>
                                  {sheet.columnKeys.map((key) => (
                                    <MenuItem key={key} value={key}>
                                      {key}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <FormControl fullWidth sx={{ flex: '1 1 50%', minWidth: 0 }}>
                                <InputLabel id={`${lonSelectId}-label`}>
                                  {workbookPreview.length > 1 ? t('postcodeAssignment.longitudeWithSheet', { suffix: sheetHeading }) : t('postcodeAssignment.postcodeLongitude')}
                                </InputLabel>
                                <Select
                                  labelId={`${lonSelectId}-label`}
                                  id={lonSelectId}
                                  label={
                                    workbookPreview.length > 1 ? t('postcodeAssignment.longitudeWithSheet', { suffix: sheetHeading }) : t('postcodeAssignment.postcodeLongitude')
                                  }
                                  value={lonVal}
                                  disabled={sheet.columnKeys.length === 0}
                                  onChange={(event) => {
                                    const next = event.target.value;
                                    setPostcodeOriginLonColumnSelectionBySheet((prev) => ({
                                      ...prev,
                                      [sheet.name]: next,
                                    }));
                                  }}
                                >
                                  <MenuItem value={NONE_POSTCODE_ORIGIN_COORD_COLUMN}>
                                    None - use postcodes.io only
                                  </MenuItem>
                                  <MenuItem value={AUTO_POSTCODE_ORIGIN_COORD_COLUMN}>{lonAutoLabel}</MenuItem>
                                  {sheet.columnKeys.map((key) => (
                                    <MenuItem key={key} value={key}>
                                      {key}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                          </>
                        )}
                      </>
                    )}
                    {sheet.rowCount > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {t('postcodeAssignment.rowCount', { count: sheet.rowCount })}
                        {sheet.columnKeys.length === 0 ? t('postcodeAssignment.noColumnHeaders') : ''}
                      </Typography>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Box>
        <FormControl sx={{ maxWidth: 260 }}>
          <InputLabel id="simulation-shift-band-label">Shift band preset</InputLabel>
          <Select
            labelId="simulation-shift-band-label"
            id="simulation-shift-band"
            label="Shift band preset"
            value={selectedShiftBand}
            onChange={(event) => handleShiftBandChange(event.target.value as ShiftBand)}
          >
            {SHIFT_BAND_WINDOWS.map((window) => (
              <MenuItem key={window.band} value={window.band}>
                {window.band} ({window.passengerWorkStart} - {window.passengerWorkEnd})
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Applies to simulation request time defaults and Ops vehicle slot windows.
          </FormHelperText>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={assignToPredeterminedHubs}
              onChange={(_event, checked) => {
                setAssignToPredeterminedHubs(checked);
                if (!checked) {
                  handleClearHubFile();
                }
              }}
              color="primary"
              inputProps={{ 'aria-label': t('postcodeAssignment.assignToPredeterminedHubsAria') }}
            />
          }
          label={t('postcodeAssignment.assignToPredeterminedHubs')}
          sx={{ mt: 0.5 }}
        />
        {(!workbookPreview || workbookPreview.length === 0) && (
          <Tooltip
            title={
              assignToPredeterminedHubs && hubFile
                ? t('postcodeAssignment.tooltips.osmRadiusDisabledWithHub')
                : t('postcodeAssignment.tooltips.osmRadius')
            }
            placement="top"
            enterTouchDelay={0}
            describeChild
            slotProps={osmSearchRadiusTooltipSlotProps}
          >
            <Box component="span" sx={{ display: 'block', width: '100%' }}>
              <TextField
                label="OSM search radius (metres)"
                type="number"
                value={searchRadiusMeters}
                disabled={assignToPredeterminedHubs && Boolean(hubFile)}
                onChange={(event) => {
                  const raw = event.target.value;
                  const n = Number(raw);
                  setSearchRadiusMeters(raw === '' || Number.isNaN(n) ? 1000 : n);
                }}
                inputProps={{ min: 80, max: 5000, step: 50 }}
                fullWidth
              />
            </Box>
          </Tooltip>
        )}
      </Stack>
      </Paper>

        {assignToPredeterminedHubs && (
          <Paper
            variant="outlined"
            sx={(theme) => ({
              p: 2,
              minWidth: 0,
              borderColor: opsColors.neutral.line,
              bgcolor: opsColors.neutral.panelAlt,
            })}
          >
            <Stack spacing={2} sx={{ minWidth: 0 }}>
              <SectionTitleWithTooltip title={t('postcodeAssignment.hubWorkbookSection')} tooltip={t('postcodeAssignment.tooltips.hubWorkbookSection')} />
              <WorkbookUploadCard
                title={t('postcodeAssignment.hubWorkbookCardTitle')}
                icon={<Icon size={20} name="location-dot" />}
                chooseFileAriaLabel={t('postcodeAssignment.hubWorkbookChooseFileAria')}
                dropRegionAriaLabel={t('postcodeAssignment.hubWorkbookDropAria')}
                fileInputRef={hubWorkbookInputRef}
                onChosenFileChange={setHubFile}
                chosenFile={hubFile}
                onClearChosen={handleClearHubFile}
              />
              <Box>
                {hubFile && hubPreviewLoading && (
                  <Typography variant="body2" color="text.secondary">
                    {t('postcodeAssignment.readingHubWorkbook')}
                  </Typography>
                )}
                {hubFile && hubPreviewError && (
                  <Alert severity="warning" sx={{ mt: hubPreviewLoading ? 1 : 0 }}>
                    {hubPreviewError}
                  </Alert>
                )}
                {hubWorkbookPreview && hubWorkbookPreview.columnKeys.length > 0 && (
                  <Stack spacing={2} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" component="h3">
                    {t('postcodeAssignment.hubColumns')}
                  </Typography>
                  <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
                    <RadioGroup
                      aria-label={t('postcodeAssignment.hubLatLonAria')}
                      row
                      name="hub-coord-layout"
                      value={hubCoordLayout}
                      onChange={(_event, value: string) => setHubCoordLayout(value as HubCoordLayoutMode)}
                      sx={{ gap: 1 }}
                    >
                      <FormControlLabel
                        value="separate"
                        control={<Radio size="small" />}
                        label={t('postcodeAssignment.separateLatLon')}
                      />
                      <FormControlLabel
                        value="combined"
                        control={<Radio size="small" />}
                        label={t('postcodeAssignment.oneColumnLatLonHub')}
                      />
                    </RadioGroup>
                  </FormControl>
                  <Stack direction="column" spacing={2} useFlexGap sx={{ width: '100%', alignItems: 'stretch' }}>
                    {hubCoordLayout === 'combined' && (
                      <FormControl fullWidth>
                        <InputLabel id="hub-combined-column-label">{t('postcodeAssignment.hubCombinedLabel')}</InputLabel>
                        <Select
                          labelId="hub-combined-column-label"
                          id="hub-combined-column"
                          label={t('postcodeAssignment.hubCombinedLabel')}
                          value={hubCombinedColumnSelection}
                          onChange={(event) => setHubCombinedColumnSelection(event.target.value)}
                        >
                          <MenuItem value={AUTO_HUB_COMBINED_COLUMN}>
                            {hubWorkbookPreview.detectedCombinedColumn
                              ? t('postcodeAssignment.autoDetected', { column: hubWorkbookPreview.detectedCombinedColumn })
                              : t('postcodeAssignment.autoNoColumn')}
                          </MenuItem>
                          {hubWorkbookPreview.columnKeys.map((key) => (
                            <MenuItem key={`hub-combined-${key}`} value={key}>
                              {key}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {hubCoordLayout === 'separate' && (
                      <Stack
                        direction="row"
                        spacing={2}
                        useFlexGap
                        sx={{
                          width: '100%',
                          flexWrap: 'nowrap',
                          alignItems: 'flex-start',
                        }}
                      >
                        <FormControl fullWidth sx={{ flex: '1 1 50%', minWidth: 0 }}>
                          <InputLabel id="hub-lat-column-label">{t('postcodeAssignment.hubLatLabel')}</InputLabel>
                          <Select
                            labelId="hub-lat-column-label"
                            id="hub-lat-column"
                            label={t('postcodeAssignment.hubLatLabel')}
                            value={hubLatColumnSelection}
                            onChange={(event) => setHubLatColumnSelection(event.target.value)}
                          >
                            <MenuItem value={AUTO_HUB_LAT_COLUMN}>
                              {hubWorkbookPreview.detectedLatColumn
                                ? t('postcodeAssignment.autoDetected', { column: hubWorkbookPreview.detectedLatColumn })
                                : t('postcodeAssignment.autoNoColumn')}
                            </MenuItem>
                            {hubWorkbookPreview.columnKeys.map((key) => (
                              <MenuItem key={`hub-lat-${key}`} value={key}>
                                {key}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl fullWidth sx={{ flex: '1 1 50%', minWidth: 0 }}>
                          <InputLabel id="hub-lon-column-label">{t('postcodeAssignment.hubLonLabel')}</InputLabel>
                          <Select
                            labelId="hub-lon-column-label"
                            id="hub-lon-column"
                            label={t('postcodeAssignment.hubLonLabel')}
                            value={hubLonColumnSelection}
                            onChange={(event) => setHubLonColumnSelection(event.target.value)}
                          >
                            <MenuItem value={AUTO_HUB_LON_COLUMN}>
                              {hubWorkbookPreview.detectedLonColumn
                                ? t('postcodeAssignment.autoDetected', { column: hubWorkbookPreview.detectedLonColumn })
                                : t('postcodeAssignment.autoNoColumn')}
                            </MenuItem>
                            {hubWorkbookPreview.columnKeys.map((key) => (
                              <MenuItem key={`hub-lon-${key}`} value={key}>
                                {key}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                    )}
                    <FormControl fullWidth>
                      <InputLabel id="hub-name-column-label">{t('postcodeAssignment.hubNameLabel')}</InputLabel>
                      <Select
                        labelId="hub-name-column-label"
                        id="hub-name-column"
                        label={t('postcodeAssignment.hubNameLabel')}
                        value={hubNameColumnSelection}
                        onChange={(event) => setHubNameColumnSelection(event.target.value)}
                      >
                        <MenuItem value={AUTO_HUB_NAME_COLUMN}>
                          {hubWorkbookPreview.detectedNameColumn
                            ? t('postcodeAssignment.autoDetected', { column: hubWorkbookPreview.detectedNameColumn })
                            : t('postcodeAssignment.autoNoColumn')}
                        </MenuItem>
                        <MenuItem value={HUB_NAME_COLUMN_NONE}>{t('postcodeAssignment.hubNameNone')}</MenuItem>
                        {hubWorkbookPreview.columnKeys.map((key) => (
                          <MenuItem key={`hub-name-${key}`} value={key}>
                            {key}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  </Stack>
                )}
              </Box>
            </Stack>
          </Paper>
        )}
      </Box>

      <Paper
        variant="outlined"
        sx={(theme) => ({
          p: 2,
          mb: 3,
          borderColor: opsColors.neutral.line,
          bgcolor: opsColors.neutral.panelAlt,
        })}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Icon name="route" size={20} style={{ color: '#52565e' }} aria-hidden />
            <SectionTitleWithTooltip
              title={t('postcodeAssignment.simulationOutputCardTitle')}
              tooltip={t('postcodeAssignment.tooltips.simulationOutputSection')}
              titleSx={{ my: 0 }}
            />
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={simulationOutputEnabled}
                onChange={(_event, checked) => setSimulationOutputEnabled(checked)}
                color="primary"
                inputProps={{ 'aria-label': t('postcodeAssignment.simulationOutputAria') }}
              />
            }
            label={
              <Typography variant="body2">
                {t('postcodeAssignment.simulationOutputToggle')}
              </Typography>
            }
            sx={{ m: 0 }}
          />
          {simulationOutputEnabled && (
            <Stack spacing={2.5} sx={{ pl: { xs: 0, sm: 1 } }}>
              {/* Direction */}
              <FormControl component="fieldset" variant="standard">
                <FormLabel
                  component="legend"
                  sx={{ typography: 'subtitle2', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                  <Icon d={ICON_FALLBACKS.SwapHorizOutlined} size={20} aria-hidden />
                  {t('postcodeAssignment.postcodeSide')}
                </FormLabel>
                <RadioGroup
                  row
                  value={postcodeDirection}
                  name="postcode-direction"
                  onChange={(_event, value: string) =>
                    setPostcodeDirection(value as SimulationPostcodeDirection)
                  }
                  sx={{ gap: 1, flexWrap: 'wrap' }}
                >
                  <FormControlLabel
                    value="postcode_origin"
                    control={<Radio size="small" />}
                    label={t('postcodeAssignment.postcodeAsOrigin')}
                  />
                  <FormControlLabel
                    value="postcode_destination"
                    control={<Radio size="small" />}
                    label={t('postcodeAssignment.postcodeAsDestination')}
                  />
                </RadioGroup>
              </FormControl>

              {/* Other endpoint */}
              <Stack spacing={1}>
                <Typography
                  variant="subtitle2"
                  component="div"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                  <Icon d={ICON_FALLBACKS.PinDropOutlined} size={20} aria-hidden />
                  {t('postcodeAssignment.otherEndpoint')}
                </Typography>
                <RadioGroup
                  row
                  value={otherEndpointMode}
                  name="other-endpoint-mode"
                  onChange={(_event, value: string) =>
                    setOtherEndpointMode(value as SimulationOtherEndpointMode)
                  }
                  sx={{ gap: 1 }}
                >
                  <FormControlLabel
                    value="manual"
                    control={<Radio size="small" />}
                    label={t('postcodeAssignment.manualLatLon')}
                  />
                  <FormControlLabel
                    value="workbook"
                    control={<Radio size="small" />}
                    label={t('postcodeAssignment.fromWorkbookColumns')}
                  />
                </RadioGroup>
                {otherEndpointMode === 'manual' ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
                    <TextField
                      label={t('postcodeAssignment.otherEndpointLat')}
                      type="number"
                      value={otherEndpointManualLatText}
                      onChange={(event) => setOtherEndpointManualLatText(event.target.value)}
                      inputProps={{ step: 'any', min: -90, max: 90 }}
                      sx={{ flex: 1, minWidth: 0 }}
                    />
                    <TextField
                      label={t('postcodeAssignment.otherEndpointLon')}
                      type="number"
                      value={otherEndpointManualLonText}
                      onChange={(event) => setOtherEndpointManualLonText(event.target.value)}
                      inputProps={{ step: 'any', min: -180, max: 180 }}
                      sx={{ flex: 1, minWidth: 0 }}
                    />
                  </Stack>
                ) : (
                  <Stack spacing={1.25}>
                    <RadioGroup
                      row
                      value={otherEndpointLayout}
                      name="other-endpoint-layout"
                      onChange={(_event, value: string) =>
                        setOtherEndpointLayout(value as SimulationOtherEndpointLayout)
                      }
                      sx={{ gap: 1 }}
                    >
                      <FormControlLabel
                        value="separate"
                        control={<Radio size="small" />}
                        label={t('postcodeAssignment.separateLatLon')}
                      />
                      <FormControlLabel
                        value="combined"
                        control={<Radio size="small" />}
                        label={t('postcodeAssignment.oneColumnLatLon')}
                      />
                    </RadioGroup>
                    {otherEndpointLayout === 'separate' ? (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
                        <FormControl fullWidth sx={{ flex: 1, minWidth: 0 }}>
                          <InputLabel id="other-endpoint-lat-label">{t('postcodeAssignment.otherEndpointLatColumn')}</InputLabel>
                          <Select
                            labelId="other-endpoint-lat-label"
                            id="other-endpoint-lat"
                            label={t('postcodeAssignment.otherEndpointLatColumn')}
                            value={otherEndpointLatSelection}
                            disabled={allPostcodeColumnKeys.length === 0}
                            onChange={(event) => setOtherEndpointLatSelection(event.target.value)}
                          >
                            <MenuItem value={AUTO_OTHER_LAT_COLUMN}>
                              {detectedOtherLat
                                ? t('postcodeAssignment.autoDetected', { column: detectedOtherLat })
                                : t('postcodeAssignment.autoNoColumn')}
                            </MenuItem>
                            {allPostcodeColumnKeys.map((key) => (
                              <MenuItem key={`other-lat-${key}`} value={key}>
                                {key}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl fullWidth sx={{ flex: 1, minWidth: 0 }}>
                          <InputLabel id="other-endpoint-lon-label">{t('postcodeAssignment.otherEndpointLonColumn')}</InputLabel>
                          <Select
                            labelId="other-endpoint-lon-label"
                            id="other-endpoint-lon"
                            label={t('postcodeAssignment.otherEndpointLonColumn')}
                            value={otherEndpointLonSelection}
                            disabled={allPostcodeColumnKeys.length === 0}
                            onChange={(event) => setOtherEndpointLonSelection(event.target.value)}
                          >
                            <MenuItem value={AUTO_OTHER_LON_COLUMN}>
                              {detectedOtherLon
                                ? t('postcodeAssignment.autoDetected', { column: detectedOtherLon })
                                : t('postcodeAssignment.autoNoColumn')}
                            </MenuItem>
                            {allPostcodeColumnKeys.map((key) => (
                              <MenuItem key={`other-lon-${key}`} value={key}>
                                {key}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                    ) : (
                      <FormControl fullWidth>
                        <InputLabel id="other-endpoint-combined-label">{t('postcodeAssignment.otherEndpointCombinedColumn')}</InputLabel>
                        <Select
                          labelId="other-endpoint-combined-label"
                          id="other-endpoint-combined"
                          label={t('postcodeAssignment.otherEndpointCombinedColumn')}
                          value={otherEndpointCombinedSelection}
                          disabled={allPostcodeColumnKeys.length === 0}
                          onChange={(event) => setOtherEndpointCombinedSelection(event.target.value)}
                        >
                          <MenuItem value={AUTO_OTHER_COMBINED_COLUMN}>
                            {detectedOtherCombined
                              ? t('postcodeAssignment.autoDetected', { column: detectedOtherCombined })
                              : t('postcodeAssignment.autoNoColumn')}
                          </MenuItem>
                          {allPostcodeColumnKeys.map((key) => (
                            <MenuItem key={`other-combined-${key}`} value={key}>
                              {key}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Stack>
                )}
                {/* Address / label for the other endpoint (drives from_address or to_address). */}
                {otherEndpointMode === 'manual' ? (
                  <TextField
                    label={t('postcodeAssignment.otherEndpointAddress')}
                    value={otherEndpointManualAddress}
                    onChange={(event) => setOtherEndpointManualAddress(event.target.value)}
                    placeholder={t('postcodeAssignment.otherEndpointAddressPlaceholder')}
                    helperText={t('postcodeAssignment.otherEndpointAddressHelper')}
                  />
                ) : (
                  <FormControl fullWidth>
                    <InputLabel id="other-endpoint-address-label">{t('postcodeAssignment.otherEndpointAddressColumn')}</InputLabel>
                    <Select
                      labelId="other-endpoint-address-label"
                      id="other-endpoint-address"
                      label={t('postcodeAssignment.otherEndpointAddressColumn')}
                      value={otherEndpointAddressSelection}
                      disabled={allPostcodeColumnKeys.length === 0}
                      onChange={(event) => setOtherEndpointAddressSelection(event.target.value)}
                    >
                      <MenuItem value={AUTO_OTHER_ADDRESS_COLUMN}>
                        {detectedOtherAddress
                          ? t('postcodeAssignment.autoDetected', { column: detectedOtherAddress })
                          : t('postcodeAssignment.autoNoAddress')}
                      </MenuItem>
                      <MenuItem value={NONE_OTHER_ADDRESS_COLUMN}>
                        {t('postcodeAssignment.noneUseManualLabel')}
                      </MenuItem>
                      {allPostcodeColumnKeys.map((key) => (
                        <MenuItem key={`other-addr-${key}`} value={key}>
                          {key}
                        </MenuItem>
                      ))}
                    </Select>
                    {otherEndpointAddressSelection === NONE_OTHER_ADDRESS_COLUMN && (
                      <TextField
                        sx={{ mt: 1.5 }}
                        label={t('postcodeAssignment.manualOtherEndpointAddress')}
                        value={otherEndpointManualAddress}
                        onChange={(event) => setOtherEndpointManualAddress(event.target.value)}
                        placeholder={t('postcodeAssignment.otherEndpointAddressPlaceholder')}
                      />
                    )}
                  </FormControl>
                )}
              </Stack>

              {/* Request time */}
              <Stack spacing={1}>
                <Typography
                  variant="subtitle2"
                  component="div"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                  <Icon name="clock" size={20} aria-hidden />
                  {t('postcodeAssignment.requestTime')}
                </Typography>
                <RadioGroup
                  row
                  value={requestTimeMode}
                  name="request-time-mode"
                  onChange={(_event, value: string) =>
                    setRequestTimeMode(value as SimulationRequestTimeMode)
                  }
                  sx={{ gap: 1 }}
                >
                  <FormControlLabel
                    value="manual"
                    control={<Radio size="small" />}
                    label={t('postcodeAssignment.applySameTime')}
                  />
                  <FormControlLabel
                    value="workbook"
                    control={<Radio size="small" />}
                    label={t('postcodeAssignment.fromWorkbookColumn')}
                  />
                </RadioGroup>
                {requestTimeMode === 'manual' ? (
                  <TextField
                    label={t('postcodeAssignment.requestTimeAllRows')}
                    type="datetime-local"
                    value={requestTimeManualLocal}
                    onChange={(event) => setRequestTimeManualLocal(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ maxWidth: { sm: 320 } }}
                  />
                ) : (
                  <FormControl fullWidth sx={{ maxWidth: { sm: 420 } }}>
                    <InputLabel id="request-time-column-label">{t('postcodeAssignment.requestTimeColumn')}</InputLabel>
                    <Select
                      labelId="request-time-column-label"
                      id="request-time-column"
                      label={t('postcodeAssignment.requestTimeColumn')}
                      value={requestTimeSelection}
                      disabled={allPostcodeColumnKeys.length === 0}
                      onChange={(event) => setRequestTimeSelection(event.target.value)}
                    >
                      <MenuItem value={AUTO_REQUEST_TIME_COLUMN}>
                        {detectedRequestTimeKey
                          ? t('postcodeAssignment.autoDetected', { column: detectedRequestTimeKey })
                          : t('postcodeAssignment.autoNoColumn')}
                      </MenuItem>
                      {allPostcodeColumnKeys.map((key) => (
                        <MenuItem key={`req-time-${key}`} value={key}>
                          {key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>

              {/* Ride / user IDs - column-driven; rows without data fall back to ride1/user1, ride2/user2 ... */}
              <Stack spacing={1}>
                <Typography
                  variant="subtitle2"
                  component="div"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                  <Icon name="route" size={20} aria-hidden />
                  {t('postcodeAssignment.tripIds')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('postcodeAssignment.tripIdsHelper')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
                  <FormControl fullWidth sx={{ flex: 1, minWidth: 0 }}>
                    <InputLabel id="ride-id-column-label">{t('postcodeAssignment.rideIdColumn')}</InputLabel>
                    <Select
                      labelId="ride-id-column-label"
                      id="ride-id-column"
                      label={t('postcodeAssignment.rideIdColumn')}
                      value={rideIdSelection}
                      disabled={allPostcodeColumnKeys.length === 0}
                      onChange={(event) => setRideIdSelection(event.target.value)}
                    >
                      <MenuItem value={AUTO_RIDE_ID_COLUMN}>
                        {detectedRideIdKey
                          ? t('postcodeAssignment.autoDetected', { column: detectedRideIdKey })
                          : t('postcodeAssignment.autoNoRideId')}
                      </MenuItem>
                      <MenuItem value={NONE_RIDE_ID_COLUMN}>{t('postcodeAssignment.rideIdNone')}</MenuItem>
                      {allPostcodeColumnKeys.map((key) => (
                        <MenuItem key={`ride-id-${key}`} value={key}>
                          {key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth sx={{ flex: 1, minWidth: 0 }}>
                    <InputLabel id="user-id-column-label">{t('postcodeAssignment.userIdColumn')}</InputLabel>
                    <Select
                      labelId="user-id-column-label"
                      id="user-id-column"
                      label={t('postcodeAssignment.userIdColumn')}
                      value={userIdSelection}
                      disabled={allPostcodeColumnKeys.length === 0}
                      onChange={(event) => setUserIdSelection(event.target.value)}
                    >
                      <MenuItem value={AUTO_USER_ID_COLUMN}>
                        {detectedUserIdKey
                          ? t('postcodeAssignment.autoDetected', { column: detectedUserIdKey })
                          : t('postcodeAssignment.autoNoUserId')}
                      </MenuItem>
                      <MenuItem value={NONE_USER_ID_COLUMN}>{t('postcodeAssignment.userIdNone')}</MenuItem>
                      {allPostcodeColumnKeys.map((key) => (
                        <MenuItem key={`user-id-${key}`} value={key}>
                          {key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>

              {/* Passengers - column-driven only; rows without data default to 1 "standard". */}
              <Stack spacing={1}>
                <Typography
                  variant="subtitle2"
                  component="div"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                  <Icon name="users" size={20} aria-hidden />
                  {t('postcodeAssignment.passengers')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('postcodeAssignment.passengersHelper')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
                  <FormControl fullWidth sx={{ flex: 1, minWidth: 0 }}>
                    <InputLabel id="passengers-count-column-label">{t('postcodeAssignment.passengerCountColumn')}</InputLabel>
                    <Select
                      labelId="passengers-count-column-label"
                      id="passengers-count-column"
                      label={t('postcodeAssignment.passengerCountColumn')}
                      value={passengersCountSelection}
                      disabled={allPostcodeColumnKeys.length === 0}
                      onChange={(event) => setPassengersCountSelection(event.target.value)}
                    >
                      <MenuItem value={AUTO_PASSENGERS_COUNT_COLUMN}>
                        {detectedPassengersCountKey
                          ? t('postcodeAssignment.autoDetected', { column: detectedPassengersCountKey })
                          : t('postcodeAssignment.autoNoPassengerCount')}
                      </MenuItem>
                      <MenuItem value={PASSENGERS_TYPE_COLUMN_NONE}>{t('postcodeAssignment.passengerCountNone')}</MenuItem>
                      {allPostcodeColumnKeys.map((key) => (
                        <MenuItem key={`pax-count-${key}`} value={key}>
                          {key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth sx={{ flex: 1, minWidth: 0 }}>
                    <InputLabel id="passengers-type-column-label">{t('postcodeAssignment.passengerTypeColumn')}</InputLabel>
                    <Select
                      labelId="passengers-type-column-label"
                      id="passengers-type-column"
                      label={t('postcodeAssignment.passengerTypeColumn')}
                      value={passengersTypeSelection}
                      disabled={allPostcodeColumnKeys.length === 0}
                      onChange={(event) => setPassengersTypeSelection(event.target.value)}
                    >
                      <MenuItem value={AUTO_PASSENGERS_TYPE_COLUMN}>
                        {detectedPassengersTypeKey
                          ? t('postcodeAssignment.autoDetected', { column: detectedPassengersTypeKey })
                          : t('postcodeAssignment.autoNoPassengerType', { type: DEFAULT_PASSENGER_TYPE })}
                      </MenuItem>
                      <MenuItem value={PASSENGERS_TYPE_COLUMN_NONE}>
                        {t('postcodeAssignment.passengerTypeNone', { type: DEFAULT_PASSENGER_TYPE })}
                      </MenuItem>
                      {allPostcodeColumnKeys.map((key) => (
                        <MenuItem key={`pax-type-${key}`} value={key}>
                          {key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>

              {otherEndpointMode === 'manual' && !manualOtherEndpoint && (
                <Alert severity="warning">
                  {t('postcodeAssignment.manualLatLonWarning')}
                </Alert>
              )}
              <Typography variant="caption" color="text.secondary">
                {t('postcodeAssignment.columnChoicesHint')}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* ── Ops vehicle mapping (optional) ──────────────────────────────────── */}
      {environmentTokensEnabled && (
        <Paper
          variant="outlined"
          sx={(theme) => ({
            p: 2,
            mb: 3,
            borderColor: opsColors.neutral.line,
            bgcolor: opsColors.neutral.panelAlt,
          })}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SectionTitleWithTooltip
                title="Ops vehicle mapping (optional)"
                tooltip="Map simulation vehicle slots to real Ops vehicle IDs. When populated, W9 uses these mappings to assign specific vehicles during route upload. Leave disabled or unmapped to upload without vehicle assignment."
                titleSx={{ my: 0 }}
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={opsVehicleMappingEnabled}
                  onChange={(_event, checked) => setOpsVehicleMappingEnabled(checked)}
                  color="primary"
                  inputProps={{ 'aria-label': 'Enable Ops vehicle mapping' }}
                />
              }
              label={
                <Typography variant="body2">
                  Enable Ops vehicle mapping
                </Typography>
              }
              sx={{ m: 0 }}
            />
            {opsVehicleMappingEnabled && (
              <OpsVehicleMappingPanel
                vehicleSlots={vehicleSlots}
                onVehicleSlotsChange={setVehicleSlots}
                selectedShiftBand={selectedShiftBand}
              />
            )}
          </Stack>
        </Paper>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 3 }}
      >
        <Button variant="contained" onClick={() => void runAssignment()} disabled={isRunning || !postcodeFile}>
          {isRunning ? <CircularProgress size={20} color="inherit" /> : t('postcodeAssignment.runAssignment')}
        </Button>
        <Button variant="outlined" color="error" onClick={handleStopAssignment} disabled={!isRunning}>
          {t('postcodeAssignment.stop')}
        </Button>
        <Tooltip
          title={t('postcodeAssignment.tooltips.walkingCalcOsrm')}
          placement="top"
          enterTouchDelay={0}
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 420,
                whiteSpace: 'pre-line',
                typography: 'body2',
                lineHeight: 1.55,
                textAlign: 'left',
                borderRadius: TOOLTIP_TIGHT_BOX_RADIUS,
              },
            },
          }}
        >
          {/* Wrapper so Tooltip forwards ref (FormControlLabel is not reliably a Tooltip child alone). */}
          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={useOsrmWalkingForConsolidation}
                  onChange={(_event, checked) => setUseOsrmWalkingForConsolidation(checked)}
                  color="primary"
                  inputProps={{ 'aria-label': t('postcodeAssignment.walkingCalculationsAria') }}
                />
              }
              disableTypography
              label={
                <Typography component="span" variant="body2" sx={{ cursor: 'help', userSelect: 'none' }}>
                  {t('postcodeAssignment.walkingCalculations')}
                </Typography>
              }
              sx={{ m: 0, mr: { xs: 0, sm: 0 }, alignItems: 'center', gap: 1 }}
            />
          </Box>
        </Tooltip>
      </Stack>
      <Paper
        variant="outlined"
        sx={(theme) => ({
          p: 2,
          mb: 3,
          borderColor: opsColors.neutral.line,
          bgcolor: opsColors.neutral.panelAlt,
        })}
      >
        <Stack spacing={1.5}>
          <Tooltip title={t('postcodeAssignment.tooltips.walkingRadiusHelp')} placement="top" enterTouchDelay={0} describeChild>
            <Typography variant="subtitle2" sx={{ cursor: 'help' }}>
              {t('postcodeAssignment.walkingOptimisation')}
            </Typography>
          </Tooltip>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={walkingRadiusEnabled}
                  onChange={(_e, checked) => {
                    if (checked) {
                      setWalkingRadiusEnabled(true);
                      setWalkingTimeEnabled(false);
                    } else {
                      setWalkingRadiusEnabled(false);
                      setWalkingTimeEnabled(true);
                    }
                    trackConfigChanged('walking_radius_toggle');
                    scheduleLiveRecompute();
                  }}
                />
              }
              label={t('postcodeAssignment.enableDistance')}
              sx={{ mr: 1 }}
            />
            <Slider
              value={walkingRadiusMeters}
              min={MIN_WALKING_RADIUS_METERS}
              max={MAX_WALKING_RADIUS_METERS}
              step={25}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}m`}
              disabled={!walkingRadiusEnabled}
              onChange={(_e, value) => {
                const next = clampWalkingRadiusMeters(Array.isArray(value) ? value[0] : value);
                setWalkingRadiusMeters(next);
                trackConfigChanged('walking_radius');
                scheduleLiveRecompute();
              }}
              onChangeCommitted={() => {
                runCachedUiRecompute();
              }}
              sx={{ flex: 1, minWidth: 220 }}
              aria-label={t('postcodeAssignment.walkingRadiusAria')}
            />
            <TextField
              label={t('postcodeAssignment.walkingRadius')}
              type="number"
              value={walkingRadiusMeters}
              disabled={!walkingRadiusEnabled}
              onChange={(event) => {
                const n = Number(event.target.value);
                const next = clampWalkingRadiusMeters(Number.isNaN(n) ? DEFAULT_WALKING_RADIUS_METERS : n);
                setWalkingRadiusMeters(next);
                trackConfigChanged('walking_radius');
                scheduleLiveRecompute();
              }}
              inputProps={{
                min: MIN_WALKING_RADIUS_METERS,
                max: MAX_WALKING_RADIUS_METERS,
                step: 25,
                'aria-label': t('postcodeAssignment.walkingRadiusInputAria'),
              }}
              sx={{ width: { xs: '100%', sm: 220 } }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={walkingTimeEnabled}
                  onChange={(_e, checked) => {
                    if (checked) {
                      setWalkingTimeEnabled(true);
                      setWalkingRadiusEnabled(false);
                    } else {
                      setWalkingTimeEnabled(false);
                      setWalkingRadiusEnabled(true);
                    }
                    trackConfigChanged('walking_time_toggle');
                    scheduleLiveRecompute();
                  }}
                />
              }
              label={t('postcodeAssignment.enableTime')}
              sx={{ mr: 1 }}
            />
            <Slider
              value={walkingTimeMinutes}
              min={MIN_WALKING_TIME_MINUTES}
              max={MAX_WALKING_TIME_MINUTES}
              step={1}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value} min`}
              disabled={!walkingTimeEnabled}
              onChange={(_e, value) => {
                const next = clampWalkingTimeMinutes(Array.isArray(value) ? value[0] : value);
                setWalkingTimeMinutes(next);
                trackConfigChanged('walking_time');
                scheduleLiveRecompute();
              }}
              onChangeCommitted={() => {
                runCachedUiRecompute();
              }}
              sx={{ flex: 1, minWidth: 220 }}
              aria-label={t('postcodeAssignment.walkingTimeAria')}
            />
            <TextField
              label={t('postcodeAssignment.walkingTime')}
              type="number"
              value={walkingTimeMinutes}
              disabled={!walkingTimeEnabled}
              onChange={(event) => {
                const n = Number(event.target.value);
                const next = clampWalkingTimeMinutes(Number.isNaN(n) ? DEFAULT_WALKING_TIME_MINUTES : n);
                setWalkingTimeMinutes(next);
                trackConfigChanged('walking_time');
                scheduleLiveRecompute();
              }}
              inputProps={{
                min: MIN_WALKING_TIME_MINUTES,
                max: MAX_WALKING_TIME_MINUTES,
                step: 1,
              }}
              sx={{ width: { xs: '100%', sm: 220 } }}
            />
          </Stack>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'stretch',
              gap: 2,
            }}
          >
            <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                {distancePresetCards.map((card) => (
                  <Paper
                    key={`dist-${card.meters}`}
                    variant="outlined"
                    sx={{ p: 1.25, cursor: 'pointer', minWidth: 150 }}
                    onClick={() => {
                      setWalkingRadiusEnabled(true);
                      setWalkingTimeEnabled(false);
                      setWalkingRadiusMeters(card.meters);
                      trackConfigChanged('walking_preset');
                      pendingPresetRecomputeRef.current = true;
                    }}
                  >
                    <Typography variant="subtitle2">{card.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('postcodeAssignment.presetStops', { count: card.stopCount, minutes: card.avgWalkingMinutes.toFixed(1) })}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                {timePresetCards.map((card) => (
                  <Paper
                    key={`time-${card.minutes}`}
                    variant="outlined"
                    sx={{ p: 1.25, cursor: 'pointer', minWidth: 150 }}
                    onClick={() => {
                      setWalkingTimeEnabled(true);
                      setWalkingRadiusEnabled(false);
                      setWalkingTimeMinutes(card.minutes);
                      trackConfigChanged('walking_preset');
                      pendingPresetRecomputeRef.current = true;
                    }}
                  >
                    <Typography variant="subtitle2">{card.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('postcodeAssignment.presetStops', { count: card.stopCount, minutes: card.avgWalkingMinutes.toFixed(1) })}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {t('postcodeAssignment.presetSummary')}
              </Typography>
            </Stack>
            <Paper
              variant="outlined"
              sx={(theme) => ({
                p: 1.5,
                minHeight: 110,
                width: { xs: '100%', md: 340 },
                flexShrink: 0,
                borderColor: opsColors.neutral.line,
                bgcolor: alpha(opsColors.neutral.panel, 0.7),
              })}
              aria-live="polite"
              aria-label={t('postcodeAssignment.consolidationSummaryAria')}
            >
              <Stack spacing={1.25}>
                <Typography variant="subtitle2">{t('postcodeAssignment.consolidationSummary')}</Typography>
                {consolidationSummary ? (
                  <>
                    <Stack direction="row" spacing={1}>
                      <Box
                        sx={(theme) => ({
                          flex: 1,
                          borderRadius: 1,
                          px: 1,
                          py: 0.75,
                          bgcolor: alpha(opsColors.brand.primary, 0.1),
                        })}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t('postcodeAssignment.originalTargets')}
                        </Typography>
                        <Typography variant="h6" sx={{ lineHeight: 1.1 }}>
                          {consolidationSummary.originalTargets}
                        </Typography>
                      </Box>
                      <Box
                        sx={(theme) => ({
                          flex: 1,
                          borderRadius: 1,
                          px: 1,
                          py: 0.75,
                          bgcolor: alpha(opsColors.brand.primary, 0.1),
                        })}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t('postcodeAssignment.consolidated')}
                        </Typography>
                        <Typography variant="h6" sx={{ lineHeight: 1.1 }}>
                          {consolidationSummary.consolidatedTargets}
                        </Typography>
                      </Box>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {t('postcodeAssignment.reducedBy', { count: consolidationSummary.reductionCount, percent: consolidationSummary.consolidationRatePct.toFixed(1) })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('postcodeAssignment.avgWalkingTime', { minutes: consolidationSummary.avgWalkingMinutes.toFixed(1) })}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('postcodeAssignment.runAssignmentToView')}
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Box>
        </Stack>
      </Paper>

      {isRunning && assignmentProgress && (
        <Paper
          variant="outlined"
          sx={(theme) => ({
            p: 2,
            mb: 3,
            borderColor: opsColors.neutral.line,
            bgcolor: opsColors.neutral.panelAlt,
          })}
          aria-busy="true"
          aria-label={t('postcodeAssignment.progressAria')}
        >
          <Stack spacing={1.5}>
            <LinearProgress
              variant="determinate"
              value={Math.min(
                100,
                Math.max(
                  0,
                  assignmentProgress.barPercent ??
                    (assignmentProgress.total > 0
                      ? (assignmentProgress.current / assignmentProgress.total) * 100
                      : 0)
                )
              )}
              sx={(theme) => ({
                height: 8,
                borderRadius: 1,
                bgcolor: opsColors.neutral.panelAlt,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 1,
                },
              })}
            />
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.primary" fontWeight={600}>
                {t('postcodeAssignment.progressRow', { sheet: assignmentProgress.sheetName, row: assignmentProgress.rowInSheet, total: assignmentProgress.sheetRows })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('postcodeAssignment.progressOverall', { current: assignmentProgress.current, total: assignmentProgress.total, detail: assignmentProgress.detail })}
              </Typography>
              {runTiming && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('postcodeAssignment.elapsed', { elapsed: formatDurationShort(runTiming.elapsedMs) })}
                  {' - '}
                  {runTiming.etaRemainingMs != null
                    ? t('postcodeAssignment.estimatedRemaining', { remaining: formatDurationShort(runTiming.etaRemainingMs) })
                    : t('postcodeAssignment.estimatingRemaining')}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}

      {infoMessage && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={() => setInfoMessage(null)}>
          {infoMessage}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {sheetResults.length > 0 && (
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Accordion
            defaultExpanded
            variant="outlined"
            disableGutters
            sx={(theme) => ({
              borderColor: opsColors.neutral.line,
              bgcolor: opsColors.neutral.panelAlt,
              '&:before': { display: 'none' },
            })}
          >
            <AccordionSummary
              expandIcon={<Icon size={20} name="chevron-down" style={{ color: '#52565e' }} />}
              aria-controls="postcode-assignment-results-content"
              id="postcode-assignment-results-header"
              sx={(theme) => ({
                px: 2,
                minHeight: 56,
                '& .MuiAccordionSummary-content': { my: 1.5, alignItems: 'center' },
                '&:focus-visible': {
                  outline: `2px solid ${opsColors.brand.primary}`,
                  outlineOffset: -2,
                },
              })}
            >
              <SectionTitleWithTooltip
                title={t('postcodeAssignment.assignmentResults')}
                tooltip={t('postcodeAssignment.tooltips.assignmentResultsSection')}
                component="div"
                titleSx={{ my: 0 }}
              />
            </AccordionSummary>
            <AccordionDetails id="postcode-assignment-results-content" sx={{ px: 2, pb: 2, pt: 0 }}>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <FormControl sx={{ minWidth: 220 }}>
                    <InputLabel id="assignment-sheet-select-label">{t('postcodeAssignment.sheet')}</InputLabel>
                    <Select
                      labelId="assignment-sheet-select-label"
                      value={activeSheet}
                      label={t('postcodeAssignment.sheet')}
                      onChange={(event) => {
                        const next = event.target.value;
                        setActiveSheet(next);
                        const sheetIndex = sheetResults.findIndex((s) => s.name === next);
                        const rowCount = sheetIndex >= 0 ? sheetResults[sheetIndex].rows.length : 0;
                        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.postcode_assignment_result_sheet_selected, {
                          sheet_index: Math.max(0, sheetIndex),
                          row_count: rowCount,
                          timestamp: new Date().toISOString(),
                        });
                      }}
                    >
                      {sheetResults.map((sheet) => (
                        <MenuItem key={sheet.name} value={sheet.name}>
                          {sheet.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label={t('postcodeAssignment.filterRows')}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 280 } }}
                  />
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      ml: { md: 'auto' },
                      justifyContent: { xs: 'flex-start', md: 'flex-end' },
                    }}
                  >
                    <Tooltip
                      title={t('postcodeAssignment.sendToMapTooltip')}
                      placement="top"
                    >
                      <Box component="span" sx={{ display: 'inline-flex' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<Icon size={20} name="map" />}
                          onClick={pushTripsAndOpenMap}
                          disabled={!simulationTripsSheet || simulationTripsSheet.rows.length === 0}
                          aria-label={t('postcodeAssignment.sendToMapAria')}
                        >
                          {t('postcodeAssignment.sendToMap')}
                        </Button>
                      </Box>
                    </Tooltip>
                    {/* Span wrapper lets Tooltip hover work when the IconButton is disabled. */}
                    <Tooltip
                      title={t('postcodeAssignment.downloadSimulationTooltip')}
                      placement="top"
                    >
                      <Box component="span" sx={{ display: 'inline-flex' }}>
                        <IconButton
                          aria-label={t('postcodeAssignment.downloadSimulationAria')}
                          color="inherit"
                          onClick={downloadSimulationTripsResults}
                          disabled={!simulationTripsSheet || simulationTripsSheet.rows.length === 0}
                          size="medium"
                          sx={{
                            flexShrink: 0,
                            color: 'text.secondary',
                            '&:hover': {
                              color: 'text.primary',
                              bgcolor: (theme) => alpha(opsColors.brand.primary, 0.08),
                            },
                            '&:focus-visible': {
                              outline: (theme) => `2px solid ${opsColors.brand.primary}`,
                              outlineOffset: 2,
                            },
                            '&.Mui-disabled': {
                              opacity: 0.45,
                            },
                          }}
                        >
                          <Icon size={20} name="route" aria-hidden />
                        </IconButton>
                      </Box>
                    </Tooltip>
                    <Tooltip title={t('postcodeAssignment.downloadEnrichedTooltip')} placement="top">
                      <Box component="span" sx={{ display: 'inline-flex' }}>
                        <IconButton
                          aria-label={t('postcodeAssignment.downloadEnrichedAria')}
                          color="inherit"
                          onClick={downloadResults}
                          disabled={!sheetResults.length}
                          edge="end"
                          size="medium"
                          sx={{
                            flexShrink: 0,
                            color: 'text.secondary',
                            '&:hover': {
                              color: 'text.primary',
                              bgcolor: (theme) => alpha(opsColors.brand.primary, 0.08),
                            },
                            '&:focus-visible': {
                              outline: (theme) => `2px solid ${opsColors.brand.primary}`,
                              outlineOffset: 2,
                            },
                            '&.Mui-disabled': {
                              opacity: 0.45,
                            },
                          }}
                        >
                          <Icon size={20} name="file-arrow-down" aria-hidden />
                        </IconButton>
                      </Box>
                    </Tooltip>
                  </Stack>
                </Stack>

                <Box sx={{ position: 'relative' }}>
                  <TableContainer sx={{ maxHeight: '65vh' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {columns.map((column) => (
                            <TableCell key={column}>{column}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedSheetRows.map((row, index) => (
                          <TableRow key={`${activeSheet}-${index}`}>
                            {columns.map((column) => (
                              <TableCell key={`${column}-${index}`}>{String(row[column] ?? '')}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {isConstraintRecomputing && (
                    <Box
                      sx={(theme) => ({
                        position: 'absolute',
                        inset: 0,
                        bgcolor: alpha(opsColors.neutral.panelAlt, 0.45),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                      })}
                      aria-live="polite"
                      aria-label={t('postcodeAssignment.updatingTableAria')}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={18} />
                        <Typography variant="caption" color="text.secondary">
                          {t('postcodeAssignment.updatingTable')}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion
            defaultExpanded={false}
            variant="outlined"
            disableGutters
            TransitionProps={{ unmountOnExit: true }}
            sx={(theme) => ({
              borderColor: opsColors.neutral.line,
              bgcolor: opsColors.neutral.panelAlt,
              '&:before': { display: 'none' },
            })}
          >
            <AccordionSummary
              expandIcon={<Icon size={20} name="chevron-down" style={{ color: '#52565e' }} />}
              aria-controls="postcode-assignment-map-content"
              id="postcode-assignment-map-header"
              sx={(theme) => ({
                px: 2,
                minHeight: 56,
                '& .MuiAccordionSummary-content': { my: 1.5, alignItems: 'center' },
                '&:focus-visible': {
                  outline: `2px solid ${opsColors.brand.primary}`,
                  outlineOffset: -2,
                },
              })}
            >
              <SectionTitleWithTooltip
                title={t('postcodeAssignment.assignmentMap')}
                tooltip={t('postcodeAssignment.tooltips.assignmentMapSection')}
                component="div"
                titleSx={{ my: 0 }}
              />
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
              <PostcodeAssignmentMapPanel
                trailRef={assignmentMapTrailRef}
                isLoading={isConstraintRecomputing}
                revision={mapRevision}
              />
            </AccordionDetails>
          </Accordion>
        </Stack>
      )}
    </Container>
  );
};

export default PostcodeStopAssignmentPage;

